import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import {
  Clock, Package, CheckCircle, XCircle, Search,
  ChevronDown, FileText, Trash2, Filter, RefreshCw, Printer, Shield, AlertTriangle, LogOut, User, Edit2,
  PlayCircle, StopCircle, Coffee, AlertOctagon, UserCheck, UserX, AlertCircle, DollarSign, Truck, MessageSquare
} from 'lucide-react';
import EquipmentRatePicker from './EquipmentRatePicker';
import { MaterialsReportTable, printMaterialsReport } from './MaterialsReport';
import FabManagerDashboard from './FabManagerDashboard';
import { MoveToWOButton } from './shop/ConsumableMoveWO';
import { PayrollLanes } from './shop/PayrollLanes';
import { WeekWrapup } from './shop/WeekWrapup';
import { FabTmReport } from './shop/FabTmReport';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/shop`;

const ATT_LABELS = { sick: 'SICK', vacation: 'VAC', absent: 'ABSENT', on_site: 'ON SITE', no_work: 'NO WORK' };
const SHOP_ROLES = ['welder', 'fitter', 'fabrication', 'fabrication_manager', 'shop_foreman', 'labourer', 'apprentice'];
const ATT_BADGE = {
  sick: 'bg-red-100 text-red-700 border-red-300',
  vacation: 'bg-blue-100 text-blue-700 border-blue-300',
  absent: 'bg-amber-100 text-amber-700 border-amber-300',
  on_site: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  no_work: 'bg-slate-200 text-slate-700 border-slate-400',
};

// Reason buttons for someone not clocked into the shop — rendered right under their name so nothing is off-screen.
const NOT_IN_REASONS = [
  { type: 'sick', label: 'Called In Sick', cls: 'bg-red-500 hover:bg-red-600', past: 'sick' },
  { type: 'absent', label: 'Absent', cls: 'bg-amber-500 hover:bg-amber-600', past: 'absent' },
  { type: 'vacation', label: 'Vacation', cls: 'bg-blue-500 hover:bg-blue-600', past: 'on vacation' },
  { type: 'on_site', label: 'On Site', cls: 'bg-emerald-600 hover:bg-emerald-700', past: 'on site' },
  { type: 'no_work', label: 'No Work', cls: 'bg-slate-200 hover:bg-slate-100', past: 'no work today' },
];

const NotInActionPanel = React.forwardRef(({ emp, selectedDate, vacRange, setVacRange, onDone, onClose }, ref) => {
  const mark = async (r) => {
    try {
      await axios.post(`${API}/attendance-event`, { employee_id: emp.employee_id, date: selectedDate, type: r.type, notes: '' });
      toast.success(`${emp.employee_name} marked ${r.past}`);
      onDone();
    } catch { toast.error('Could not save — try again'); }
  };
  const clear = async () => {
    try { await axios.delete(`${API}/attendance-event/${emp.marked.id}`); toast.success(`Cleared ${ATT_LABELS[emp.marked.type] || ''} for ${emp.employee_name}`); onDone(); }
    catch { toast.error('Could not clear'); }
  };
  return (
    <div ref={ref} className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="not-in-action-panel">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-slate-900">
          {emp.employee_name}
          {emp.marked && <span className="ml-2 text-xs font-normal text-slate-600">— already marked <b>{ATT_LABELS[emp.marked.type] || emp.marked.type}</b>{emp.marked.logged_by ? ` by ${emp.marked.logged_by}` : ''}; pick another to change it</span>}
        </p>
        <button onClick={onClose} className="text-xs text-slate-500" data-testid="not-in-action-close">X</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {NOT_IN_REASONS.map(r => (
          <button key={r.type} onClick={() => mark(r)} data-testid={`not-in-mark-${r.type}`}
            className={`px-3 py-1.5 text-slate-900 rounded-lg text-xs font-medium ${r.cls} ${emp.marked?.type === r.type ? 'ring-2 ring-offset-1 ring-blue-400' : ''}`}>
            {r.label}
          </button>
        ))}
        <button data-testid="vacation-range-toggle" onClick={() => setVacRange(vacRange ? null : { from: selectedDate, to: selectedDate })}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium ${vacRange ? 'bg-blue-700 text-white' : 'bg-blue-100 hover:bg-blue-200 text-blue-800 border border-blue-300'}`}>
          Vacation (Multiple Days)
        </button>
        <button onClick={async () => {
          const note = window.prompt(`Note for ${emp.employee_name}:`);
          if (note === null) return;
          await axios.post(`${API}/employee-notes/${emp.employee_id}`, { note, category: 'attendance' });
          toast.success('Note saved');
          onClose();
        }} className="px-3 py-1.5 bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 rounded-lg text-xs font-medium" data-testid="not-in-add-note">
          Add Note
        </button>
        {emp.marked?.id && (
          <button onClick={clear} className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-red-600 underline decoration-dotted" data-testid="not-in-clear-mark">Clear</button>
        )}
      </div>
      {vacRange && (
        <div className="mt-3 flex flex-wrap items-end gap-2 p-2 bg-white rounded-lg border border-blue-200" data-testid="vacation-range-panel">
          <div>
            <label className="block text-[10px] text-slate-500 mb-0.5">From</label>
            <input type="date" value={vacRange.from} onChange={e => setVacRange({ ...vacRange, from: e.target.value })}
              data-testid="vacation-range-from" className="px-2 py-1 border border-slate-300 rounded text-xs text-slate-900" />
          </div>
          <div>
            <label className="block text-[10px] text-slate-500 mb-0.5">To</label>
            <input type="date" value={vacRange.to} onChange={e => setVacRange({ ...vacRange, to: e.target.value })}
              data-testid="vacation-range-to" className="px-2 py-1 border border-slate-300 rounded text-xs text-slate-900" />
          </div>
          <button data-testid="vacation-range-save" onClick={async () => {
            try {
              const r = await axios.post(`${API}/attendance-event`, { employee_id: emp.employee_id, date: vacRange.from, date_to: vacRange.to, type: 'vacation', notes: '' });
              toast.success(`${emp.employee_name} on vacation — ${r.data.created} day${r.data.created > 1 ? 's' : ''} marked`);
              onDone();
            } catch { toast.error('Failed to save vacation'); }
          }} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium">
            Save Vacation
          </button>
        </div>
      )}
    </div>
  );
});

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', weekday: 'short' });
};
const localDate = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
const today = () => localDate(new Date());
const sundayOf = (d) => {
  const dt = new Date(d + 'T12:00:00');
  dt.setDate(dt.getDate() - dt.getDay());
  return localDate(dt);
};
const saturdayOf = (d) => {
  const sun = sundayOf(d);
  const dt = new Date(sun + 'T12:00:00');
  dt.setDate(dt.getDate() + 6);
  return localDate(dt);
};

const statusColors = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-600 border-red-200',
};

export default function ShopTracker() {
  // Deep links from payroll / wrap-up notifications: /shop-tracker?tab=reports&report=linda&week=YYYY-MM-DD
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'entry');
  const [stats, setStats] = useState(null);
  const [kioskMode, setKioskMode] = useState(() => localStorage.getItem('shop_kiosk_mode') === 'true');
  const [clockOutPromptActive, setClockOutPromptActive] = useState(false);
  const { user, login, logout } = useAuth();

  // Kiosk login state
  const [kioskEmail, setKioskEmail] = useState('');
  const [kioskPassword, setKioskPassword] = useState('');
  const [kioskLoginError, setKioskLoginError] = useState('');
  const [kioskLoggingIn, setKioskLoggingIn] = useState(false);

  const grants = user?.extra_permissions || [];
  const isForeman =
    ['super_admin', 'office_admin', 'shop_foreman', 'fabrication_manager'].includes(user?.role) || grants.includes('shop_foreman_review');
  // Dispatch (Tool Crib sign-off) and PMs (millwright sign-off) need the QB report for two-tier payroll approval
  const canViewReports =
    ['super_admin', 'admin', 'office_admin', 'shop_foreman', 'project_manager_estimator', 'project_manager', 'fabrication_manager', 'dispatch'].includes(user?.role) || grants.includes('shop_foreman_review');
  const isSuperAdmin = ['super_admin', 'fabrication_manager'].includes(user?.role);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/dashboard-stats`).then(r => setStats(r.data)).catch(() => {});
    }
  }, [tab, user]);


  const handleKioskLogout = () => {
    logout();
    // kiosk mode stays active in localStorage — App.js will render kiosk login
  };

  const handleKioskLogin = async (e) => {
    e.preventDefault();
    setKioskLoggingIn(true);
    setKioskLoginError('');
    const result = await login(kioskEmail, kioskPassword);
    if (!result.success) {
      setKioskLoginError(result.error || 'Login failed');
    } else {
      setKioskPassword('');
      setKioskLoginError('');
    }
    setKioskLoggingIn(false);
  };

  const tabs = [
    { id: 'entry', label: 'Log Work', icon: Clock },
    ...(isForeman ? [{ id: 'foreman', label: 'Foreman Review', icon: CheckCircle }] : []),
    ...(isSuperAdmin ? [{ id: 'manager', label: 'Fab Manager', icon: User }] : []),
    ...(canViewReports ? [{ id: 'reports', label: 'Reports', icon: FileText }] : []),
    ...(isSuperAdmin ? [{ id: 'admin', label: 'Admin', icon: Shield }] : []),
  ];

  // ── Kiosk login screen ──
  const kioskLoginScreen = (
    <div className="fixed inset-0 z-[100] ops-canvas flex items-center justify-center" data-testid="shop-tracker-kiosk">
      <div className="w-full max-w-sm px-6">
        <h1
          className="text-3xl font-bold text-slate-900 text-center mb-1 select-none cursor-default"
        >
          Shop Time Tracker
        </h1>
        <p className="text-slate-500 text-center text-sm mb-8">Sign in to log your hours</p>
        <form onSubmit={handleKioskLogin} className="space-y-4">
          <div>
            <input
              data-testid="kiosk-email"
              type="email"
              value={kioskEmail}
              onChange={e => setKioskEmail(e.target.value)}
              placeholder="Email"
              required
              className="w-full px-4 py-3.5 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 text-lg focus:border-blue-500 outline-none"
            />
          </div>
          <div>
            <input
              data-testid="kiosk-password"
              type="password"
              value={kioskPassword}
              onChange={e => setKioskPassword(e.target.value)}
              placeholder="Password"
              required
              className="w-full px-4 py-3.5 bg-white border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 text-lg focus:border-blue-500 outline-none"
            />
          </div>
          {kioskLoginError && (
            <p className="text-red-600 text-sm text-center">{kioskLoginError}</p>
          )}
          <button
            data-testid="kiosk-login-btn"
            type="submit"
            disabled={kioskLoggingIn}
            className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-200 text-white rounded-xl font-semibold text-lg transition-colors"
          >
            {kioskLoggingIn ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );

  // ── Kiosk content (logged in) ──
  const firstName = (user?.full_name || '').split(' ')[0] || user?.email || '';

  const kioskContent = (
    <div className="fixed inset-0 z-[100] ops-canvas overflow-y-auto p-4 sm:p-6 text-base sm:text-sm" data-testid="shop-tracker-kiosk">
      <div className="max-w-[1600px] mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="/365-logo-dark.png" alt="365"
              className="w-12 h-10 object-contain select-none cursor-default"
              data-testid="shop-tracker-title"
            />
            <div>
              <h1 className="text-xl font-bold text-slate-900">Hey {firstName}</h1>
              <p className="text-xs text-slate-500">Log your hours and consumables below</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {stats && (
              <span className="hidden sm:inline px-3 py-1.5 rounded-lg bg-white/70 text-slate-600 border border-slate-200 text-xs">
                Late {stats.ytd_late || 0} / Absent {stats.ytd_absent || 0} / Sick {stats.ytd_sick || 0}
              </span>
            )}
            {stats && (
              <span className="hidden sm:inline px-3 py-1.5 rounded-lg bg-white/60 text-slate-600 border border-slate-200 text-xs">
                Last Week: {stats.last_week_regular}h{stats.last_week_overtime > 0 ? ` + ${stats.last_week_overtime}h OT` : ''}
              </span>
            )}
            {stats && (
              <span className="hidden sm:inline px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 text-xs">
                This Week: {stats.my_week_regular}h{stats.my_week_overtime > 0 ? ` + ${stats.my_week_overtime}h OT` : ''}
              </span>
            )}
            <button
              data-testid="kiosk-logout-btn"
              onClick={handleKioskLogout}
              disabled={clockOutPromptActive}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-colors border ${
                clockOutPromptActive
                  ? 'bg-slate-200 text-slate-500 border-slate-300 cursor-not-allowed'
                  : 'bg-white hover:bg-slate-100 text-slate-500 hover:text-slate-900 border-slate-200'
              }`}
            >
              <LogOut className="w-4 h-4" /> Done
            </button>
          </div>
        </div>

        <div className="flex gap-1 bg-white/60 p-1 rounded-xl border border-slate-200 overflow-x-auto">
          {tabs.map(t => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-all flex-shrink-0 ${
                tab === t.id
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                  : 'text-slate-500 hover:text-slate-900 hover:bg-blue-100'
              }`}
            >
              <t.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">{t.label}</span>
              <span className="sm:hidden">{t.label.split(' ')[0]}</span>
            </button>
          ))}
        </div>

        {tab === 'entry' && <UnifiedEntryTab onClockOutPromptChange={setClockOutPromptActive} />}
        {tab === 'foreman' && isForeman && <ForemanTab />}
        {tab === 'manager' && isSuperAdmin && <FabManagerDashboard />}
        {tab === 'reports' && canViewReports && <ReportsTab isForeman={isForeman} />}
        {tab === 'admin' && isSuperAdmin && <AdminTab />}
       </div>
    </div>
  );

  // ── Kiosk mode rendering ──
  if (kioskMode) {
    return user ? kioskContent : kioskLoginScreen;
  }

  // ── Normal mode ──
  const normalContent = (
    <>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1
            className="text-2xl font-bold text-slate-900 select-none cursor-default"
            data-testid="shop-tracker-title"
          >
            Shop Time Tracker
          </h1>
          <p className="text-sm text-slate-600 mt-0.5">
            Logged in as <span className="text-slate-900 font-medium">{user?.full_name || user?.email}</span>
          </p>
        </div>
        {stats && (
          <div className="flex gap-3 text-xs flex-wrap">
            <span className="px-3 py-1.5 rounded-lg bg-white/60 text-slate-900 border border-slate-200">
              Late {stats.ytd_late || 0} / Absent {stats.ytd_absent || 0} / Sick {stats.ytd_sick || 0}
            </span>
            <span className="px-3 py-1.5 rounded-lg bg-white/60 text-slate-900 border border-slate-200">
              Last Week: {stats.last_week_regular}h{stats.last_week_overtime > 0 ? ` + ${stats.last_week_overtime}h OT` : ''}
            </span>
            <span className="px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-200">
              Week: {stats.my_week_regular}h reg / {stats.my_week_overtime}h OT
            </span>
            {isForeman && (
              <span className="px-3 py-1.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200">
                {stats.pending_time_entries + stats.pending_consumable_entries} pending
              </span>
            )}
          </div>
        )}
      </div>

      <div className="flex gap-1 bg-white/60 backdrop-blur-sm p-1 rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        {tabs.map(t => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
              tab === t.id
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/30'
                : 'text-slate-900 hover:text-slate-900 hover:bg-blue-100'
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'entry' && <UnifiedEntryTab onClockOutPromptChange={setClockOutPromptActive} />}
      {tab === 'foreman' && isForeman && <ForemanTab />}
      {tab === 'manager' && isSuperAdmin && <FabManagerDashboard />}
      {tab === 'reports' && canViewReports && <ReportsTab isForeman={isForeman} />}
      {tab === 'admin' && isSuperAdmin && <AdminTab />}
    </>
  );

  return (
    <div className="space-y-4" data-testid="shop-tracker-page">
      {normalContent}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   CLOCK IN/OUT BANNER — Shows at top of entry tab
   ════════════════════════════════════════════════════════════════ */
function ClockInBanner({ onClockChange, onAvailableHoursChange, onClockOutPromptChange }) {
  const [clockStatus, setClockStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showManual, setShowManual] = useState(false);
  const [manualTime, setManualTime] = useState('');
  const [manualNotes, setManualNotes] = useState('');
  const [selectedShift, setSelectedShift] = useState('days');
  const [submitting, setSubmitting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/clock-status`);
      setClockStatus(r.data);
      onAvailableHoursChange?.(r.data?.available_hours ?? null);
    } catch { }
    setLoading(false);
  }, [onAvailableHoursChange]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Auto-refresh available hours every 60s while clocked in
  useEffect(() => {
    if (clockStatus?.clocked_in && !clockStatus?.clocked_out) {
      const interval = setInterval(fetchStatus, 60000);
      return () => clearInterval(interval);
    }
  }, [clockStatus, fetchStatus]);

  const handleClockIn = async () => {
    setSubmitting(true);
    try {
      const tzOffset = new Date().getTimezoneOffset();
      await axios.post(`${API}/clock-in`, { tz_offset: tzOffset, shift: selectedShift });
      toast.success('Clocked in!');
      fetchStatus();
      onClockChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clock-in failed');
    }
    setSubmitting(false);
  };

  const handleManualClockIn = async () => {
    if (!manualTime) { toast.error('Enter your start time'); return; }
    setSubmitting(true);
    try {
      const tzOffset = new Date().getTimezoneOffset();
      await axios.post(`${API}/clock-in`, { manual_time: manualTime, tz_offset: tzOffset, shift: selectedShift, notes: manualNotes || 'Manual entry — forgot to clock in' });
      toast.success('Manual clock-in recorded');
      setShowManual(false);
      setManualTime('');
      setManualNotes('');
      fetchStatus();
      onClockChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Manual clock-in failed');
    }
    setSubmitting(false);
  };

  const [showClockOutPrompt, setShowClockOutPrompt] = useState(false);
  const [pendingLunch, setPendingLunch] = useState(true);

  const handleClockOut = async (tookLunch) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/clock-out`, { took_lunch: tookLunch });
      toast.success('Clocked out!');
      setShowClockOutPrompt(false);
      onClockOutPromptChange?.(false);
      fetchStatus();
      onClockChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clock-out failed');
    }
    setSubmitting(false);
  };

  const initiateClockOut = (tookLunch) => {
    setPendingLunch(tookLunch);
    setShowClockOutPrompt(true);
    onClockOutPromptChange?.(true);
  };

  const handleLunchToggle = async () => {
    if (!clockStatus?.event?.id) return;
    setSubmitting(true);
    try {
      const newVal = !clockStatus.event.took_lunch;
      await axios.put(`${API}/clock/${clockStatus.event.id}/lunch`, { took_lunch: newVal });
      fetchStatus();
    } catch (e) {
      toast.error('Failed to update lunch');
    }
    setSubmitting(false);
  };

  if (loading) return null;

  const fmtTime = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Toronto' }); }
    catch { return iso; }
  };

  // Not clocked in yet
  if (!clockStatus?.clocked_in) {
    return (
      <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm p-5" data-testid="clock-in-banner">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
              <PlayCircle className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <p className="text-slate-900 font-medium">Start your day</p>
              <p className="text-xs text-slate-900">Clock in to track your hours</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Shift picker */}
            <div className="flex bg-white/60 rounded-lg border border-slate-200 p-0.5" data-testid="shift-picker">
              {[['days','Days'],['afternoons','Aft'],['nights','Nights']].map(([val,label]) => (
                <button key={val} onClick={() => setSelectedShift(val)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    selectedShift === val ? 'bg-blue-600 text-white' : 'text-slate-900 hover:text-white'
                  }`}>{label}</button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="clock-in-btn"
                onClick={handleClockIn}
                disabled={submitting}
                className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-xl font-semibold text-sm transition-colors flex items-center gap-2"
              >
                <PlayCircle className="w-4 h-4" /> Clock In
              </button>
              <button
                data-testid="manual-clock-in-toggle"
                onClick={() => setShowManual(!showManual)}
                className="px-3 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-900 rounded-xl text-xs transition-colors"
              >
                Forgot?
              </button>
            </div>
          </div>
        </div>
        {showManual && (
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-amber-700" />
              <p className="text-sm text-amber-700 font-medium">Manual Entry — flagged for foreman review</p>
            </div>
            <div className="flex gap-3 items-end flex-wrap">
              <div>
                <label className="block text-xs text-slate-900 mb-1">Start Time</label>
                <input
                  data-testid="manual-time-input"
                  type="time"
                  value={manualTime}
                  onChange={e => setManualTime(e.target.value)}
                  className="px-3 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 outline-none"
                />
              </div>
              <div className="flex-1 min-w-[150px]">
                <label className="block text-xs text-slate-900 mb-1">Reason</label>
                <input
                  data-testid="manual-notes-input"
                  type="text"
                  value={manualNotes}
                  onChange={e => setManualNotes(e.target.value)}
                  placeholder="e.g. Phone died, forgot to clock in"
                  className="w-full px-3 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 text-sm outline-none"
                />
              </div>
              <button
                data-testid="manual-clock-in-btn"
                onClick={handleManualClockIn}
                disabled={submitting}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Submit
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Clocked in — show status + available hours
  const event = clockStatus.event;
  const isOut = clockStatus.clocked_out;
  const tookLunch = event?.took_lunch ?? true;
  const availHours = clockStatus.available_hours ?? 0;

  return (
    <div className={`rounded-xl border p-5 ${isOut ? 'bg-white border-slate-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="clock-in-banner">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isOut ? 'bg-slate-200' : 'bg-emerald-50'}`}>
            {isOut ? <StopCircle className="w-5 h-5 text-slate-900" /> : <Clock className="w-5 h-5 text-emerald-700 animate-pulse" />}
          </div>
          <div>
            <p className="text-slate-900 font-medium flex items-center gap-2">
              Clocked in at {fmtTime(event?.clock_in_time)}
              {event?.shift && event.shift !== 'days' && (
                <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200 capitalize">{event.shift}</span>
              )}
              {event?.is_manual_entry && (
                <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200">Manual</span>
              )}
              {event?.is_late && (
                <span className="text-xs bg-red-50 text-red-600 px-2 py-0.5 rounded-full border border-red-200">{event.late_minutes}min late</span>
              )}
            </p>
            {isOut ? (
              <p className="text-xs text-slate-900">Clocked out at {fmtTime(event?.clock_out_time)}</p>
            ) : (
              <p className="text-xs text-emerald-700">Working...</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Lunch toggle */}
          {!isOut && (
            <button
              data-testid="lunch-toggle-btn"
              onClick={handleLunchToggle}
              disabled={submitting}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors border ${
                tookLunch
                  ? 'bg-amber-50 border-amber-200 text-amber-700'
                  : 'bg-white border-slate-300 text-slate-900 hover:text-slate-900'
              }`}
            >
              <Coffee className="w-3.5 h-3.5" />
              {tookLunch ? 'Lunch: Yes (-30min)' : 'No Lunch'}
            </button>
          )}

          {/* Available hours badge */}
          <div className="text-center px-4 py-2 bg-white/60 rounded-lg border border-slate-200">
            <p className="text-2xl font-bold text-slate-900" data-testid="available-hours">{availHours}</p>
            <p className="text-[10px] text-slate-900 uppercase tracking-wider">hrs available</p>
          </div>

          {/* Clock out button */}
          {!isOut && (
            <button
              data-testid="clock-out-btn"
              onClick={() => initiateClockOut(tookLunch)}
              disabled={submitting}
              className="px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white rounded-xl font-semibold text-sm transition-colors flex items-center gap-2"
            >
              <StopCircle className="w-4 h-4" /> Clock Out
            </button>
          )}

          {/* Clock back in button (after clocking out) */}
          {isOut && (
            <button
              data-testid="clock-back-in-btn"
              onClick={handleClockIn}
              disabled={submitting}
              className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-xl font-semibold text-sm transition-colors flex items-center gap-2"
            >
              <PlayCircle className="w-4 h-4" /> Clock Back In
            </button>
          )}
        </div>
      </div>
      {/* Clock-out consumable reminder */}
      {showClockOutPrompt && (
        <div className="mt-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-slate-900 font-medium mb-3">Have you input all your consumables today?</p>
          <div className="flex gap-2">
            <button onClick={() => handleClockOut(pendingLunch)} disabled={submitting}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium">
              {submitting ? 'Clocking out...' : 'Yes — Clock Out'}
            </button>
            <button onClick={() => { setShowClockOutPrompt(false); onClockOutPromptChange?.(false); onClockChange?.('show-consumables'); }}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium">
              No — Log Consumables First
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ════════════════════════════════════════════════════════════════
   UNIFIED ENTRY TAB — Job-based time tracking
   ════════════════════════════════════════════════════════════════ */
function UnifiedEntryTab({ onClockOutPromptChange }) {
  const [clockStatus, setClockStatus] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [todaySessions, setTodaySessions] = useState([]);
  const [totalHours, setTotalHours] = useState(0);
  const [loading, setLoading] = useState(true);

  // WO picker for starting jobs
  const [woSearch, setWoSearch] = useState('');
  const [woResults, setWoResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showRecents, setShowRecents] = useState(false);

  // Consumable state (for active job)
  const [catalog, setCatalog] = useState([]);
  const [quickPicks, setQuickPicks] = useState({});
  const [submittingCons, setSubmittingCons] = useState(false);
  const [showEq, setShowEq] = useState(false);
  const [eqPick, setEqPick] = useState({ rate_basis: 'day', qty: 1 });
  const [myConsToday, setMyConsToday] = useState([]);

  // Stock materials state
  const [steelCategories, setSteelCategories] = useState([]);
  const [steelMembers, setSteelMembers] = useState([]);
  const [matCategory, setMatCategory] = useState('');
  const [matMember, setMatMember] = useState('');
  const [matType, setMatType] = useState('Mild Steel');
  const [matQty, setMatQty] = useState('');
  const [jobNote, setJobNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const submitJobNote = async () => {
    if (!jobNote.trim()) return;
    setSavingNote(true);
    try {
      const r = await axios.post(`${API}/job/note`, { note: jobNote.trim() });
      toast.success(r.data.attached_to === 'job' ? `Note added to ${r.data.work_order_number}` : 'Note added to your day');
      setJobNote('');
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save note'); }
    setSavingNote(false);
  };
  const [matUnit, setMatUnit] = useState('ft');

  // Edit session state
  const [editingSession, setEditingSession] = useState(null);
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');

  // Last week entries
  const [lastWeekTime, setLastWeekTime] = useState([]);
  const [lastWeekCons, setLastWeekCons] = useState([]);

  const [submitting, setSubmitting] = useState(false);

  const getRecentWOs = () => {
    try { return JSON.parse(localStorage.getItem('shop_recent_wos') || '[]'); } catch { return []; }
  };
  const saveRecentWO = (wo) => {
    const recents = getRecentWOs().filter(r => r.project_number !== wo.project_number);
    recents.unshift({ project_number: wo.project_number, client_name: wo.client_name || '', title: wo.title || wo.description || '' });
    localStorage.setItem('shop_recent_wos', JSON.stringify(recents.slice(0, 5)));
  };

  const refresh = useCallback(async () => {
    try {
      const [clockR, activeR, todayR] = await Promise.all([
        axios.get(`${API}/clock-status`),
        axios.get(`${API}/job/active`),
        axios.get(`${API}/job/today`),
      ]);
      setClockStatus(clockR.data);
      setActiveJob(activeR.data.active_job);
      setTodaySessions(todayR.data.sessions);
      setTotalHours(todayR.data.total_hours);
      // Fetch today's consumables
      const consFrom = today();
      axios.get(`${API}/consumable-entries`, { params: { mine: true, date_from: consFrom, date_to: consFrom } })
        .then(r => setMyConsToday(r.data)).catch(() => {});
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
    axios.get(`${API}/catalog`).then(r => setCatalog(r.data)).catch(() => {});
    axios.get(`${API}/steel-categories`).then(r => setSteelCategories(r.data)).catch(() => {});
    // Last week
    const lwSun = sundayOf(today());
    const lwStart = new Date(lwSun + 'T12:00:00');
    lwStart.setDate(lwStart.getDate() - 7);
    const lwFrom = localDate(lwStart);
    const lwEnd = new Date(lwSun + 'T12:00:00');
    lwEnd.setDate(lwEnd.getDate() - 1);
    const lwTo = localDate(lwEnd);
    axios.get(`${API}/time-entries`, { params: { mine: true, date_from: lwFrom, date_to: lwTo } }).then(r => setLastWeekTime(r.data)).catch(() => {});
    axios.get(`${API}/consumable-entries`, { params: { mine: true, date_from: lwFrom, date_to: lwTo } }).then(r => setLastWeekCons(r.data)).catch(() => {});
  }, [refresh]);

  // Auto-refresh every 60s
  useEffect(() => {
    const i = setInterval(refresh, 60000);
    return () => clearInterval(i);
  }, [refresh]);

  const searchWO = async (q) => {
    setWoSearch(q);
    setShowRecents(false);
    if (q.length < 2) { setWoResults([]); return; }
    setSearching(true);
    try {
      const r = await axios.get(`${API}/work-orders`, { params: { q } });
      setWoResults(r.data.slice(0, 8));
    } catch { setWoResults([]); }
    setSearching(false);
  };

  const handleWOFocus = () => {
    if (!woSearch) {
      const recents = getRecentWOs();
      if (recents.length > 0) { setWoResults(recents); setShowRecents(true); }
    }
  };

  const startJob = async (wo) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/job/start`, {
        work_order_number: wo.project_number,
        work_order_customer: wo.client_name || '',
        work_order_description: wo.title || wo.description || '',
      });
      saveRecentWO(wo);
      setWoSearch('');
      setWoResults([]);
      toast.success(`Started: ${wo.project_number}`);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start job');
    }
    setSubmitting(false);
  };

  const stopJob = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/job/stop`, {});
      toast.success('Job stopped');
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to stop job');
    }
    setSubmitting(false);
  };

  const submitConsumables = async () => {
    if (!activeJob) { toast.error('No active job'); return; }
    const entries = Object.entries(quickPicks)
      .filter(([_, qty]) => (parseFloat(qty) || 0) > 0)
      .map(([name, qty]) => {
        if (name.startsWith('CUSTOM:')) {
          // Custom item — no catalog match, price = 0 (manager fills in later)
          return { item: { name: name.replace('CUSTOM:', ''), unit: 'each', unit_cost: 0 }, quantity: qty, isCustom: true };
        }
        return { item: catalog.find(c => c.name === name), quantity: qty };
      })
      .filter(e => e.item);
    if (entries.length === 0) { toast.error('Select consumables first'); return; }

    setSubmittingCons(true);
    for (const row of entries) {
      try {
        await axios.post(`${API}/consumable-entries`, {
          work_order_number: activeJob.work_order_number,
          work_order_customer: activeJob.work_order_customer || '',
          work_order_description: activeJob.work_order_description || '',
          date: today(),
          item_name: row.item.name,
          quantity: parseFloat(row.quantity),
          unit: row.item.unit,
          unit_cost: row.item.unit_cost,
          notes: row.isCustom ? 'Custom item — needs pricing' : '',
        });
      } catch (e) {
        toast.error(`Failed: ${row.item.name}`);
      }
    }
    toast.success('Consumables logged');
    setQuickPicks({});
    setSubmittingCons(false);
    refresh(); // reload to show logged consumables
  };

  const saveSessionEdit = async (sessionId, originalStartTime) => {
    try {
      // Convert HH:MM time inputs to ISO using the session's date
      const dateStr = originalStartTime.split('T')[0];
      const toISO = (t) => new Date(`${dateStr}T${t}:00`).toISOString();
      await axios.put(`${API}/job/${sessionId}`, { start_time: toISO(editStart), end_time: toISO(editEnd) });
      toast.success('Session updated');
      setEditingSession(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Toronto' }); }
    catch { return ''; }
  };

  const fmtTimeInput = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
    } catch { return ''; }
  };

  if (loading) return <div className="text-center py-8 text-slate-900">Loading...</div>;

  const isClockedIn = clockStatus?.clocked_in && !clockStatus?.clocked_out;

  return (
    <div className="space-y-4">
      <ClockInBanner onClockChange={refresh} onAvailableHoursChange={() => {}} onClockOutPromptChange={onClockOutPromptChange} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">

          {/* ── Active Job Panel ── */}
          {isClockedIn && activeJob && (
            <div className="bg-emerald-50 rounded-xl border border-emerald-200 p-5" data-testid="active-job-panel">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs text-emerald-700 uppercase tracking-wider font-semibold">Currently Working On</p>
                  <p className="text-xl font-bold text-slate-900 mt-1">
                    <span className="font-mono text-blue-700">{activeJob.work_order_number}</span>
                    <span className="mx-2 text-slate-900">|</span>
                    {activeJob.work_order_customer}
                  </p>
                  {activeJob.work_order_description && (
                    <p className="text-sm text-slate-900 mt-0.5">{activeJob.work_order_description}</p>
                  )}
                  <p className="text-xs text-slate-900 mt-1">Started at {fmtTime(activeJob.start_time)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-center px-4 py-2 bg-white/60 rounded-lg border border-slate-200">
                    <p className="text-2xl font-bold text-emerald-700" data-testid="live-duration">
                      {activeJob.live_duration_hours ? activeJob.live_duration_hours.toFixed(1) : '0.0'}
                    </p>
                    <p className="text-[10px] text-slate-900 uppercase">hrs on job</p>
                  </div>
                  <button data-testid="stop-job-btn" onClick={stopJob} disabled={submitting}
                    className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold flex items-center gap-2">
                    <StopCircle className="w-4 h-4" /> Stop Job
                  </button>
                </div>
              </div>

              {/* Quick consumables for active job */}
              <div className="border-t border-emerald-200 pt-4">
                <h4 className="text-xs text-slate-900 font-bold uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Package className="w-3 h-3 text-emerald-700" /> Log Consumables for this Job
                </h4>
                <QuickConsumablePicker catalog={catalog} quickPicks={quickPicks} setQuickPicks={setQuickPicks} />
                {Object.keys(quickPicks).length > 0 && (
                  <button onClick={submitConsumables} disabled={submittingCons}
                    className="mt-3 w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-xl font-semibold text-sm">
                    {submittingCons ? 'Logging...' : `Log ${Object.keys(quickPicks).length} Consumable(s)`}
                  </button>
                )}
                {/* 365-owned equipment at the day / week / month rate */}
                <div className="mt-3 pt-3 border-t border-emerald-200" data-testid="shop-equipment-log">
                  <button onClick={() => setShowEq(v => !v)} className="text-xs text-slate-900 font-bold uppercase tracking-wider flex items-center gap-1" data-testid="shop-equipment-toggle">
                    <Truck className="w-3 h-3 text-blue-500" /> {showEq ? '▾' : '▸'} Log 365 Equipment on this Job
                  </button>
                  {showEq && (
                    <div className="mt-2">
                      <EquipmentRatePicker value={eqPick} onChange={setEqPick} dark testid="shop-eq"
                        onAdd={async (p) => {
                          try {
                            await axios.post(`${API}/consumable-entries`, {
                              work_order_number: activeJob.work_order_number,
                              work_order_customer: activeJob.work_order_customer || '',
                              work_order_description: activeJob.work_order_description || '',
                              date: today(), item_name: p.name, quantity: p.qty, unit: p.rate_basis, unit_cost: p.unit_price,
                              rate_id: p.rate_id, rate_basis: p.rate_basis,
                            });
                            toast.success(`${p.name} — ${p.qty} ${p.rate_basis}${p.qty === 1 ? '' : 's'} logged`);
                            setEqPick({ rate_basis: p.rate_basis, qty: 1 });
                            refresh();
                          } catch (e) { toast.error(e.response?.data?.detail || 'Failed to log equipment'); }
                        }} />
                    </div>
                  )}
                </div>
                {/* Today's logged consumables */}
                {myConsToday.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-emerald-200">
                    <p className="text-xs text-slate-900 font-semibold mb-1">Logged Today:</p>
                    <div className="flex flex-wrap gap-1">
                      {myConsToday.map((c, i) => (
                        <span key={i} className="text-[10px] px-2 py-0.5 bg-slate-200 text-slate-900 rounded border border-slate-200">
                          {c.item_name} x{c.quantity} <span className="text-blue-700">{c.work_order_number}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Stock Materials Entry ── */}
          {isClockedIn && activeJob && (
            <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm p-5" data-testid="materials-panel">
              <h4 className="text-xs text-slate-900 font-bold uppercase tracking-wider mb-3 flex items-center gap-1">
                <Package className="w-3 h-3 text-blue-700" /> Log Stock Materials
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <div>
                  <label className="text-[10px] text-slate-900">Material</label>
                  <select value={matType} onChange={e => setMatType(e.target.value)}
                    className="w-full px-2 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-xs outline-none">
                    <option value="Mild Steel">Mild Steel</option>
                    <option value="Stainless Steel">Stainless Steel</option>
                    <option value="Self-Weathering">Self-Weathering</option>
                    <option value="AR-400 Plate">AR-400 Plate</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-slate-900">Category</label>
                  <select value={matCategory} onChange={e => {
                    setMatCategory(e.target.value);
                    setMatMember('');
                    if (e.target.value) axios.get(`${API}/steel-members`, { params: { category: e.target.value } }).then(r => setSteelMembers(r.data)).catch(() => {});
                  }} className="w-full px-2 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-xs outline-none">
                    <option value="">Select...</option>
                    {steelCategories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-slate-900">Size</label>
                  <select value={matMember} onChange={e => setMatMember(e.target.value)}
                    className="w-full px-2 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-xs outline-none">
                    <option value="">Select...</option>
                    {steelMembers.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-slate-900">Qty</label>
                  <div className="flex gap-1">
                    <input type="number" value={matQty} onFocus={e => e.target.select()}
                      onChange={e => setMatQty(e.target.value)} placeholder="0"
                      className="w-full px-2 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-xs text-center outline-none" />
                    <select value={matUnit} onChange={e => setMatUnit(e.target.value)}
                      className="px-1 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-[10px] outline-none">
                      <option value="ft">ft</option>
                      <option value="sqft">sqft</option>
                      <option value="pcs">pcs</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-end">
                  <button onClick={async () => {
                    if (!matMember || !matQty) { toast.error('Select size and enter quantity'); return; }
                    try {
                      await axios.post(`${API}/consumable-entries`, {
                        work_order_number: activeJob.work_order_number,
                        work_order_customer: activeJob.work_order_customer || '',
                        work_order_description: activeJob.work_order_description || '',
                        date: today(),
                        item_name: `${matMember} (${matType})`,
                        quantity: parseFloat(matQty),
                        unit: matUnit,
                        unit_cost: 0,
                        is_material: true,
                        material_type: matType,
                      });
                      toast.success(`Logged ${matQty}${matUnit} ${matMember}`);
                      setMatQty(''); setMatMember('');
                      refresh();
                    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                  }} className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium">
                    Log Material
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Quick Note (why I'm jumping jobs, what's in the way, etc.) ── */}
          {isClockedIn && (
            <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm p-5" data-testid="job-note-panel">
              <h4 className="text-xs text-slate-900 font-bold uppercase tracking-wider mb-1 flex items-center gap-1">
                <MessageSquare className="w-3 h-3 text-blue-700" /> Leave a Note
              </h4>
              <p className="text-[11px] text-slate-600 mb-2">
                {activeJob
                  ? <>Goes on your <span className="font-mono text-blue-500 font-bold">{activeJob.work_order_number}</span> session so the foreman knows why (e.g. "switched tasks — truck was in the way").</>
                  : 'Goes on your day so the foreman can see it.'}
              </p>
              <div className="flex gap-2">
                <input type="text" value={jobNote} onChange={e => setJobNote(e.target.value)} maxLength={500}
                  onKeyDown={e => { if (e.key === 'Enter') submitJobNote(); }}
                  placeholder="e.g. Switched to another task because the truck was in the way"
                  className="flex-1 px-3 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-sm outline-none focus:border-blue-400"
                  data-testid="job-note-input" />
                <button onClick={submitJobNote} disabled={savingNote || !jobNote.trim()}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-xs font-medium"
                  data-testid="job-note-submit">
                  {savingNote ? 'Saving…' : 'Add Note'}
                </button>
              </div>
            </div>
          )}

          {/* ── Start/Switch Job Panel ── */}
          {isClockedIn && (
            <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm p-5" data-testid="start-job-panel">
              <h3 className="text-sm font-medium text-slate-900 mb-3 flex items-center gap-2">
                <PlayCircle className="w-4 h-4 text-blue-700" />
                {activeJob ? 'Switch to Another Job' : 'Start a Job'}
              </h3>
              <div className="relative">
                <Search className="absolute left-3 top-3 w-5 h-5 text-slate-900" />
                <input
                  data-testid="wo-search-input"
                  type="text" value={woSearch} onChange={e => searchWO(e.target.value)} onFocus={handleWOFocus}
                  placeholder="Search WO#, customer, or description..."
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 text-lg focus:border-blue-500 outline-none"
                />
                {searching && <RefreshCw className="absolute right-3 top-3 w-5 h-5 text-blue-700 animate-spin" />}
              </div>
              {woResults.length > 0 && (
                <div className="mt-2 max-h-60 overflow-y-auto bg-white border border-slate-300 rounded-lg divide-y divide-slate-200">
                  {showRecents && <div className="px-4 py-1.5 bg-slate-100 text-xs text-slate-900 font-medium uppercase tracking-wider">Recent Jobs</div>}
                  {woResults.map(wo => (
                    <button key={wo.project_number || wo.id} onClick={() => startJob(wo)}
                      className="w-full text-left px-4 py-3 hover:bg-white/60 transition-colors" data-testid={`wo-result-${wo.project_number}`}>
                      <span className="font-mono text-blue-700 font-bold">{wo.project_number}</span>
                      <span className="mx-2 text-slate-900">|</span>
                      <span className="text-slate-900">{wo.client_name || 'No customer'}</span>
                      {(wo.title || wo.description) && <p className="text-xs text-slate-900 mt-0.5 truncate">{wo.title || wo.description}</p>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Today's Job Sessions ── */}
          {todaySessions.length > 0 && (
            <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm p-5" data-testid="today-sessions">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-slate-900 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-700" /> Today's Jobs
                </h3>
                <span className="text-sm font-bold text-blue-700">{totalHours}h total</span>
              </div>
              <div className="space-y-2">
                {todaySessions.map(s => (
                  <div key={s.id} className={`p-3 rounded-lg border ${!s.end_time ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white/50'}`}>
                    {editingSession === s.id ? (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-blue-700 font-bold text-sm">{s.work_order_number}</span>
                          <span className="text-slate-900 text-sm">{s.work_order_customer}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <input type="time" value={editStart} onChange={e => setEditStart(e.target.value)}
                            className="px-2 py-1 bg-white border border-blue-500 rounded text-slate-900 text-sm outline-none" />
                          <span className="text-slate-900">to</span>
                          <input type="time" value={editEnd} onChange={e => setEditEnd(e.target.value)}
                            className="px-2 py-1 bg-white border border-blue-500 rounded text-slate-900 text-sm outline-none" />
                          <button onClick={() => saveSessionEdit(s.id, s.start_time)}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium">Save</button>
                          <button onClick={() => setEditingSession(null)} className="text-xs text-slate-900">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="font-mono text-blue-700 font-bold text-sm">{s.work_order_number}</span>
                          <span className="mx-2 text-slate-900">|</span>
                          <span className="text-slate-900 text-sm">{s.work_order_customer}</span>
                          <p className="text-xs text-slate-900 mt-0.5">
                            {fmtTime(s.start_time)} — {s.end_time ? fmtTime(s.end_time) : <span className="text-emerald-700">Active</span>}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-slate-900">
                            {s.end_time ? `${s.duration_hours || 0}h` : `${(s.live_duration_hours || 0).toFixed(1)}h`}
                          </span>
                          {s.end_time && (
                            <button onClick={() => {
                              setEditingSession(s.id);
                              setEditStart(fmtTimeInput(s.start_time));
                              setEditEnd(fmtTimeInput(s.end_time));
                            }} className="text-slate-900 hover:text-blue-700">
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {s.is_manual_edit && <span className="text-[10px] text-amber-700">edited</span>}
                        </div>
                      </div>
                    )}
                    {(s.notes || []).length > 0 && (
                      <ul className="mt-1.5 space-y-0.5" data-testid={`session-notes-${s.id}`}>
                        {s.notes.map(n => (
                          <li key={n.id} className="text-xs text-slate-700 flex items-start gap-1.5">
                            <MessageSquare className="w-3 h-3 text-blue-700 mt-0.5 flex-shrink-0" />
                            <span><span className="text-slate-500">{fmtTime(n.time)} —</span> {n.text}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right sidebar */}
        <div className="space-y-4">
          <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm p-4">
            <h3 className="text-sm font-medium text-slate-900 mb-3">Last Week's Time</h3>
            {lastWeekTime.length === 0 ? (
              <p className="text-slate-900 text-sm">No time entries</p>
            ) : (
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {lastWeekTime.map(e => (
                  <div key={e.id} className={`p-3 rounded-lg border ${statusColors[e.status]}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm font-bold">{e.work_order_number}</span>
                      <span className="text-xs capitalize px-2 py-0.5 rounded-full bg-slate-200">{e.status}</span>
                    </div>
                    <div className="text-xs text-slate-900 mt-1">
                      {fmtDate(e.date)} — {e.regular_hours}h reg{e.overtime_hours > 0 ? ` + ${e.overtime_hours}h OT` : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm p-4">
            <h3 className="text-sm font-medium text-slate-900 mb-3">Last Week's Consumables</h3>
            {lastWeekCons.length === 0 ? (
              <p className="text-slate-900 text-sm">No consumable entries</p>
            ) : (
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {lastWeekCons.map(e => (
                  <div key={e.id} className={`p-3 rounded-lg border ${statusColors[e.status]}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm font-bold">{e.work_order_number}</span>
                      <span className="text-xs capitalize px-2 py-0.5 rounded-full bg-slate-200">{e.status}</span>
                    </div>
                    <div className="text-xs text-slate-900 mt-1">
                      {fmtDate(e.date)} — {e.item_name} x{e.quantity}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Quick Consumable Picker (compact grid) ──────────────────────── */
function QuickConsumablePicker({ catalog, quickPicks, setQuickPicks }) {
  const [expandedCats, setExpandedCats] = useState({});
  const [customItem, setCustomItem] = useState('');
  const [customQty, setCustomQty] = useState(1);
  const categoryOrder = ['ABRASIVE', 'WELDING', 'WELDING WIRE', 'WELDING ROD', 'WELDING GAS', 'PAINT', 'SAFETY', 'HOLE SAWS', 'DRILL BITS', 'ROTOBROACHES', 'TAPS & DIES', 'BLADES', 'TAPE & SIGNS', 'PIPE SUPPLIES', 'ADHESIVE', 'LUBRICANT', 'SHIM', 'MARKING', 'SHOP SUPPLY', 'EQUIPMENT'];
  const grouped = {};
  for (const item of catalog) {
    const cat = item.category || 'Other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(item);
  }
  const orderedCats = categoryOrder.filter(c => grouped[c]);
  Object.keys(grouped).forEach(c => { if (!orderedCats.includes(c)) orderedCats.push(c); });

  const toggleCat = (cat) => setExpandedCats(prev => ({ ...prev, [cat]: !prev[cat] }));

  const addOne = (name) => setQuickPicks(prev => ({ ...prev, [name]: (parseInt(prev[name]) || 0) + 1 }));
  const subOne = (name) => setQuickPicks(prev => {
    const val = (parseInt(prev[name]) || 1) - 1;
    if (val <= 0) { const copy = { ...prev }; delete copy[name]; return copy; }
    return { ...prev, [name]: val };
  });

  const selectedCount = Object.keys(quickPicks).length;

  return (
    <div>
      {/* Custom Item Entry */}
      <div className="mb-3 p-3 bg-slate-100/80 rounded-lg border border-slate-200">
        <p className="text-xs text-slate-900 font-semibold mb-2">Not in the list? Add a custom item:</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={customItem}
            onChange={e => setCustomItem(e.target.value)}
            placeholder="Item name (e.g. 3/8 bolt grade 8)"
            className="flex-1 px-3 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-sm placeholder-slate-400 outline-none focus:border-blue-500"
          />
          <input
            type="number"
            value={customQty}
            onFocus={e => e.target.select()}
            onChange={e => setCustomQty(Math.max(1, parseInt(e.target.value) || 1))}
            min="1"
            className="w-16 px-2 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-sm text-center outline-none"
          />
          <button
            onClick={() => {
              if (!customItem.trim()) return;
              const key = `CUSTOM:${customItem.trim()}`;
              setQuickPicks(prev => ({ ...prev, [key]: (parseInt(prev[key]) || 0) + customQty }));
              setCustomItem('');
              setCustomQty(1);
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium whitespace-nowrap"
          >
            + Add
          </button>
        </div>
      </div>

      {selectedCount > 0 && (
        <div className="text-xs text-emerald-700 mb-2 font-medium">{selectedCount} item(s) selected — tap + to add more, - to remove</div>
      )}

      {/* Show selected custom items */}
      {Object.entries(quickPicks).filter(([k]) => k.startsWith('CUSTOM:')).length > 0 && (
        <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-[10px] text-blue-700 font-semibold mb-1.5 uppercase tracking-wider">Custom Items (price TBD)</p>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(quickPicks).filter(([k]) => k.startsWith('CUSTOM:')).map(([key, qty]) => (
              <div key={key} className="flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded-lg">
                <button onClick={() => subOne(key)}
                  className="w-5 h-5 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white rounded text-xs font-bold">-</button>
                <span className="text-xs text-blue-700 px-1">{key.replace('CUSTOM:', '')} x{qty}</span>
                <button onClick={() => addOne(key)}
                  className="w-5 h-5 flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-bold">+</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-1">
        {orderedCats.map(cat => {
          const items = grouped[cat];
          const isExpanded = expandedCats[cat];
          const visible = isExpanded ? items : items.slice(0, 10);
          const hasMore = items.length > 10;

          return (
            <div key={cat} className="rounded-lg border border-slate-200 overflow-hidden">
              <button onClick={() => toggleCat(cat)}
                className="w-full flex items-center justify-between px-3 py-2 bg-white/40 hover:bg-white/60 transition-colors">
                <span className="text-sm font-semibold text-slate-900">{cat}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-slate-900">{items.length}</span>
                  <ChevronDown className={`w-3.5 h-3.5 text-slate-900 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
              </button>
              <div className="p-2">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-1">
                  {visible.map(item => {
                    const qty = quickPicks[item.name];
                    const isActive = qty !== undefined;
                    return (
                      <div key={item.name} className={`rounded-lg border transition-all ${
                        isActive ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-white/50'
                      }`}>
                        {isActive ? (
                          <div className="flex items-center justify-between px-1.5 py-1">
                            <button onClick={() => subOne(item.name)}
                              className="w-6 h-6 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white rounded text-sm font-bold">-</button>
                            <div className="text-center flex-1 min-w-0 px-1">
                              <span className="text-emerald-700 text-[10px] leading-tight block truncate">{item.name}</span>
                              <span className="text-slate-900 text-sm font-bold">{qty}</span>
                            </div>
                            <button onClick={() => addOne(item.name)}
                              className="w-6 h-6 flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white rounded text-sm font-bold">+</button>
                          </div>
                        ) : (
                          <button onClick={() => addOne(item.name)}
                            className="w-full px-2 py-1.5 text-xs text-slate-900 hover:bg-slate-100 text-left truncate">
                            {item.name}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                {!isExpanded && hasMore && (
                  <button onClick={() => toggleCat(cat)}
                    className="mt-1 text-[10px] text-blue-700 hover:text-blue-700 px-2">
                    +{items.length - 10} more...
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Consumable Row (inline picker) ─────────────────────────────── */
function ConsumableRowInput({ row, idx, catalog, onUpdate, onRemove, recentItems = [] }) {
  const filtered = catalog.filter(c =>
    c.name.toLowerCase().includes((row.filter || '').toLowerCase()) ||
    c.category.toLowerCase().includes((row.filter || '').toLowerCase())
  );
  const hasFilter = (row.filter || '').length > 0;
  const recentSet = new Set(recentItems);

  return (
    <div className="flex gap-2 items-start">
      <div className="flex-1 relative">
        <button onClick={() => onUpdate(idx, 'catOpen', !row.catOpen)}
          className="w-full px-3 py-2.5 bg-slate-50 border border-slate-300 rounded-lg text-left flex items-center justify-between text-sm">
          <span className={row.item ? 'text-slate-900' : 'text-slate-900'}>
            {row.item ? row.item.name : 'Select item...'}
          </span>
          <ChevronDown className="w-4 h-4 text-slate-900" />
        </button>
        {row.catOpen && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-slate-300 rounded-lg shadow-xl max-h-52 overflow-y-auto">
            <div className="sticky top-0 p-2 bg-white">
              <input type="text" value={row.filter || ''} onChange={e => onUpdate(idx, 'filter', e.target.value)}
                placeholder="Filter..." autoFocus
                className="w-full px-3 py-1.5 bg-white/80 border border-slate-200 rounded text-slate-900 placeholder-slate-400 text-xs outline-none" />
            </div>
            {!hasFilter && recentItems.length > 0 && filtered.some(c => recentSet.has(c.name)) && (
              <div className="px-3 py-1 bg-slate-100 text-xs text-slate-900 font-medium uppercase tracking-wider">Recently Used</div>
            )}
            {filtered.map((c, i) => {
              const isRecent = recentSet.has(c.name);
              const prevIsRecent = i > 0 && recentSet.has(filtered[i - 1]?.name);
              const showOtherHeader = !hasFilter && !isRecent && (i === 0 || prevIsRecent);
              return (
                <React.Fragment key={c.id}>
                  {showOtherHeader && recentItems.length > 0 && (
                    <div className="px-3 py-1 bg-slate-100 text-xs text-slate-900 font-medium uppercase tracking-wider">All Items</div>
                  )}
                  <button
                    onClick={() => { onUpdate(idx, 'item', c); onUpdate(idx, 'catOpen', false); onUpdate(idx, 'filter', ''); }}
                    className="w-full text-left px-3 py-2 hover:bg-white/60 border-t border-slate-200 text-sm">
                    <span className="text-slate-900">{c.name}</span>
                    <span className="text-xs text-slate-900 ml-2">{c.category}</span>
                  </button>
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
      <div className="w-20">
        <input type="number" step="1" min="0" value={row.quantity} onChange={e => onUpdate(idx, 'quantity', e.target.value)}
          placeholder="Qty"
          className="w-full px-2 py-2.5 bg-slate-50 border border-slate-300 rounded-lg text-slate-900 text-center font-bold focus:border-blue-500 outline-none" />
      </div>
      <button onClick={() => onRemove(idx)} className="text-red-600 hover:text-red-700 p-2 self-center">
        <XCircle className="w-4 h-4" />
      </button>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   ATTENDANCE PANEL — Foreman view of who's clocked in today
   ════════════════════════════════════════════════════════════════ */

/* ════════════════════════════════════════════════════════════════
   FOREMAN TAB — Unified crew management
   ════════════════════════════════════════════════════════════════ */
function ForemanTab() {
  const { user } = useAuth();
  const [crew, setCrew] = useState([]);
  const [notIn, setNotIn] = useState([]);
  const [notInAction, setNotInAction] = useState(null); // {employee_id, employee_name}
  const notInPanelRef = useRef(null);
  const [vacRange, setVacRange] = useState(null); // {from, to} — multi-day vacation picker
  const [loading, setLoading] = useState(true);
  const [expandedEmp, setExpandedEmp] = useState(null);
  const [empConsumables, setEmpConsumables] = useState([]);
  const [empSessions, setEmpSessions] = useState([]);
  const [empNotes, setEmpNotes] = useState([]);
  // Pending entries for approval
  const [pending, setPending] = useState({ time_entries: [], consumable_entries: [] });
  const [idleEmployees, setIdleEmployees] = useState([]);
  const [weekStatus, setWeekStatus] = useState(null);
  const [forklifts, setForklifts] = useState([]);
  const [unassignedForklifts, setUnassignedForklifts] = useState([]);
  const [expandedForklift, setExpandedForklift] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [selectedDate, setSelectedDate] = useState(today());
  const [newNote, setNewNote] = useState('');
  const [noteCategory, setNoteCategory] = useState('general');

  // Inline price edit for pending consumables
  const [editingPriceId, setEditingPriceId] = useState(null);
  const [priceInput, setPriceInput] = useState('');
  const [priceMode, setPriceMode] = useState('unit'); // 'lb', 'unit', 'flat'

  // Edit session
  const [editingId, setEditingId] = useState(null);
  const [editWO, setEditWO] = useState('');
  const [editWOResults, setEditWOResults] = useState([]);
  const [editWOSelected, setEditWOSelected] = useState(null);
  const [editStart, setEditStart] = useState('');
  const [editEnd, setEditEnd] = useState('');

  // Add session
  const [addingFor, setAddingFor] = useState(null);
  const [addWO, setAddWO] = useState('');
  const [addWOResults, setAddWOResults] = useState([]);
  const [addSelectedWO, setAddSelectedWO] = useState(null);
  const [addStart, setAddStart] = useState('');
  const [addEnd, setAddEnd] = useState('');
  const [searching, setSearching] = useState(false);

  // Track approved employees
  const [approvedEmps, setApprovedEmps] = useState(new Set());
  const [selectedEntries, setSelectedEntries] = useState([]);

  // Foreman add consumable
  const [addConsFor, setAddConsFor] = useState(null);
  const [consSearch, setConsSearch] = useState('');
  const [editConsQtyId, setEditConsQtyId] = useState(null);
  const [editConsQtyVal, setEditConsQtyVal] = useState('');
  const [consQty, setConsQty] = useState(1);
  const [catalog, setCatalog] = useState([]);
  
  // Add employee who didn't clock in
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [allEmployees, setAllEmployees] = useState([]);
  const [selectedEmpToAdd, setSelectedEmpToAdd] = useState(null);
  const [empSearch, setEmpSearch] = useState('');

  // Job Material form
  const [showJobMat, setShowJobMat] = useState(false);
  // Job Consumable form
  const [showJobCons, setShowJobCons] = useState(false);
  const [jcWO, setJcWO] = useState('');
  const [jcWOResults, setJcWOResults] = useState([]);
  const [jcSelectedWO, setJcSelectedWO] = useState(null);
  const [jcItems, setJcItems] = useState({});
  const [jmWO, setJmWO] = useState('');
  const [jmWOResults, setJmWOResults] = useState([]);
  const [jmSelectedWO, setJmSelectedWO] = useState(null);
  const [jmMatType, setJmMatType] = useState('Mild Steel');
  const [jmMode, setJmMode] = useState('plate'); // 'plate' or 'linear'
  const [jmThickness, setJmThickness] = useState('');
  const [jmWidth, setJmWidth] = useState('');
  const [jmLength, setJmLength] = useState('');
  const [jmPcs, setJmPcs] = useState('1');
  const [jmCategory, setJmCategory] = useState('');
  const [jmMember, setJmMember] = useState('');
  const [jmLinearFt, setJmLinearFt] = useState('');
  const [jmSteelCats, setJmSteelCats] = useState([]);
  const [jmSteelMembers, setJmSteelMembers] = useState([]);

  const isToday = selectedDate === today();

  const load = useCallback(async () => {
    try {
      if (isToday) {
        const [crewR, attR] = await Promise.all([
          axios.get(`${API}/job/crew-status`),
          axios.get(`${API}/attendance/today`),
        ]);
        setCrew(crewR.data.crew || []);
        setNotIn(attR.data.not_clocked_in || []);
      } else {
        // Past date: load sessions + clock events
        const [sessR, clockR] = await Promise.all([
          axios.get(`${API}/job/all-today`, { params: { date: selectedDate } }),
          axios.get(`${API}/attendance/history`, { params: { date_from: selectedDate, date_to: selectedDate } }),
        ]);
        const emps = sessR.data.employees || [];
        const clockEvents = clockR.data.events || [];
        
        // Check approval status for each employee on this date
        const approvedChecks = await Promise.all(
          emps.map(e => axios.get(`${API}/time-entries`, { params: { mine: false, employee_id: e.employee_id, date_from: selectedDate, date_to: selectedDate } }).catch(() => ({ data: [] })))
        );
        
        setCrew(emps.map((e, idx) => {
          const ce = clockEvents.find(c => c.employee_id === e.employee_id);
          const entries = approvedChecks[idx]?.data || [];
          const hasApproved = Array.isArray(entries) && entries.some(t => t.status === 'approved');
          // Calculate from raw timestamps for accuracy (not stored rounded values)
          const rawUnrounded = e.sessions.reduce((sum, s) => {
            if (s.start_time && s.end_time) {
              let hrs = (new Date(s.end_time) - new Date(s.start_time)) / 3600000;
              if (hrs < 0) hrs += 24; // overnight
              return sum + hrs;
            }
            return sum + (s.duration_hours || 0);
          }, 0);
          // Round to nearest 0.5h (matches payroll calculation)
          const rawHours = Math.round(rawUnrounded * 2) / 2;
          const tookLunch = ce?.took_lunch ?? true;
          const lunchDeducted = tookLunch && rawHours > 0.5;
          const finalHours = lunchDeducted ? rawHours - 0.5 : rawHours;
          return {
            employee_id: e.employee_id,
            employee_name: e.employee_name,
            active_job: null,
            clock_in_time: ce?.clock_in_time || null,
            clock_out_time: ce?.clock_out_time || null,
            is_late: ce?.is_late || false,
            late_minutes: ce?.late_minutes || 0,
            is_approved: hasApproved,
            approved_by: (() => {
              const approved = Array.isArray(entries) ? entries.find(t => t.status === 'approved') : null;
              return approved ? `${approved.approved_by || 'Unknown'} — ${approved.approved_at ? new Date(approved.approved_at).toLocaleDateString() : ''}` : '';
            })(),
            total_hours_today: finalHours,
            session_hours: finalHours,
            approved_hours: hasApproved ? Math.round(entries.filter(t => t.status === 'approved').reduce((sum, t) => sum + (t.regular_hours || 0) + (t.overtime_hours || 0), 0) * 100) / 100 : null,
            approval_stale: hasApproved && Math.abs(entries.filter(t => t.status === 'approved').reduce((sum, t) => sum + (t.regular_hours || 0) + (t.overtime_hours || 0), 0) - finalHours) > 0.01,
            approval_ot_mode: (entries.find(t => t.status === 'approved' && t.approval_ot_mode) || {}).approval_ot_mode || null,
            raw_hours: rawHours,
            took_lunch: tookLunch,
            lunch_deducted: lunchDeducted,
            job_count: e.sessions.length,
            wo_summary: (() => {
              const map = {};
              e.sessions.forEach(s => {
                const wo = s.work_order_number || '';
                if (!wo) return;
                if (!map[wo]) map[wo] = { wo, customer: s.work_order_customer || '', hours: 0 };
                const hrs = s.start_time && s.end_time ? (() => { let h = (new Date(s.end_time) - new Date(s.start_time)) / 3600000; if (h < 0) h += 24; return h; })() : (s.duration_hours || 0);
                map[wo].hours += hrs;
              });
              return Object.values(map).map(v => ({ ...v, hours: Math.round(v.hours * 2) / 2 }));
            })(),
          };
        }));
        setNotIn([]);
      }
      // Load pending entries scoped to the week being viewed (Sun–Sat of selectedDate)
      const pendingR = await axios.get(`${API}/foreman/pending`, {
        params: { date_from: sundayOf(selectedDate), date_to: saturdayOf(selectedDate) },
      });
      setPending(pendingR.data);
      setSelectedEntries([]);
      // Load idle employees (today only) and week status
      axios.get(`${API}/foreman/idle-employees`).then(r => setIdleEmployees(r.data || [])).catch(() => {});
      axios.get(`${API}/foreman/week-approval-status`, { params: { date: selectedDate } }).then(r => setWeekStatus(r.data)).catch(() => {});
      axios.get(`${API}/foreman/forklift-inspections`).then(r => {
        const d = r.data || {};
        const fls = Array.isArray(d) ? d.filter(x => !x.unmatched) : (d.forklifts || []);
        const un = Array.isArray(d) ? [] : (d.unassigned || []);
        setForklifts(fls);
        setUnassignedForklifts(un);
      }).catch(() => {});
    } catch {}
    setLoading(false);
  }, [selectedDate, isToday]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users`).then(r => {
      const users = Array.isArray(r.data) ? r.data : r.data.users || [];
      setAllEmployees(users.filter(u => u.is_active !== false && u.shop_number));
    }).catch(() => {});
    axios.get(`${API}/catalog`).then(r => setCatalog(r.data)).catch(() => {});
    axios.get(`${API}/steel-categories`).then(r => setJmSteelCats(r.data)).catch(() => {});
  }, []);

  const assignForkliftInspection = async (inspectionId, forkliftId) => {
    if (!inspectionId || !forkliftId) return;
    try {
      await axios.post(`${API}/foreman/assign-forklift-inspection`, { inspection_id: inspectionId, forklift_id: forkliftId });
      toast.success(`Inspection assigned to ${forkliftId}`);
      load();
    } catch {
      toast.error('Could not assign inspection. Please try again.');
    }
  };


  const addEmployeeSession = async (empId) => {
    if (!addSelectedWO || !addStart) { toast.error('Select WO and start time'); return; }
    try {
      await axios.post(`${API}/job/foreman-add`, {
        employee_id: empId, work_order_number: addSelectedWO.project_number,
        work_order_customer: addSelectedWO.client_name || '',
        work_order_description: addSelectedWO.title || addSelectedWO.description || '',
        date: selectedDate, start_time: new Date(`${selectedDate}T${addStart}:00`).toISOString(),
        end_time: addEnd ? new Date(`${selectedDate}T${addEnd}:00`).toISOString() : undefined,
      });
      toast.success('Work period added');
      setShowAddEmployee(false); setSelectedEmpToAdd(null); setAddSelectedWO(null); setAddWO(''); setAddStart(''); setAddEnd('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const fmtTime = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Toronto' }); }
    catch { return ''; }
  };
  const fmtTimeInput = (iso) => {
    if (!iso) return '';
    try { const d = new Date(iso); return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`; }
    catch { return ''; }
  };

  const refreshEmployee = async (empId) => {
    if (!empId) return;
    setLoadingDetail(true);
    try {
      const [sessR, consR, notesR] = await Promise.all([
        axios.get(`${API}/job/all-today`, { params: { date: selectedDate } }),
        axios.get(`${API}/job/${empId}/consumables-today`),
        axios.get(`${API}/employee-notes/${empId}`),
      ]);
      const allSess = sessR.data || [];
      setEmpSessions(allSess.filter(s => s.employee_id === empId));
      setEmpConsumables(consR.data || []);
      setEmpNotes(notesR.data || []);
    } catch {}
    setLoadingDetail(false);
    load();
  };

  const expandEmployee = async (empId) => {
    if (expandedEmp === empId) { setExpandedEmp(null); return; }
    setExpandedEmp(empId);
    setLoadingDetail(true);
    try {
      const [sessR, consR, notesR] = await Promise.all([
        axios.get(`${API}/job/all-today`, { params: { date: selectedDate } }),
        axios.get(`${API}/job/${empId}/consumables-today`),
        axios.get(`${API}/employee-notes/${empId}`),
      ]);
      const empData = (sessR.data.employees || []).find(e => e.employee_id === empId);
      setEmpSessions(empData?.sessions || []);
      setEmpConsumables(consR.data || []);
      setEmpNotes(notesR.data || []);
    } catch {
      setEmpSessions([]);
      setEmpConsumables([]);
      setEmpNotes([]);
    }
    setLoadingDetail(false);
  };

  const removeLate = async (empId, clockEventId) => {
    try {
      await axios.put(`${API}/foreman/edit-clock/${clockEventId}`, { remove_late: true });
      toast.success('Late flag removed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const addNote = async (empId) => {
    if (!newNote.trim()) { toast.error('Enter a note'); return; }
    try {
      await axios.post(`${API}/employee-notes`, { employee_id: empId, note: newNote, category: noteCategory });
      toast.success('Note added');
      setNewNote(''); setNoteCategory('general');
      expandEmployee(empId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/employee-notes/${noteId}`);
      toast.success('Note deleted');
      if (expandedEmp) expandEmployee(expandedEmp);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const clockOutEmployee = async (empId) => {
    try {
      await axios.post(`${API}/foreman-clock-out`, { employee_id: empId, date: selectedDate });
      toast.success('Clocked out');
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const saveEdit = async (sessionId, origStart) => {
    try {
      const dateStr = origStart.split('T')[0];
      const toISO = (t) => new Date(`${dateStr}T${t}:00`).toISOString();
      const payload = { start_time: toISO(editStart), end_time: editEnd ? toISO(editEnd) : undefined };
      if (editWOSelected) {
        payload.work_order_number = editWOSelected.project_number;
        payload.work_order_customer = editWOSelected.client_name || '';
        payload.work_order_description = editWOSelected.title || editWOSelected.description || '';
      }
      await axios.put(`${API}/job/${sessionId}`, payload);
      toast.success(editWOSelected ? 'WO & time updated — approval reset' : 'Updated');
      setEditingId(null); setEditWOSelected(null); setEditWO('');
      load(); expandEmployee(expandedEmp);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const deleteSession = async (id) => {
    try {
      await axios.delete(`${API}/job/${id}`);
      toast.success('Deleted');
      expandEmployee(expandedEmp);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const searchWO = async (q) => {
    setAddWO(q);
    if (q.length < 2) { setAddWOResults([]); return; }
    setSearching(true);
    try { const r = await axios.get(`${API}/work-orders`, { params: { q } }); setAddWOResults(r.data.slice(0, 5)); }
    catch { setAddWOResults([]); }
    setSearching(false);
  };

  const addSession = async (empId) => {
    if (!addSelectedWO || !addStart) { toast.error('Select WO and start time'); return; }
    const targetDate = selectedDate;
    const toISO = (t) => new Date(`${targetDate}T${t}:00`).toISOString();
    try {
      await axios.post(`${API}/job/foreman-add`, {
        employee_id: empId, work_order_number: addSelectedWO.project_number,
        work_order_customer: addSelectedWO.client_name || '',
        work_order_description: addSelectedWO.title || addSelectedWO.description || '',
        date: targetDate, start_time: toISO(addStart), end_time: addEnd ? toISO(addEnd) : undefined,
      });
      toast.success('Work period added');
      setAddingFor(null); setAddSelectedWO(null); setAddWO(''); setAddStart(''); setAddEnd('');
      expandEmployee(empId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const fillToolCribGap = async (empId) => {
    try {
      const r = await axios.post(`${API}/job/fill-tool-crib-gap`, { employee_id: empId, date: selectedDate });
      if (r.data.gap_hours > 0) {
        toast.success(`Added ${r.data.gap_hours}h Tool Crib session`);
        load();
        expandEmployee(empId);
      } else {
        toast.info('No unassigned time to fill');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to fill gap'); }
  };

  const [otPrompt, setOtPrompt] = useState(null); // kept for backwards compat
  const [otSelWos, setOtSelWos] = useState({}); // { empId: [wo, ...] } per-project OT picks
  const toggleOtWo = (empId, wo) => {
    setOtSelWos(prev => {
      const cur = prev[empId] || [];
      return { ...prev, [empId]: cur.includes(wo) ? cur.filter(w => w !== wo) : [...cur, wo] };
    });
  };

  const approveEmployee = async (empId, mode = '8_ot', force = false, otWos = []) => {
    try {
      const r = await axios.post(`${API}/job/foreman-approve`, {
        employee_id: empId, date: selectedDate, force, ot_mode: mode, ot_wos: otWos,
      });
      const label = mode === 'all_reg' ? ' (all reg)'
        : mode === 'all_ot' ? ' (all OT)'
        : mode === 'select' ? ` (OT on ${otWos.join(', ')})` : '';
      toast.success(`Approved${label} — ${r.data.time_entries_created} entries`);
      // Optimistic update — mark row approved instantly, no full reload (avoids N+1 lag)
      setApprovedEmps(prev => new Set([...prev, empId]));
      setCrew(prev => prev.map(c => c.employee_id === empId
        ? { ...c, is_approved: true, approval_stale: false, approved_hours: c.session_hours ?? c.approved_hours, approval_ot_mode: mode, approved_by: `${user?.full_name || 'You'} — ${new Date().toLocaleDateString()}` }
        : c));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // Remove an employee's ENTIRE entry for the selected day — works even when there's no
  // removable clock event or the entry has already been approved.
  const dismissEmployeeDay = async (empId, empName, approved) => {
    const warn = approved ? '\n\n⚠️ This entry is APPROVED — removing it will also delete the approved hours for that day.' : '';
    if (!window.confirm(`Remove ${empName}'s entire entry for this day?\n\nThis deletes their clock-in, job sessions and time entries for the day.${warn}`)) return;
    try {
      await axios.delete(`${API}/foreman/dismiss-day/${empId}`, { params: { date: selectedDate } });
      toast.success(`${empName}'s entry removed`);
      setExpandedEmp(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to remove entry'); }
  };

  // Fab guy worked in the field but clocked into Shop Tracker → jump his day over to Field Tracker
  const [movingFor, setMovingFor] = useState(null);
  const [moveWO, setMoveWO] = useState('');
  const [moveWOResults, setMoveWOResults] = useState([]);
  const [moveSelectedWO, setMoveSelectedWO] = useState(null);
  const [moveServiceCall, setMoveServiceCall] = useState(false);
  const [moving, setMoving] = useState(false);
  // Move ONE job session (quick field job in the middle of a shop day) to Field Tracker
  const [movingSession, setMovingSession] = useState(null);
  const [moveSessWO, setMoveSessWO] = useState('');
  const [moveSessResults, setMoveSessResults] = useState([]);
  const [moveSessSelected, setMoveSessSelected] = useState(null);
  const [moveSessService, setMoveSessService] = useState(false);
  const [moveSessBusy, setMoveSessBusy] = useState(false);

  const openMoveSession = (s) => {
    setMovingSession(movingSession === s.id ? null : s.id);
    setMoveSessWO(s.work_order_number || ''); setMoveSessSelected(null); setMoveSessResults([]); setMoveSessService(false);
  };
  const searchMoveSessWO = async (q) => {
    setMoveSessWO(q); setMoveSessSelected(null);
    if (q.length < 2) { setMoveSessResults([]); return; }
    try { const r = await axios.get(`${API}/work-orders`, { params: { q } }); setMoveSessResults(r.data.slice(0, 6)); }
    catch { setMoveSessResults([]); }
  };
  const moveSessionToField = async (s, c, approved, force = false) => {
    const wo = moveSessSelected?.project_number || moveSessWO.trim();
    if (!wo) { toast.error('Pick the field work order first'); return; }
    if (!force && !window.confirm(`Move ${c.employee_name.split(' ')[0]}'s ${fmtTime(s.start_time)} – ${fmtTime(s.end_time)} session to Field Tracker on WO ${wo}?\n\nOnly this session moves — the rest of the shop day stays put and its shop hours are recalculated.${approved ? '\n\n⚠️ This shop day is already APPROVED — it goes back to pending for re-approval.' : ''}`)) return;
    setMoveSessBusy(true);
    try {
      const r = await axios.post(`${API}/move-session-to-field`, { session_id: s.id, work_order_number: wo, is_service_call: moveSessService, force: force || approved });
      const d = r.data;
      toast.success(`${c.employee_name} — ${d.hours}h moved to Field Tracker on WO ${wo}${d.customer ? ` (${d.customer})` : ''}, pending PM approval. ${d.shop_sessions_left} shop session${d.shop_sessions_left === 1 ? '' : 's'} left on the day.`, { duration: 9000 });
      setMovingSession(null);
      load();
    } catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm(`${e.response.data.detail}\n\nMove it anyway?`)) return moveSessionToField(s, c, approved, true);
      } else toast.error(e.response?.data?.detail || 'Failed to move the session');
    } finally { setMoveSessBusy(false); }
  };

  const searchMoveWO = async (q) => {
    setMoveWO(q); setMoveSelectedWO(null);
    if (q.length < 2) { setMoveWOResults([]); return; }
    try { const r = await axios.get(`${API}/work-orders`, { params: { q } }); setMoveWOResults(r.data.slice(0, 6)); }
    catch { setMoveWOResults([]); }
  };

  const moveToField = async (c, approved, force = false) => {
    const wo = moveSelectedWO?.project_number || moveWO.trim();
    if (!wo) { toast.error('Pick the field work order first'); return; }
    if (!force && !window.confirm(`Move ${c.employee_name}'s whole day to Field Tracker on WO ${wo}?\n\nTheir shop clock-in, job sessions and shop time entries for this day are removed and the same clock in/out becomes a field entry (PM approves it in Field Tracker).${approved ? '\n\n⚠️ The shop time for this day is already APPROVED — it will be replaced.' : ''}`)) return;
    setMoving(true);
    try {
      const r = await axios.post(`${API}/move-to-field`, {
        employee_id: c.employee_id, date: selectedDate, work_order_number: wo,
        is_service_call: moveServiceCall, force: force || approved,
      });
      const d = r.data;
      toast.success(d.still_clocked_in
        ? `${c.employee_name} moved to Field Tracker on WO ${wo} — still clocked in, so they clock out in Field Tracker (or the PM can from Field Crew)`
        : `${c.employee_name} moved to Field Tracker — ${d.hours}h on WO ${wo}${d.customer ? ` (${d.customer})` : ''}, pending PM approval`,
        { duration: 9000 });
      setMovingFor(null); setMoveWO(''); setMoveSelectedWO(null); setMoveWOResults([]); setMoveServiceCall(false);
      setExpandedEmp(null);
      load();
    } catch (e) {
      if (e.response?.status === 409) {
        if (window.confirm(`${e.response.data.detail}\n\nMove it anyway?`)) return moveToField(c, approved, true);
      } else toast.error(e.response?.data?.detail || 'Failed to move to Field Tracker');
    } finally { setMoving(false); }
  };

  // Pending entries helpers
  const isEntrySelected = (id) => selectedEntries.some(s => s.id === id);
  const toggleEntry = (id, type) => {
    setSelectedEntries(prev => isEntrySelected(id) ? prev.filter(s => s.id !== id) : [...prev, { id, type }]);
  };
  const selectAllPending = () => {
    const all = [
      ...pending.time_entries.map(e => ({ id: e.id, type: 'time' })),
      ...pending.consumable_entries.map(e => ({ id: e.id, type: 'cons' })),
    ];
    setSelectedEntries(selectedEntries.length === all.length ? [] : all);
  };
  const doPendingAction = async (action) => {
    if (selectedEntries.length === 0) { toast.error('Select entries first'); return; }
    let reason = '';
    if (action === 'reject') reason = prompt('Rejection reason (optional):') || '';
    const timeIds = selectedEntries.filter(s => s.type === 'time').map(s => s.id);
    const consIds = selectedEntries.filter(s => s.type === 'cons').map(s => s.id);
    try {
      if (timeIds.length > 0) await axios.post(`${API}/foreman/approve-time`, { entry_ids: timeIds, action, reason });
      if (consIds.length > 0) await axios.post(`${API}/foreman/approve-consumables`, { entry_ids: consIds, action, reason });
      toast.success(`${action === 'approve' ? 'Approved' : 'Rejected'} ${selectedEntries.length} entries`);
      load();
    } catch { toast.error('Action failed'); }
  };

  const [statPopup, setStatPopup] = useState(null);

  if (loading) return <div className="text-center py-8 text-slate-900">Loading...</div>;

  const lateCount = crew.filter(c => c.is_late).length;
  const activeCount = crew.filter(c => c.active_job).length;
  const clockedOutCount = crew.filter(c => c.clock_out_time).length;

  return (
    <div className="space-y-4" data-testid="foreman-tab">
      {/* Date picker + Summary bar */}
      <div className="flex flex-wrap gap-3 items-center">
        <input type="date" value={selectedDate} onChange={e => { setSelectedDate(e.target.value); setExpandedEmp(null); }}
          className="px-3 py-1.5 bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-xs outline-none" />
        {!isToday && (
          <button onClick={() => setSelectedDate(today())} className="text-xs text-blue-700 hover:text-blue-700">Today</button>
        )}
        {/* Quick day buttons for the week */}
        <div className="flex gap-0.5 bg-white/60 rounded-lg shadow-sm p-0.5 border border-slate-200">
          {(() => {
            const d = new Date(selectedDate + 'T12:00:00');
            const dayOfWeek = d.getDay();
            const sun = new Date(d); sun.setDate(d.getDate() - dayOfWeek);
            return ['S','M','T','W','T','F','S'].map((label, i) => {
              const dt = new Date(sun); dt.setDate(sun.getDate() + i);
              const ds = localDate(dt);
              const isSel = ds === selectedDate;
              const isFuture = ds > today();
              return (
                <button key={i} disabled={isFuture} onClick={() => { setSelectedDate(ds); setExpandedEmp(null); }}
                  className={`w-7 h-7 rounded text-[10px] font-bold transition-colors ${isSel ? 'bg-blue-600 text-white' : isFuture ? 'text-slate-900' : 'text-slate-900 hover:bg-blue-100'}`}>
                  {label}
                </button>
              );
            });
          })()}
        </div>
        <button onClick={() => setStatPopup(statPopup === 'clocked_in' ? null : 'clocked_in')}
          className="text-xs px-3 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50 cursor-pointer transition-colors">
          {crew.length} {isToday ? 'clocked in' : 'employees'}
        </button>
        <button onClick={() => setStatPopup(statPopup === 'on_jobs' ? null : 'on_jobs')}
          className="text-xs px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-50 cursor-pointer transition-colors">
          {activeCount} on jobs
        </button>
        {lateCount > 0 && (
          <button onClick={() => setStatPopup(statPopup === 'late' ? null : 'late')}
            className="text-xs px-3 py-1.5 rounded-lg bg-red-50 text-red-600 border border-red-200 hover:bg-red-50 cursor-pointer transition-colors">
            {lateCount} late
          </button>
        )}
        <button onClick={() => setStatPopup(statPopup === 'not_in' ? null : 'not_in')}
          className="text-xs px-3 py-1.5 rounded-lg bg-white/60 text-slate-900 border border-slate-200 hover:bg-white/80 cursor-pointer transition-colors">
          {notIn.length} not in
        </button>
        {clockedOutCount > 0 && (
          <button onClick={() => setStatPopup(statPopup === 'clocked_out' ? null : 'clocked_out')}
            className="text-xs px-3 py-1.5 rounded-lg bg-white/60 text-slate-900 border border-slate-200 hover:bg-white/80 cursor-pointer transition-colors">
            {clockedOutCount} clocked out
          </button>
        )}
        <button onClick={load} className="ml-auto px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-900 rounded-lg text-xs flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
        <button onClick={() => setShowAddEmployee(!showAddEmployee)} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs">
          + Add Employee
        </button>
        <button onClick={() => setShowJobMat(!showJobMat)} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs">
          + Job Material
        </button>
        <button onClick={() => setShowJobCons(!showJobCons)} className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs">
          + Job Consumable
        </button>
      </div>

      {/* Stat popup */}
      {statPopup && (
        <div className="bg-white/95 border border-slate-200 rounded-xl shadow-lg p-4 animate-in fade-in">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-sm font-bold text-slate-800">
              {statPopup === 'clocked_in' && `Clocked In (${crew.length})`}
              {statPopup === 'on_jobs' && `On Jobs (${activeCount})`}
              {statPopup === 'late' && `Late (${lateCount})`}
              {statPopup === 'not_in' && `Not Clocked In (${notIn.length})`}
              {statPopup === 'clocked_out' && `Clocked Out (${clockedOutCount})`}
            </h4>
            <button onClick={() => setStatPopup(null)} className="text-slate-500 hover:text-slate-600 text-sm">x</button>
          </div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {statPopup === 'clocked_in' && crew.map(c => (
              <div key={c.employee_id} className="flex justify-between text-xs py-1 border-b border-slate-100 last:border-0">
                <span className="font-medium text-slate-800">{c.employee_name}</span>
                <span className="text-slate-500">{c.total_hours_today}h {c.active_job ? `— ${c.active_job.work_order_number}` : ''}</span>
              </div>
            ))}
            {statPopup === 'on_jobs' && crew.filter(c => c.active_job).map(c => (
              <div key={c.employee_id} className="flex justify-between text-xs py-1 border-b border-slate-100 last:border-0">
                <span className="font-medium text-slate-800">{c.employee_name}</span>
                <span className="text-blue-600 font-mono">{c.active_job.work_order_number} <span className="text-slate-500">— {c.active_job.work_order_customer}</span></span>
              </div>
            ))}
            {statPopup === 'late' && crew.filter(c => c.is_late).map(c => (
              <div key={c.employee_id} className="flex justify-between text-xs py-1 border-b border-slate-100 last:border-0">
                <span className="font-medium text-slate-800">{c.employee_name}</span>
                <span className="text-red-600 font-bold">{c.late_minutes}m late</span>
              </div>
            ))}
            {statPopup === 'not_in' && notIn.map(e => (
              <div key={e.employee_id} className="text-xs py-1 border-b border-slate-100 last:border-0">
                <span className="text-slate-700">{e.employee_name}</span>
              </div>
            ))}
            {statPopup === 'clocked_out' && crew.filter(c => c.clock_out_time).map(c => (
              <div key={c.employee_id} className="flex justify-between text-xs py-1 border-b border-slate-100 last:border-0">
                <span className="font-medium text-slate-800">{c.employee_name}</span>
                <span className="text-slate-500">{c.total_hours_today}h</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Forklift Inspections ── */}
      {isToday && (forklifts.length > 0 || unassignedForklifts.length > 0) && (
        <div className="bg-white/90 rounded-xl border border-slate-200 shadow-sm p-4">
          <h4 className="text-xs text-slate-900 font-bold uppercase tracking-wider mb-2 flex items-center gap-1">
            <Truck className="w-3 h-3" /> Today's Forklift Inspections
          </h4>
          <div className="flex flex-wrap gap-2">
            {forklifts.map(fl => (
              <button key={fl.id} onClick={() => setExpandedForklift(expandedForklift === fl.id ? null : fl.id)}
                className={`px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${
                  fl.inspected ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-red-50 border-red-300 text-red-800'
                } ${expandedForklift === fl.id ? 'ring-2 ring-blue-400' : ''}`}>
                {fl.short || fl.id} {fl.inspected ? '✓' : '✗'}
              </button>
            ))}
          </div>
          {expandedForklift && (() => {
            const fl = forklifts.find(f => f.id === expandedForklift);
            if (!fl) return null;
            return (
              <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-slate-900">{fl.name}</p>
                  <span className={`text-xs font-bold ${fl.inspected ? 'text-emerald-600' : 'text-red-600'}`}>
                    {fl.inspected ? 'INSPECTED' : 'NOT INSPECTED'}
                  </span>
                </div>
                {fl.inspection ? (
                  <div className="space-y-1 text-xs">
                    <p className="text-slate-600">Inspector: <span className="text-slate-900 font-medium">{fl.inspection.inspector_name || '—'}</span></p>
                    <p className="text-slate-600">Time: <span className="text-slate-900">{fl.inspection.inspection_time || '—'}</span></p>
                    <p className="text-slate-600">Status: <span className={
                      fl.inspection.overall_status === 'pass' ? 'text-emerald-700 font-bold' :
                      fl.inspection.overall_status === 'fail' ? 'text-red-700 font-bold' :
                      'text-amber-700 font-bold'
                    }>{
                      fl.inspection.overall_status === 'pass' ? '✓ Pass' :
                      fl.inspection.overall_status === 'fail' ? '🚫 Do Not Use' :
                      fl.inspection.overall_status === 'needs_attention' ? '⚠ Usable — Defects Flagged' :
                      (fl.inspection.overall_status || '—')
                    }</span></p>
                    {fl.inspection.deficiencies_noted && (
                      <p className="text-red-600 mt-1 font-medium">Deficiencies: {fl.inspection.deficiencies_noted}</p>
                    )}
                    {fl.inspection.inspection_items && fl.inspection.inspection_items.length > 0 && (
                      <div className="mt-2 max-h-40 overflow-y-auto border-t border-slate-200 pt-2">
                        {fl.inspection.inspection_items.map((item, i) => {
                          const isValue = (item.type === 'text' || item.type === 'date');
                          const st = (item.status || item.result || '');
                          const label = isValue
                            ? (item.notes || item.value || 'Completed')
                            : st === 'pass' ? 'Pass'
                            : st === 'needs_attention' ? 'Minor Defect'
                            : st === 'fail' ? 'Major Defect'
                            : st === 'na' ? 'N/A'
                            : (st || '—');
                          const color = isValue ? 'text-blue-600'
                            : st === 'pass' ? 'text-emerald-600'
                            : st === 'needs_attention' ? 'text-amber-600'
                            : st === 'fail' ? 'text-red-600'
                            : st === 'na' ? 'text-slate-500'
                            : 'text-slate-600';
                          return (
                            <div key={i} className="flex items-center justify-between py-0.5 gap-2">
                              <span className="text-slate-900 text-xs truncate">{item.description || item.item_name || item.name || ('Item ' + (i + 1))}</span>
                              <span className={`text-xs font-medium flex-shrink-0 ${color}`}>{label}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-red-600">No inspection completed — unit should not be operated</p>
                )}
              </div>
            );
          })()}
          {unassignedForklifts.length > 0 && (
            <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-300" data-testid="unassigned-forklifts">
              <p className="text-xs font-bold text-amber-800 mb-2 flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> {unassignedForklifts.length} inspection(s) submitted without a forklift — assign to a unit:
              </p>
              <div className="space-y-2">
                {unassignedForklifts.map(u => (
                  <div key={u.inspection_id} data-testid="unassigned-forklift-row"
                    className="flex items-center justify-between gap-2 bg-white rounded-md border border-amber-200 px-2 py-1.5">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-slate-900 truncate">
                        {u.inspector_name}{u.inspection_time ? ` · ${u.inspection_time}` : ''}
                      </p>
                      <p className="text-[11px] text-slate-500 truncate">{u.equipment_label}</p>
                    </div>
                    <select
                      data-testid="assign-forklift-select"
                      defaultValue=""
                      onChange={(e) => { if (e.target.value) assignForkliftInspection(u.inspection_id, e.target.value); }}
                      className="text-xs border border-amber-300 rounded-md px-2 py-1 bg-white flex-shrink-0">
                      <option value="">Assign to…</option>
                      <option value="F1">F1</option>
                      <option value="F2">F2</option>
                      <option value="F3">F3</option>
                      <option value="F4">F4</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Idle Employees Alert ── */}
      {isToday && idleEmployees.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="idle-alert">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-700">Employees Clocked In — Not On A Job</p>
              <div className="mt-2 space-y-1">
                {idleEmployees.map(e => (
                  <div key={e.employee_id} className="flex items-center gap-3 text-sm">
                    <span className="text-slate-900 font-medium">{e.employee_name}</span>
                    <span className="text-red-600 text-xs">{e.minutes_idle} min idle</span>
                    <span className="text-slate-900 text-xs">clocked in {new Date(e.clock_in_time).toLocaleTimeString('en-US', {hour:'numeric', minute:'2-digit', hour12:true, timeZone:'America/Toronto'})}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── End-of-week wrap-up: daily sheets + consumables per project still to approve ── */}
      <WeekWrapup date={selectedDate} onChanged={() => axios.get(`${API}/foreman/week-approval-status`, { params: { date: selectedDate } }).then(r => setWeekStatus(r.data)).catch(() => {})} />

      {/* ── Week Approval Overview ── */}
      {weekStatus && (
        <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs text-slate-900 font-bold uppercase tracking-wider">Week Overview</p>
            {weekStatus.is_week_approved ? (
              <span className="text-[10px] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">Week Approved</span>
            ) : (
              <span className="text-[10px] text-amber-800 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded font-semibold">Not Approved for Payroll</span>
            )}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {weekStatus.days.map(d => {
              const isSel = d.date === selectedDate;
              const isFuture = d.date > today();
              const hasData = d.employees > 0;
              const allApproved = d.fully_approved;
              return (
                <button key={d.date} disabled={isFuture}
                  onClick={() => { if (!isFuture) { setSelectedDate(d.date); setExpandedEmp(null); } }}
                  className={`rounded-lg p-2 text-center transition-colors border ${
                    isSel ? 'border-blue-500 bg-blue-50' :
                    allApproved ? 'border-emerald-200 bg-emerald-50' :
                    hasData && d.pending > 0 ? 'border-amber-200 bg-amber-50' :
                    isFuture ? 'border-slate-200 bg-white/30/60' :
                    'border-slate-200 hover:bg-slate-100/80'
                  }`}>
                  <p className={`text-xs font-bold ${isSel ? 'text-blue-700' : isFuture ? 'text-slate-900' : 'text-slate-900'}`}>{d.day_name}</p>
                  <p className="text-[10px] text-slate-900">{new Date(d.date + 'T00:00:00').getDate()}</p>
                  {hasData && !isFuture && (
                    <div className="mt-1">
                      <p className="text-[10px] text-slate-900">{d.employees} ppl</p>
                      {allApproved ? (
                        <CheckCircle className="w-3 h-3 text-emerald-700 mx-auto mt-0.5" />
                      ) : d.pending > 0 ? (
                        <p className="text-[10px] text-amber-700">{d.pending} pending</p>
                      ) : null}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          {/* Unapproved-entries reminder for the week being viewed */}
          {!weekStatus.is_week_approved && weekStatus.days.some(d => d.pending > 0) && (
            <div className="mt-3 bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-center gap-2" data-testid="week-unapproved-warning">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <p className="text-xs text-amber-800 font-medium">This week has unapproved entries — please review and approve for payroll</p>
            </div>
          )}
        </div>
      )}

      {/* Add Employee Form */}
      {showAddEmployee && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-3">
          <h4 className="text-sm font-semibold text-slate-900">Add Work Period for Employee</h4>
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[160px]">
              <label className="text-[10px] text-slate-900">Employee</label>
              <select value={selectedEmpToAdd?.id || ''} onChange={e => setSelectedEmpToAdd(allEmployees.find(emp => emp.id === e.target.value))}
                className="w-full px-2 py-1.5 bg-white/80 border border-slate-200 rounded text-slate-900 text-sm outline-none">
                <option value="">Select employee...</option>
                {allEmployees.filter(e => !crew.some(c => c.employee_id === e.id)).sort((a,b) => (a.full_name||'').localeCompare(b.full_name||'')).map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.full_name} ({emp.shop_number})</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[160px] relative">
              <label className="text-[10px] text-slate-900">Work Order</label>
              <input type="text" value={addWO} onChange={e => { setAddWO(e.target.value); if (e.target.value.length >= 2) axios.get(`${API}/work-orders`, { params: { q: e.target.value } }).then(r => setAddWOResults(r.data.slice(0, 5))).catch(() => {}); else setAddWOResults([]); }}
                placeholder="Search WO#..." className="w-full px-2 py-1.5 bg-white/80 border border-slate-200 rounded text-slate-900 text-sm placeholder-slate-400 outline-none" />
              {addWOResults.length > 0 && !addSelectedWO && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-slate-300 rounded-lg shadow-lg max-h-40 overflow-y-auto divide-y divide-slate-100" data-testid="add-employee-wo-results">
                  {addWOResults.map(wo => (
                    <button key={wo.project_number} onMouseDown={e => { e.preventDefault(); setAddSelectedWO(wo); setAddWO(wo.project_number); setAddWOResults([]); }}
                      className="w-full text-left px-2.5 py-1.5 hover:bg-blue-50 text-xs">
                      <span className="font-mono font-bold text-blue-700">{wo.project_number}</span> <span className="text-slate-900 font-medium">{wo.client_name || 'No customer'}</span>
                      {(wo.title || wo.description) && <p className="text-[11px] text-slate-600 truncate">{wo.title || wo.description}</p>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="text-[10px] text-slate-900">Start</label>
              <input type="time" value={addStart} onChange={e => setAddStart(e.target.value)}
                className="block px-2 py-1.5 bg-white/80 border border-slate-200 rounded text-slate-900 text-sm outline-none" />
            </div>
            <div>
              <label className="text-[10px] text-slate-900">End</label>
              <input type="time" value={addEnd} onChange={e => setAddEnd(e.target.value)}
                className="block px-2 py-1.5 bg-white/80 border border-slate-200 rounded text-slate-900 text-sm outline-none" />
            </div>
            <button onClick={() => selectedEmpToAdd && addEmployeeSession(selectedEmpToAdd.id)} disabled={!selectedEmpToAdd || !addSelectedWO || !addStart}
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded text-xs font-medium">Add</button>
            <button onClick={() => setShowAddEmployee(false)} className="text-xs text-slate-900">Cancel</button>
          </div>
        </div>
      )}

      {/* Job Material Form */}
      {showJobMat && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-900">Add Stock Material to Job</h4>
            <button onClick={() => setShowJobMat(false)} className="text-xs text-slate-900">Close</button>
          </div>

          {/* WO Search */}
          <div className="relative">
            <label className="text-[10px] text-slate-900">Work Order *</label>
            <input type="text" value={jmWO} onChange={async e => {
              setJmWO(e.target.value);
              if (e.target.value.length > 2) {
                const r = await axios.get(`${API}/work-orders`, { params: { q: e.target.value } }).catch(() => ({ data: [] }));
                setJmWOResults(r.data.slice?.(0, 6) || []);
              } else { setJmWOResults([]); }
            }} placeholder="Search WO#..."
              className="w-full px-3 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-sm outline-none" />
            {jmWOResults.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-slate-300 rounded-lg max-h-40 shadow-lg overflow-y-auto">
                {jmWOResults.map(wo => (
                  <button key={wo.project_number} onClick={() => { setJmSelectedWO(wo); setJmWO(wo.project_number); setJmWOResults([]); }}
                    className="w-full text-left px-3 py-2 hover:bg-white/60 text-xs text-slate-900 border-b border-slate-200">
                    <span className="font-mono text-blue-700">{wo.project_number}</span> — {wo.client_name}
                  </button>
                ))}
              </div>
            )}
            {jmSelectedWO && <p className="text-[10px] text-blue-700 mt-1">{jmSelectedWO.project_number} — {jmSelectedWO.client_name}</p>}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {/* Material Type */}
            <div>
              <label className="text-[10px] text-slate-900">Material</label>
              <select value={jmMatType} onChange={e => setJmMatType(e.target.value)}
                className="w-full px-2 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-xs outline-none">
                <option>Mild Steel</option><option>Stainless Steel</option><option>Self-Weathering</option><option>AR-400 Plate</option>
              </select>
            </div>
            {/* Mode */}
            <div>
              <label className="text-[10px] text-slate-900">Type</label>
              <select value={jmMode} onChange={e => { setJmMode(e.target.value); setJmMember(''); }}
                className="w-full px-2 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-xs outline-none">
                <option value="plate">Plate / Sheet (dimensions)</option>
                <option value="linear">Structural (linear ft)</option>
              </select>
            </div>
          </div>

          {jmMode === 'plate' ? (
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
              <div>
                <label className="text-[10px] text-slate-900">Thickness</label>
                <select value={jmThickness} onChange={e => setJmThickness(e.target.value)}
                  className="w-full px-2 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-xs outline-none">
                  <option value="">Select...</option>
                  {['3/16','1/4','5/16','3/8','7/16','1/2','5/8','3/4','7/8','1','1-1/4','1-1/2','2'].map(t => (
                    <option key={t} value={t}>{t}"</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-slate-900">Width (inches)</label>
                <input type="text" value={jmWidth} onFocus={e => e.target.select()} onChange={e => setJmWidth(e.target.value)} placeholder='3-1/4"'
                  className="w-full px-2 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-xs outline-none" />
              </div>
              <div>
                <label className="text-[10px] text-slate-900">Length (inches)</label>
                <input type="text" value={jmLength} onFocus={e => e.target.select()} onChange={e => setJmLength(e.target.value)} placeholder='32"'
                  className="w-full px-2 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-xs outline-none" />
              </div>
              <div>
                <label className="text-[10px] text-slate-900">Qty (pcs)</label>
                <input type="number" min="1" value={jmPcs} onFocus={e => e.target.select()} onChange={e => setJmPcs(e.target.value)}
                  className="w-full px-2 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-xs text-center outline-none" />
              </div>
              <div className="text-center">
                {jmWidth && jmLength && (() => {
                  const parseFrac = (s) => { const p = s.replace(/[" ]/g,'').replace('-',' ').split(' '); let t=0; p.forEach(x => { if(x.includes('/')) { const [n,d]=x.split('/'); t+=parseFloat(n)/parseFloat(d); } else if(x) t+=parseFloat(x); }); return t; };
                  const w = parseFrac(jmWidth), l = parseFrac(jmLength), pcs = parseInt(jmPcs) || 1;
                  const sqft = (w * l / 144 * pcs).toFixed(2);
                  const thk = jmThickness ? parseFrac(jmThickness) : 0;
                  const weight = thk > 0 ? Math.round(thk * w * l * pcs * 0.2836) : 0;
                  return (
                    <span className="block mt-4">
                      <span className="text-emerald-700 text-xs font-bold block">{sqft} sqft</span>
                      {weight > 0 && (
                        <span className="text-slate-700 text-[11px] font-semibold block" data-testid="jm-weight-est">≈ {weight} lbs (est.)</span>
                      )}
                    </span>
                  );
                })()}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
              <div>
                <label className="text-[10px] text-slate-900">Category</label>
                <select value={jmCategory} onChange={e => {
                  setJmCategory(e.target.value); setJmMember('');
                  if (e.target.value) axios.get(`${API}/steel-members`, { params: { category: e.target.value } }).then(r => setJmSteelMembers(r.data)).catch(() => {});
                }} className="w-full px-2 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-xs outline-none">
                  <option value="">Select...</option>
                  {jmSteelCats.filter(c => c !== 'Plate').map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-slate-900">Size</label>
                <select value={jmMember} onChange={e => setJmMember(e.target.value)}
                  className="w-full px-2 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-xs outline-none">
                  <option value="">Select...</option>
                  {jmSteelMembers.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-slate-900">Length (ft)</label>
                <input type="number" step="0.1" value={jmLinearFt} onFocus={e => e.target.select()} onChange={e => setJmLinearFt(e.target.value)}
                  className="w-full px-2 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-xs text-center outline-none" />
              </div>
              <div>
                <label className="text-[10px] text-slate-900">Qty (pcs)</label>
                <input type="number" min="1" value={jmPcs} onFocus={e => e.target.select()} onChange={e => setJmPcs(e.target.value)}
                  className="w-full px-2 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-xs text-center outline-none" />
              </div>
            </div>
          )}

          <button onClick={async () => {
            if (!jmSelectedWO) { toast.error('Select a work order'); return; }
            const parseFrac = (s) => { if(!s) return 0; const p = s.replace(/[" ]/g,'').replace('-',' ').split(' '); let t=0; p.forEach(x => { if(x.includes('/')) { const [n,d]=x.split('/'); t+=parseFloat(n)/parseFloat(d); } else if(x) t+=parseFloat(x); }); return t; };
            try {
              const payload = {
                work_order_number: jmSelectedWO.project_number,
                work_order_customer: jmSelectedWO.client_name || '',
                material_type: jmMatType,
                date: selectedDate,
                quantity: parseInt(jmPcs) || 1,
              };
              if (jmMode === 'plate') {
                if (!jmThickness || !jmWidth || !jmLength) { toast.error('Enter thickness, width, and length'); return; }
                payload.thickness = jmThickness;
                payload.width_inches = parseFrac(jmWidth);
                payload.length_inches = parseFrac(jmLength);
              } else {
                if (!jmMember || !jmLinearFt) { toast.error('Select size and enter footage'); return; }
                payload.member_name = jmMember;
                payload.linear_ft = parseFloat(jmLinearFt) || 0;
                payload.unit = 'ft';
              }
              const r = await axios.post(`${API}/job-material`, payload);
              toast.success(`Material added — ${r.data.quantity} ${r.data.unit}`);
              setShowJobMat(false);
              setJmWidth(''); setJmLength(''); setJmPcs('1'); setJmLinearFt(''); setJmMember('');
              load();
            } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
          }} className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">
            Add Material to Job
          </button>
        </div>
      )}

      {/* Job Consumable Form */}
      {showJobCons && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-900">Add Consumables to Job</h4>
            <button onClick={() => setShowJobCons(false)} className="text-xs text-slate-900">Close</button>
          </div>

          {/* WO Search */}
          <div className="relative">
            <label className="text-[10px] text-slate-900">Work Order *</label>
            <input type="text" value={jcWO} onChange={async e => {
              setJcWO(e.target.value);
              if (e.target.value.length > 2) {
                const r = await axios.get(`${API}/work-orders`, { params: { q: e.target.value } }).catch(() => ({ data: [] }));
                setJcWOResults(r.data?.slice?.(0, 6) || []);
              } else setJcWOResults([]);
            }} placeholder="Search WO#..."
              className="w-full px-3 py-2 bg-white/80 border border-slate-200 rounded-lg text-slate-900 text-sm outline-none" />
            {jcWOResults.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-slate-300 rounded-lg max-h-40 overflow-y-auto shadow-lg">
                {jcWOResults.map(wo => (
                  <button key={wo.project_number} onClick={() => { setJcSelectedWO(wo); setJcWO(wo.project_number); setJcWOResults([]); }}
                    className="w-full text-left px-3 py-2 hover:bg-white/60 text-xs text-slate-900 border-b border-slate-100">
                    <span className="font-mono text-blue-600">{wo.project_number}</span> — {wo.client_name}
                  </button>
                ))}
              </div>
            )}
            {jcSelectedWO && <p className="text-[10px] text-blue-700 mt-1">{jcSelectedWO.project_number} — {jcSelectedWO.client_name}</p>}
          </div>

          {/* Quick-pick consumables */}
          <div className="max-h-[300px] overflow-y-auto space-y-1">
            {(() => {
              const grouped = {};
              catalog.forEach(c => { const cat = c.category || 'Other'; if (!grouped[cat]) grouped[cat] = []; grouped[cat].push(c); });
              return Object.keys(grouped).sort().map(cat => (
                <details key={cat} className="rounded-lg border border-slate-200 overflow-hidden">
                  <summary className="px-3 py-2 bg-slate-100 text-xs font-semibold text-slate-900 cursor-pointer hover:bg-slate-200">
                    {cat} ({grouped[cat].length})
                    {Object.keys(jcItems).filter(k => grouped[cat].some(i => i.name === k)).length > 0 && (
                      <span className="ml-2 text-amber-600">{Object.keys(jcItems).filter(k => grouped[cat].some(i => i.name === k)).length} selected</span>
                    )}
                  </summary>
                  <div className="p-2 grid grid-cols-2 sm:grid-cols-3 gap-1">
                    {grouped[cat].map(item => {
                      const qty = jcItems[item.name] || 0;
                      return (
                        <div key={item.name} className={`flex items-center justify-between px-2 py-1.5 rounded border text-xs ${qty > 0 ? 'border-amber-400 bg-amber-50' : 'border-slate-200 bg-white'}`}>
                          <span className="text-slate-900 truncate flex-1 mr-1">{item.name}</span>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {qty > 0 && <button onClick={() => setJcItems(prev => { const c = {...prev}; c[item.name] = Math.max(0, (c[item.name]||0) - 1); if (c[item.name] <= 0) delete c[item.name]; return c; })}
                              className="w-5 h-5 bg-red-500 text-white rounded text-xs font-bold flex items-center justify-center">-</button>}
                            {qty > 0 && <span className="text-amber-700 font-bold w-4 text-center">{qty}</span>}
                            <button onClick={() => setJcItems(prev => ({...prev, [item.name]: (prev[item.name] || 0) + 1}))}
                              className="w-5 h-5 bg-emerald-600 text-white rounded text-xs font-bold flex items-center justify-center">+</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </details>
              ));
            })()}
          </div>

          {/* Selected summary + submit */}
          {Object.keys(jcItems).length > 0 && (
            <div className="bg-white/80 border border-amber-300 rounded-lg p-3">
              <p className="text-xs font-semibold text-slate-900 mb-2">{Object.keys(jcItems).length} items selected:</p>
              <div className="flex flex-wrap gap-1 mb-3">
                {Object.entries(jcItems).map(([name, qty]) => (
                  <span key={name} className="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded">{name} x{qty}</span>
                ))}
              </div>
              <button onClick={async () => {
                if (!jcSelectedWO) { toast.error('Select a work order'); return; }
                let added = 0;
                for (const [name, qty] of Object.entries(jcItems)) {
                  try {
                    await axios.post(`${API}/consumable-entries`, {
                      work_order_number: jcSelectedWO.project_number,
                      work_order_customer: jcSelectedWO.client_name || '',
                      date: selectedDate,
                      item_name: name,
                      quantity: qty,
                      unit: 'each',
                      unit_cost: 0,
                      notes: 'Job consumable — added by foreman',
                    });
                    added++;
                  } catch {}
                }
                toast.success(`Added ${added} consumables to ${jcSelectedWO.project_number}`);
                setJcItems({}); setShowJobCons(false);
                load();
              }} className="w-full py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-semibold">
                Add {Object.keys(jcItems).length} Consumables to Job
              </button>
            </div>
          )}
        </div>
      )}

      {/* OT Approval Prompt */}
      {/* Employee cards */}
      {crew.map(c => {
        const isExpanded = expandedEmp === c.employee_id;
        const isActive = c.active_job && !c.clock_out_time;
        const isDone = !!c.clock_out_time;
        const isApproved = approvedEmps.has(c.employee_id) || c.is_approved;

        return (
          <div key={c.employee_id} className={`rounded-xl border overflow-hidden transition-colors ${
            isApproved ? 'bg-white/90 border-emerald-400' : isActive ? 'bg-white/85 border-emerald-300' : isDone ? 'bg-white/90 border-slate-300' : 'bg-white/85 border-amber-300'
          }`}>
            {/* Header bar — always visible */}
            <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-white/60 transition-colors"
              onClick={() => expandEmployee(c.employee_id)}>
              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                isActive ? 'bg-emerald-400 animate-pulse' : isDone ? 'bg-slate-400' : 'bg-amber-400'
              }`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-slate-900 font-semibold text-sm">{c.employee_name}</span>
                  {c.is_late && (
                    <span className="text-[10px] text-red-600 bg-red-50 px-1.5 py-0.5 rounded flex items-center gap-1">
                      {c.late_minutes}m late
                      <button onClick={(e) => { e.stopPropagation(); removeLate(c.employee_id, c.clock_event_id); }}
                        className="text-red-700 hover:text-slate-900 underline ml-0.5" title="Remove late flag">fix</button>
                    </span>
                  )}
                  {isDone && <span className="text-[10px] text-slate-700 bg-slate-200 px-1.5 py-0.5 rounded font-medium">Clocked out</span>}
                </div>
                {c.active_job ? (
                  <p className="text-xs text-slate-900 mt-0.5">
                    Working on: <span className="font-mono text-blue-700">{c.active_job.work_order_number}</span> — {c.active_job.work_order_customer}
                  </p>
                ) : isDone ? (
                  <p className="text-xs text-slate-900 mt-0.5">Done for the day</p>
                ) : (
                  <p className="text-xs text-amber-700 mt-0.5">Clocked in — no active job</p>
                )}
                {/* WO breakdown — visible without expanding */}
                {c.wo_summary && c.wo_summary.length > 0 && (
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                    {c.wo_summary.map((wo, i) => (
                      <span key={i} className="text-[10px] text-slate-900">
                        <span className="font-mono text-blue-600">{wo.wo}</span>
                        <span className="text-slate-900 font-semibold ml-1">{wo.hours}h</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-slate-900">{c.total_hours_today.toFixed(1)}h</span>
                  {c.lunch_deducted && <span className="text-[9px] text-amber-700 ml-0.5" title="Lunch deducted">-L</span>}
                  {c.total_hours_today > 8 && (
                    <span className="text-[10px] text-amber-700 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded font-bold">
                      +{(c.total_hours_today - 8).toFixed(1)} OT
                    </span>
                  )}
                  <span className="text-xs text-slate-900">{c.job_count} jobs</span>
                </div>
                <div className="flex items-center gap-1.5">
                  {!isDone && (
                    <button onClick={(e) => { e.stopPropagation(); clockOutEmployee(c.employee_id); }}
                      data-testid={`clock-out-${c.employee_id}`}
                      className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-[10px] font-medium">
                      Clock Out
                    </button>
                  )}
                  {isApproved && (
                    <span className="text-emerald-700 text-[10px] font-medium flex items-center gap-0.5"
                      data-testid={`approved-badge-${c.employee_id}`}
                      title={c.approved_by ? `Approved by ${c.approved_by}` : 'Approved'}>
                      <CheckCircle className="w-3 h-3" /> Approved
                    </span>
                  )}
                  {isApproved && c.approval_stale && (
                    <button onClick={(e) => { e.stopPropagation(); approveEmployee(c.employee_id, c.approval_ot_mode || '8_ot', true); }}
                      data-testid={`approval-stale-${c.employee_id}`}
                      title={`The report has ${c.approved_hours}h booked but the dailies add up to ${c.session_hours}h — jobs changed after approval. Click to re-sync the report to the dailies.`}
                      className="px-2 py-1 bg-amber-500 hover:bg-amber-600 text-white rounded text-[10px] font-bold flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Report {c.approved_hours}h ≠ {c.session_hours}h · Re-sync
                    </button>
                  )}
                  {!isApproved ? (
                    <>
                      <button onClick={(e) => { e.stopPropagation(); approveEmployee(c.employee_id, '8_ot', false); }}
                        data-testid={`approve-8ot-${c.employee_id}`}
                        title="First 8h regular, anything over 8h as overtime"
                        className="px-2 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-[10px] font-medium flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" /> 8 + OT
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); approveEmployee(c.employee_id, 'all_reg', false); }}
                        data-testid={`approve-allreg-${c.employee_id}`}
                        title="All hours as regular/straight time (4×10 schedule, weld practice, negotiated hours)"
                        className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-[10px] font-medium">
                        All Reg
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); approveEmployee(c.employee_id, 'all_ot', false); }}
                        data-testid={`approve-allot-${c.employee_id}`}
                        title="Whole shift as overtime (weekend shifts)"
                        className="px-2 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded text-[10px] font-medium">
                        All OT
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={(e) => { e.stopPropagation(); approveEmployee(c.employee_id, '8_ot', true); }}
                        data-testid={`reapprove-8ot-${c.employee_id}`}
                        title="Re-approve as 8h + overtime"
                        className="px-2 py-1 text-emerald-700 hover:bg-emerald-100 rounded text-[10px] font-medium">
                        8 + OT
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); approveEmployee(c.employee_id, 'all_reg', true); }}
                        data-testid={`reapprove-allreg-${c.employee_id}`}
                        title="Re-approve as all regular / straight time"
                        className="px-2 py-1 text-blue-700 hover:bg-blue-100 rounded text-[10px] font-medium">
                        All Reg
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); approveEmployee(c.employee_id, 'all_ot', true); }}
                        data-testid={`reapprove-allot-${c.employee_id}`}
                        title="Re-approve the whole shift as overtime (weekend shifts)"
                        className="px-2 py-1 text-amber-700 hover:bg-amber-100 rounded text-[10px] font-medium">
                        All OT
                      </button>
                    </>
                  )}
                  <ChevronDown className={`w-4 h-4 text-slate-900 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
              </div>
            </div>

            {/* Expanded detail */}
            {isExpanded && (
              <div className="border-t border-slate-200 px-4 py-4 space-y-4">
                {loadingDetail ? (
                  <p className="text-sm text-slate-900">Loading...</p>
                ) : (
                  <>
                    {/* Per-project overtime — pick the job(s) that pushed them into OT */}
                    {c.wo_summary && c.wo_summary.length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid={`ot-select-${c.employee_id}`}>
                        <p className="text-[11px] font-bold text-amber-800 mb-2 uppercase tracking-wide">
                          Overtime by project — tap the job(s) that are OT, everything else stays regular
                        </p>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {c.wo_summary.map((wo) => {
                            const sel = (otSelWos[c.employee_id] || []).includes(wo.wo);
                            return (
                              <button key={wo.wo}
                                onClick={(e) => { e.stopPropagation(); toggleOtWo(c.employee_id, wo.wo); }}
                                data-testid={`ot-wo-${c.employee_id}-${wo.wo}`}
                                className={`px-2.5 py-1 rounded-md text-[11px] font-mono font-bold border transition-colors ${sel
                                  ? 'bg-amber-500 text-white border-amber-600'
                                  : 'bg-white text-slate-700 border-slate-300 hover:border-amber-400'}`}>
                                {wo.wo} · {wo.hours}h{sel ? ' = OT' : ''}
                              </button>
                            );
                          })}
                        </div>
                        <button
                          disabled={!(otSelWos[c.employee_id] || []).length}
                          onClick={(e) => { e.stopPropagation(); approveEmployee(c.employee_id, 'select', isApproved, otSelWos[c.employee_id]); }}
                          data-testid={`approve-select-ot-${c.employee_id}`}
                          className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-md text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed">
                          {isApproved ? 'Re-approve' : 'Approve'} — OT on {(otSelWos[c.employee_id] || []).length || 0} job{(otSelWos[c.employee_id] || []).length === 1 ? '' : 's'}
                        </button>
                      </div>
                    )}                    {/* Clock info */}
                    <div className="flex gap-4 text-xs text-slate-900 flex-wrap">
                      <span>Clock in: <span className="text-slate-900">{fmtTime(c.clock_in_time)}</span></span>
                      <span>Clock out: <span className="text-slate-900">{c.clock_out_time ? fmtTime(c.clock_out_time) : '—'}</span></span>
                      {c.shift && c.shift !== 'days' && <span>Shift: <span className="text-slate-900 capitalize">{c.shift}</span></span>}
                      {c.raw_hours != null && c.lunch_deducted ? (
                        <>
                          <span>Raw: <span className="text-slate-900">{c.raw_hours?.toFixed(1)}h</span></span>
                          <span className="text-amber-700">Lunch: -0.5h</span>
                          <span>Final: <span className="text-emerald-700 font-bold">{c.total_hours_today?.toFixed(1)}h</span></span>
                        </>
                      ) : (
                        <span>Total: <span className="text-slate-900 font-bold">{c.total_hours_today?.toFixed(1)}h</span>{!c.took_lunch && <span className="text-slate-900 ml-1">(no lunch)</span>}</span>
                      )}
                    </div>

                    {/* Remove the whole day's entry (accidental / no-show rows). Always
                        available in Foreman Review — works even when approved or when there's
                        no removable clock event. */}
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); dismissEmployeeDay(c.employee_id, c.employee_name, isApproved); }}
                        className="text-xs px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg border border-red-200 flex items-center gap-1.5 transition-colors"
                        data-testid={`dismiss-entry-${c.employee_id}`}
                      >
                        <Trash2 className="w-3 h-3" /> Remove Entry
                      </button>
                      {c.source !== 'field' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setMovingFor(movingFor === c.employee_id ? null : c.employee_id); setMoveWO(''); setMoveSelectedWO(null); setMoveWOResults([]); }}
                          className="text-xs px-3 py-1.5 bg-blue-50 hover:bg-blue-50 text-blue-700 rounded-lg border border-blue-200 flex items-center gap-1.5 transition-colors"
                          title="He actually worked in the field today — move this whole day to Field Tracker"
                          data-testid={`move-to-field-${c.employee_id}`}
                        >
                          → Move to Field Tracker
                        </button>
                      )}
                    </div>
                    {movingFor === c.employee_id && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2" data-testid={`move-to-field-form-${c.employee_id}`} onClick={(e) => e.stopPropagation()}>
                        <p className="text-[11px] text-blue-900 font-semibold">Which field job was {c.employee_name.split(' ')[0]} on? The shop clock-in {fmtTime(c.clock_in_time)}{c.clock_out_time ? ` → ${fmtTime(c.clock_out_time)}` : ' (still clocked in)'} becomes a Field Tracker entry.</p>
                        <div className="flex items-end gap-2 flex-wrap">
                          <div className="flex-1 min-w-[180px] relative">
                            <label className="text-[10px] text-slate-700">Field Work Order</label>
                            <input type="text" value={moveWO} onChange={e => searchMoveWO(e.target.value)} placeholder="Search WO#…"
                              data-testid={`move-to-field-wo-${c.employee_id}`}
                              className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-slate-900 text-sm placeholder-slate-400 outline-none" />
                            {moveWOResults.length > 0 && (
                              <div className="absolute z-10 mt-1 w-full bg-white border border-slate-300 rounded shadow max-h-40 overflow-y-auto">
                                {moveWOResults.map(wo => (
                                  <button key={wo.project_number} onClick={() => { setMoveSelectedWO(wo); setMoveWO(wo.project_number); setMoveWOResults([]); }}
                                    data-testid={`move-to-field-wo-opt-${wo.project_number}`}
                                    className="w-full text-left px-2 py-1.5 hover:bg-blue-50 text-xs">
                                    <span className="font-mono text-blue-700 font-bold">{wo.project_number}</span> <span className="text-slate-700">{wo.client_name}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          <label className="flex items-center gap-1.5 text-[11px] text-slate-700 pb-1.5">
                            <input type="checkbox" checked={moveServiceCall} onChange={e => setMoveServiceCall(e.target.checked)} data-testid={`move-to-field-service-${c.employee_id}`} /> Service call (4h min)
                          </label>
                          <button onClick={() => moveToField(c, isApproved)} disabled={moving || !(moveSelectedWO || moveWO.trim())}
                            data-testid={`move-to-field-confirm-${c.employee_id}`}
                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded text-xs font-bold">
                            {moving ? 'Moving…' : 'Move day to Field'}
                          </button>
                          <button onClick={() => setMovingFor(null)} className="px-2 py-1.5 text-slate-600 hover:text-slate-900 text-xs">Cancel</button>
                        </div>
                      </div>
                    )}

                    {/* Job Sessions */}
                    <div>
                      {(c.day_notes || []).length > 0 && (
                        <div className="mb-2 p-2 rounded-lg bg-blue-50 border border-blue-100" data-testid={`day-notes-${c.employee_id}`}>
                          <p className="text-[10px] font-bold uppercase tracking-wider text-blue-700 mb-1">Notes from {c.employee_name.split(' ')[0]}</p>
                          {c.day_notes.map(n => (
                            <p key={n.id} className="text-xs text-slate-700"><span className="text-slate-500">{fmtTime(n.time)} —</span> {n.text}</p>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs text-slate-900 font-bold uppercase tracking-wider flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Job Sessions
                        </h4>
                        <div className="flex items-center gap-2">
                          {c.clock_out_time && c.raw_hours > 0 && empSessions.length > 0 && (() => {
                            const sessionHrs = empSessions.filter(s => s.end_time).reduce((sum, s) => sum + (s.duration_hours || 0), 0);
                            const gap = (c.raw_hours || 0) - sessionHrs;
                            return gap >= 0.5 ? (
                              <button
                                data-testid={`fill-tool-crib-${c.employee_id}`}
                                onClick={(e) => { e.stopPropagation(); fillToolCribGap(c.employee_id); }}
                                className="text-xs px-2 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded flex items-center gap-1"
                              >
                                + {gap.toFixed(1)}h → Tool Crib
                              </button>
                            ) : null;
                          })()}
                          <button onClick={() => setAddingFor(addingFor === c.employee_id ? null : c.employee_id)}
                            className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded flex items-center gap-1">
                            + Add Work Period
                          </button>
                        </div>
                      </div>

                      {/* Add form */}
                      {addingFor === c.employee_id && (
                        <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-end gap-2 flex-wrap">
                            <div className="flex-1 min-w-[160px] relative">
                              <label className="text-[10px] text-slate-900">Work Order</label>
                              <input type="text" value={addWO} onChange={e => searchWO(e.target.value)} placeholder="Search WO#..."
                                className="w-full px-2 py-1.5 bg-white/80 border border-slate-200 rounded text-slate-900 text-sm placeholder-slate-400 outline-none" />
                              {addWOResults.length > 0 && (
                                <div className="absolute z-10 mt-1 w-full bg-white border border-slate-300 rounded-lg shadow-lg max-h-40 overflow-y-auto divide-y divide-slate-100" data-testid="review-add-wo-results">
                                  {addWOResults.map(wo => (
                                    <button key={wo.project_number} onClick={() => { setAddSelectedWO(wo); setAddWO(wo.project_number); setAddWOResults([]); }}
                                      className="w-full text-left px-2.5 py-1.5 hover:bg-blue-50 text-xs" data-testid={`review-add-wo-${wo.project_number}`}>
                                      <span className="font-mono font-bold text-blue-700">{wo.project_number}</span> <span className="text-slate-900 font-medium">{wo.client_name || 'No customer'}</span>
                                      {(wo.title || wo.description) && <p className="text-[11px] text-slate-600 truncate">{wo.title || wo.description}</p>}
                                    </button>
                                  ))}
                                </div>
                              )}
                              {addSelectedWO && <p className="text-[10px] text-emerald-700 mt-0.5">{addSelectedWO.project_number} — {addSelectedWO.client_name}</p>}
                            </div>
                            <div>
                              <label className="text-[10px] text-slate-900">Start</label>
                              <input type="time" value={addStart} onChange={e => setAddStart(e.target.value)}
                                className="block px-2 py-1.5 bg-white/80 border border-slate-200 rounded text-slate-900 text-sm outline-none" />
                            </div>
                            <div>
                              <label className="text-[10px] text-slate-900">End</label>
                              <input type="time" value={addEnd} onChange={e => setAddEnd(e.target.value)}
                                className="block px-2 py-1.5 bg-white/80 border border-slate-200 rounded text-slate-900 text-sm outline-none" />
                            </div>
                            <button onClick={() => addSession(c.employee_id)}
                              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium">Add</button>
                            <button onClick={() => setAddingFor(null)} className="text-xs text-slate-900">Cancel</button>
                          </div>
                        </div>
                      )}

                      {empSessions.length === 0 ? (
                        <div className="space-y-2">
                          <p className="text-sm text-slate-900">No job sessions</p>
                          {c.clock_out_time && (
                            <button
                              data-testid={`fill-all-tool-crib-${c.employee_id}`}
                              onClick={(e) => { e.stopPropagation(); fillToolCribGap(c.employee_id); }}
                              className="text-xs px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded flex items-center gap-1"
                            >
                              Assign all hours to Tool Crib
                            </button>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {empSessions.map(s => (
                            <div key={s.id} className={`p-2.5 rounded-lg border ${!s.end_time ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white/30/60'}`}>
                              {editingId === s.id ? (
                                <div className="flex items-center gap-2 flex-wrap">
                                  <div className="relative">
                                    <input type="text" value={editWO} 
                                      onChange={async e => {
                                        setEditWO(e.target.value);
                                        if (e.target.value.length > 2) {
                                          const r = await axios.get(`${API}/work-orders`, { params: { q: e.target.value } }).catch(() => ({ data: [] }));
                                          setEditWOResults(r.data?.slice?.(0, 5) || []);
                                        } else { setEditWOResults([]); }
                                      }}
                                      className="px-2 py-1 bg-white border border-blue-500 rounded text-blue-700 text-sm font-mono outline-none w-36" />
                                    {editWOResults.length > 0 && (
                                      <div className="absolute z-20 mt-1 w-64 bg-white border border-slate-300 rounded-lg shadow-lg max-h-40 overflow-y-auto divide-y divide-slate-100" data-testid="review-edit-wo-results">
                                        {editWOResults.map(wo => (
                                          <button key={wo.project_number} onClick={() => { setEditWOSelected(wo); setEditWO(wo.project_number); setEditWOResults([]); }}
                                            className="w-full text-left px-2.5 py-1.5 hover:bg-blue-50 text-xs text-slate-900">
                                            <span className="font-mono font-bold text-blue-700">{wo.project_number}</span> <span className="font-medium">{wo.client_name || 'No customer'}</span>
                                            {(wo.title || wo.description) && <p className="text-[11px] text-slate-600 truncate">{wo.title || wo.description}</p>}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <input type="time" value={editStart} onChange={e => setEditStart(e.target.value)}
                                    className="px-2 py-1 bg-white border border-blue-500 rounded text-slate-900 text-sm outline-none w-24" />
                                  <span className="text-slate-900 text-xs">to</span>
                                  <input type="time" value={editEnd} onChange={e => setEditEnd(e.target.value)}
                                    className="px-2 py-1 bg-white border border-blue-500 rounded text-slate-900 text-sm outline-none w-24" />
                                  <button onClick={() => saveEdit(s.id, s.start_time)} className="px-2 py-1 bg-blue-600 text-white rounded text-xs">Save</button>
                                  <button onClick={() => { setEditingId(null); setEditWO(''); setEditWOSelected(null); }} className="text-xs text-slate-900">Cancel</button>
                                </div>
                              ) : (
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-mono text-blue-700 text-sm font-bold">{s.work_order_number}</span>
                                    <span className="text-slate-900 text-xs">{s.work_order_customer}</span>
                                    <span className="text-slate-900 text-xs">
                                      {fmtTime(s.start_time)} — {s.end_time ? fmtTime(s.end_time) : <span className="text-emerald-700 font-medium">Active</span>}
                                    </span>
                                    {s.is_manual_edit && <span className="text-[10px] text-amber-700 bg-amber-50 px-1 rounded">edited</span>}
                                    {s.added_by_foreman && <span className="text-[10px] text-blue-700 bg-blue-50 px-1 rounded">foreman added</span>}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-bold text-slate-900">
                                      {s.end_time ? `${s.duration_hours || 0}h` : `${(s.live_duration_hours || 0).toFixed(1)}h`}
                                    </span>
                                    <button onClick={() => { setEditingId(s.id); setEditStart(fmtTimeInput(s.start_time)); setEditEnd(fmtTimeInput(s.end_time)); setEditWO(s.work_order_number); setEditWOSelected(null); }}
                                      className="text-slate-900 hover:text-blue-700"><Edit2 className="w-3.5 h-3.5" /></button>
                                    {s.end_time && c.source !== 'field' && (
                                      <button onClick={() => openMoveSession(s)} data-testid={`move-session-to-field-${s.id}`}
                                        title="This session was actually field work — move just this session to Field Tracker"
                                        className={`hover:text-blue-600 ${movingSession === s.id ? 'text-blue-600' : 'text-slate-900'}`}><Truck className="w-3.5 h-3.5" /></button>
                                    )}
                                    <button onClick={() => deleteSession(s.id)}
                                      className="text-slate-900 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                                  </div>
                                </div>
                              )}
                              {movingSession === s.id && (
                                <div className="mt-2 p-2.5 bg-blue-50 border border-blue-200 rounded-lg space-y-1.5" data-testid={`move-session-form-${s.id}`}>
                                  <p className="text-[11px] text-blue-900 font-semibold">Move only this session ({fmtTime(s.start_time)} – {fmtTime(s.end_time)}, {s.duration_hours || 0}h) to Field Tracker. Which field job?</p>
                                  <div className="flex items-end gap-2 flex-wrap">
                                    <div className="flex-1 min-w-[180px] relative">
                                      <input type="text" value={moveSessWO} onChange={e => searchMoveSessWO(e.target.value)} placeholder="Search WO#…"
                                        data-testid={`move-session-wo-${s.id}`}
                                        className="w-full px-2 py-1.5 bg-white border border-slate-300 rounded text-slate-900 text-sm font-mono placeholder-slate-400 outline-none" />
                                      {moveSessResults.length > 0 && (
                                        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-300 rounded-lg shadow-lg max-h-40 overflow-y-auto divide-y divide-slate-100">
                                          {moveSessResults.map(wo => (
                                            <button key={wo.project_number} onClick={() => { setMoveSessSelected(wo); setMoveSessWO(wo.project_number); setMoveSessResults([]); }}
                                              data-testid={`move-session-wo-opt-${wo.project_number}`}
                                              className="w-full text-left px-2.5 py-1.5 hover:bg-blue-50 text-xs">
                                              <span className="font-mono font-bold text-blue-700">{wo.project_number}</span> <span className="text-slate-900 font-medium">{wo.client_name || 'No customer'}</span>
                                              {(wo.title || wo.description) && <p className="text-[11px] text-slate-600 truncate">{wo.title || wo.description}</p>}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    <label className="flex items-center gap-1.5 text-[11px] text-slate-700 pb-1.5">
                                      <input type="checkbox" checked={moveSessService} onChange={e => setMoveSessService(e.target.checked)} data-testid={`move-session-service-${s.id}`} /> Service call (4h min)
                                    </label>
                                    <button onClick={() => moveSessionToField(s, c, isApproved)} disabled={moveSessBusy || !moveSessWO.trim()}
                                      data-testid={`move-session-confirm-${s.id}`}
                                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded text-xs font-bold flex items-center gap-1">
                                      <Truck className="w-3.5 h-3.5" /> {moveSessBusy ? 'Moving…' : 'Move session to Field'}
                                    </button>
                                    <button onClick={() => setMovingSession(null)} className="px-2 py-1.5 text-slate-600 hover:text-slate-900 text-xs">Cancel</button>
                                  </div>
                                </div>
                              )}
                              {(s.notes || []).length > 0 && (
                                <ul className="mt-1.5 space-y-0.5" data-testid={`foreman-session-notes-${s.id}`}>
                                  {s.notes.map(n => (
                                    <li key={n.id} className="text-xs text-slate-700 flex items-start gap-1.5">
                                      <MessageSquare className="w-3 h-3 text-blue-700 mt-0.5 flex-shrink-0" />
                                      <span><span className="text-slate-500">{fmtTime(n.time)} —</span> {n.text}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Consumables — show existing + add button */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-xs text-slate-900 font-bold uppercase tracking-wider flex items-center gap-1">
                          <Package className="w-3 h-3" /> Consumables
                        </h4>
                        <button onClick={() => setAddConsFor(addConsFor === c.employee_id ? null : c.employee_id)}
                          className="text-xs px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded flex items-center gap-1">
                          + Add Consumable
                        </button>
                      </div>

                      {addConsFor === c.employee_id && (
                        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg mb-2">
                          <div className="flex flex-wrap gap-2 items-end">
                            <div className="flex-1 min-w-[180px] relative">
                              <label className="text-[10px] text-slate-900">Item</label>
                              <input type="text" value={consSearch} onChange={e => setConsSearch(e.target.value)}
                                placeholder="Search consumable..."
                                className="w-full px-2 py-1.5 bg-white/80 border border-slate-200 rounded text-slate-900 text-sm placeholder-slate-400 outline-none" />
                              {consSearch.length > 1 && (
                                <div className="absolute z-30 bottom-full mb-1 w-full bg-white border border-slate-300 rounded max-h-40 overflow-y-auto shadow-lg">
                                  {catalog.filter(ci => ci.name.toLowerCase().includes(consSearch.toLowerCase())).slice(0, 10).map(ci => (
                                    <button key={ci.name} onClick={() => setConsSearch(ci.name)}
                                      className="w-full text-left px-2 py-1.5 hover:bg-blue-50 text-xs text-slate-900 border-b border-slate-100">
                                      {ci.name} <span className="text-slate-500">{ci.category}</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            <div className="w-16">
                              <label className="text-[10px] text-slate-900">Qty</label>
                              <input type="number" min="1" value={consQty}
                                onFocus={e => e.target.select()}
                                onChange={e => setConsQty(parseInt(e.target.value) || 0)}
                                className="w-full px-2 py-1.5 bg-white/80 border border-slate-200 rounded text-slate-900 text-sm text-center outline-none" />
                            </div>
                            <button onClick={async () => {
                              if (!consSearch.trim()) { toast.error('Select an item'); return; }
                              const wo = empSessions[0]?.work_order_number || '';
                              try {
                                await axios.post(`${API}/foreman/add-consumable`, {
                                  employee_id: c.employee_id, item_name: consSearch.trim(), quantity: consQty,
                                  work_order_number: wo, work_order_customer: empSessions[0]?.work_order_customer || '', date: selectedDate,
                                });
                                toast.success(`Added ${consSearch} x${consQty}`);
                                setConsSearch(''); setConsQty(1); setAddConsFor(null);
                                refreshEmployee(c.employee_id);
                              } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                            }} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-medium">Add</button>
                            <button onClick={() => setAddConsFor(null)} className="text-xs text-slate-900">Cancel</button>
                          </div>
                        </div>
                      )}

                      {empConsumables.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                          {empConsumables.map((e, i) => (
                            <div key={i} className="flex items-center justify-between px-2 py-1.5 bg-slate-900/10 rounded text-xs">
                              <span className="text-slate-900">
                                {e.item_name}{' '}
                                {editConsQtyId === e.id ? (
                                  <span className="inline-flex items-center gap-1">
                                    <span className="text-slate-900">x</span>
                                    <input type="number" min="1" value={editConsQtyVal} onFocus={ev => ev.target.select()}
                                      onChange={ev => setEditConsQtyVal(ev.target.value)}
                                      onKeyDown={async ev => {
                                        if (ev.key === 'Enter') {
                                          const q = parseFloat(editConsQtyVal);
                                          if (q > 0) {
                                            await axios.put(`${API}/foreman/edit-consumable/${e.id}`, { quantity: q });
                                            toast.success(`Updated to x${q}`);
                                            setEditConsQtyId(null);
                                            refreshEmployee(c.employee_id);
                                          }
                                        } else if (ev.key === 'Escape') setEditConsQtyId(null);
                                      }}
                                      className="w-12 px-1 py-0.5 bg-white border border-blue-500 rounded text-slate-900 text-xs text-center outline-none" autoFocus />
                                    <button onClick={async () => {
                                      const q = parseFloat(editConsQtyVal);
                                      if (q > 0) {
                                        await axios.put(`${API}/foreman/edit-consumable/${e.id}`, { quantity: q });
                                        toast.success(`Updated to x${q}`);
                                        setEditConsQtyId(null);
                                        refreshEmployee(c.employee_id);
                                      }
                                    }} className="text-emerald-700 text-[10px]">ok</button>
                                    <button onClick={() => setEditConsQtyId(null)} className="text-slate-900 text-[10px]">X</button>
                                  </span>
                                ) : (
                                  <button onClick={() => { setEditConsQtyId(e.id); setEditConsQtyVal(String(e.quantity)); }}
                                    className="text-slate-900 font-bold hover:text-blue-700 hover:underline cursor-pointer" title="Click to edit quantity">
                                    x{e.quantity}
                                  </button>
                                )}
                              </span>
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-blue-700">{e.work_order_number}</span>
                                {e.added_by_foreman && <span className="text-[10px] text-blue-700 bg-blue-50 px-1 rounded">foreman</span>}
                                <button onClick={async () => {
                                  try {
                                    await axios.delete(`${API}/consumable-entries/${e.id}`);
                                    toast.success('Deleted');
                                    refreshEmployee(c.employee_id);
                                  } catch { toast.error('Failed'); }
                                }} className="text-slate-900 hover:text-red-600">
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {empConsumables.length === 0 && addConsFor !== c.employee_id && (
                        <p className="text-xs text-slate-900">No consumables logged</p>
                      )}
                    </div>

                    {/* Employee Notes */}
                    <div>
                      <h4 className="text-xs text-slate-900 font-bold uppercase tracking-wider mb-2 flex items-center gap-1">
                        <FileText className="w-3 h-3" /> Notes & Incidents
                      </h4>
                      <div className="flex gap-2 mb-2">
                        <select value={noteCategory} onChange={e => setNoteCategory(e.target.value)}
                          className="px-2 py-1.5 bg-white/80 border border-slate-200 rounded text-slate-900 text-xs outline-none">
                          <option value="general">General</option>
                          <option value="attendance">Attendance</option>
                          <option value="performance">Performance</option>
                          <option value="incident">Incident</option>
                          <option value="positive">Positive</option>
                        </select>
                        <input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a note..."
                          onKeyDown={e => e.key === 'Enter' && addNote(c.employee_id)}
                          className="flex-1 px-2 py-1.5 bg-white border border-slate-300 rounded text-slate-900 placeholder-slate-400 text-xs outline-none" />
                        <button onClick={() => addNote(c.employee_id)}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium">Add</button>
                      </div>
                      {empNotes.length > 0 && (
                        <div className="space-y-1 max-h-[150px] overflow-y-auto">
                          {empNotes.map(n => (
                            <div key={n.id} className={`flex items-start gap-2 px-2 py-1.5 rounded text-xs border ${
                              n.category === 'incident' ? 'bg-red-50 border-red-200' :
                              n.category === 'attendance' ? 'bg-amber-50 border-amber-200' :
                              n.category === 'positive' ? 'bg-emerald-50 border-emerald-200' :
                              'bg-slate-900/10 border-slate-200'
                            }`}>
                              <div className="flex-1">
                                <span className="text-slate-900">{n.note}</span>
                                <span className="text-slate-900 ml-2">{n.date} by {n.added_by}</span>
                              </div>
                              <button onClick={() => deleteNote(n.id)} className="text-slate-900 hover:text-red-600 flex-shrink-0">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Shop guys out on a customer site today (millwrights live in the Field Tracker) */}
      {notIn.some(e => e.on_site && SHOP_ROLES.includes(e.role)) && (
        <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-emerald-200 shadow-sm p-4" data-testid="on-site-panel">
          <h4 className="text-xs text-emerald-800 font-semibold uppercase tracking-wider mb-3 flex items-center gap-1">
            <Truck className="w-3 h-3" /> Shop Crew On Site ({notIn.filter(e => e.on_site && SHOP_ROLES.includes(e.role)).length})
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-1.5">
            {[...notIn.filter(e => e.on_site && SHOP_ROLES.includes(e.role))].sort((a, b) => {
              const rank = s => (s.source === 'clock' ? (s.clock_out_time ? 2 : 0) : 1);
              return rank(a.on_site) - rank(b.on_site) || a.employee_name.localeCompare(b.employee_name);
            }).map(e => {
              const s = e.on_site;
              const live = s.source === 'clock' && !s.clock_out_time;
              return (
                <div key={e.employee_id} className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg border ${live ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-slate-200'}`} data-testid={`on-site-emp-${e.employee_id}`}>
                  <span className={`w-2 h-2 rounded-full shrink-0 ${live ? 'bg-emerald-500 ring-2 ring-emerald-200' : s.source === 'clock' ? 'bg-slate-300' : 'bg-sky-400'}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-900 font-medium truncate">{e.employee_name}
                      <span className="ml-1.5 px-1 py-px rounded border font-bold text-[9px] bg-emerald-100 text-emerald-700 border-emerald-300">ON SITE</span></p>
                    <p className="text-[11px] text-slate-600 truncate">
                      {s.work_order_number && <span className="font-mono text-blue-700 font-semibold">{s.work_order_number} </span>}{s.customer}{s.location ? ` · ${s.location}` : ''}
                    </p>
                    <p className="text-[10px] text-slate-500 truncate">
                      {s.source === 'clock'
                        ? (live ? `Clocked in ${fmtTime(s.clock_in_time)}${s.clocked_in_by ? ` by ${s.clocked_in_by}` : ''}` : `Clocked out ${fmtTime(s.clock_out_time)}`)
                        : `Scheduled${s.start_time ? ` ${s.start_time}${s.end_time ? `–${s.end_time}` : ''}` : ''} (dispatch)`}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Not clocked in */}
      {notIn.some(e => !e.on_site) && (
        <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm p-4" data-testid="not-clocked-in-panel">
          <h4 className="text-xs text-slate-900 font-semibold uppercase tracking-wider mb-3 flex items-center gap-1">
            <UserX className="w-3 h-3" /> Not Clocked In ({notIn.filter(e => !e.on_site).length})
          </h4>
          {(() => {
            const groups = {};
            const OFFICE_ROLES = ['super_admin','admin','hr','office_admin','project_manager','estimator','project_manager_estimator'];
            notIn.forEach(e => {
              // Skip office employees and anyone already accounted for on a site
              if (OFFICE_ROLES.includes(e.role) || e.on_site) return;
              const dept = e.department || (
                ['millwright','millwright_foreman'].includes(e.role) ? 'Millwright' :
                ['welder','fitter','fabrication','fabrication_manager','shop_foreman','labourer'].includes(e.role) ? 'Fabrication' :
                ['tool_crib'].includes(e.role) ? 'Tool Crib' :
                ['dispatch'].includes(e.role) ? 'Dispatch' :
                'Other'
              );
              if (!groups[dept]) groups[dept] = [];
              groups[dept].push(e);
            });
            const order = ['Fabrication', 'Millwright', 'Tool Crib', 'Dispatch', 'Other'];
            const pick = (e) => {
              const next = notInAction?.employee_id === e.employee_id ? null : e;
              setNotInAction(next); setVacRange(null);
              // the buttons render right under this group — bring them into view on the next paint
              if (next) requestAnimationFrame(() => notInPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
            };
            return order.filter(d => groups[d]).map(dept => {
              const emps = groups[dept];
              const hasSelected = emps.some(e => e.employee_id === notInAction?.employee_id);
              return (
              <div key={dept} className="mb-2">
                <p className="text-[10px] text-slate-600 font-bold uppercase mb-1">{dept}</p>
                <div className="flex flex-wrap gap-1.5">
                  {emps.map(e => (
                    <button key={e.employee_id} onClick={() => pick(e)} data-testid={`not-in-emp-${e.employee_id}`}
                      className={`text-xs px-2 py-1 rounded-lg border transition-colors inline-flex items-center gap-1.5 ${notInAction?.employee_id === e.employee_id ? 'bg-blue-100 border-blue-400 text-blue-800' : e.marked ? 'bg-white text-slate-500 border-slate-200 hover:bg-blue-50' : 'bg-slate-100 text-slate-800 border-slate-200 hover:bg-blue-50'}`}>
                      {e.employee_name}
                      {e.marked && <span className={`px-1 py-px rounded border font-bold text-[9px] ${ATT_BADGE[e.marked.type] || 'bg-slate-100 text-slate-600 border-slate-300'}`} data-testid={`not-in-marked-${e.employee_id}`}>{ATT_LABELS[e.marked.type] || e.marked.type}</span>}
                    </button>
                  ))}
                </div>
                {hasSelected && notInAction && <NotInActionPanel ref={notInPanelRef} emp={notInAction} selectedDate={selectedDate} vacRange={vacRange} setVacRange={setVacRange}
                  onDone={() => { setNotInAction(null); setVacRange(null); load(); }} onClose={() => { setNotInAction(null); setVacRange(null); }} />}
              </div>
            );});
          })()}
        </div>
      )}

      {/* Pending Entries for Approval */}
      {(pending.time_entries.length > 0 || pending.consumable_entries.length > 0) && (
        <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 bg-slate-100/80 border-b border-slate-200 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-700" />
              Pending Approval ({pending.time_entries.length + pending.consumable_entries.length} entries)
            </h3>
            <div className="flex items-center gap-2">
              <button onClick={selectAllPending} className="px-2 py-1 bg-slate-200 hover:bg-slate-300 text-slate-900 rounded text-xs">
                {selectedEntries.length === pending.time_entries.length + pending.consumable_entries.length ? 'Deselect All' : 'Select All'}
              </button>
              <button onClick={() => doPendingAction('approve')}
                className="px-3 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-medium flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Approve ({selectedEntries.length})
              </button>
              <button onClick={() => doPendingAction('reject')}
                className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-medium flex items-center gap-1">
                <XCircle className="w-3 h-3" /> Reject ({selectedEntries.length})
              </button>
            </div>
          </div>

          {pending.time_entries.length > 0 && (
            <div className="px-4 py-3">
              <h5 className="text-xs text-slate-900 font-semibold uppercase tracking-wider mb-2 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Time Entries
              </h5>
              <div className="space-y-1">
                {pending.time_entries.map(e => (
                  <div key={e.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200">
                    <input type="checkbox" checked={isEntrySelected(e.id)} onChange={() => toggleEntry(e.id, 'time')} className="rounded border-slate-500" />
                    <span className="text-sm text-slate-900 font-medium flex-shrink-0">{e.employee_name}</span>
                    <span className="font-mono text-blue-700 text-xs">{e.work_order_number}</span>
                    <span className="text-xs text-slate-900">{fmtDate(e.date)}</span>
                    <span className="text-sm text-slate-900 font-bold">{e.regular_hours}h</span>
                    {e.overtime_hours > 0 && <span className="text-sm text-amber-700 font-bold">+{e.overtime_hours}h OT</span>}
                    {e.source === 'job_session' && <span className="text-[10px] text-blue-700 bg-blue-50 px-1 rounded">auto</span>}
                    {e.notes && <span className="text-xs text-slate-900 truncate max-w-[150px]">{e.notes}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {pending.consumable_entries.length > 0 && (
            <div className={`px-4 py-3 ${pending.time_entries.length > 0 ? 'border-t border-slate-200' : ''}`}>
              <h5 className="text-xs text-slate-900 font-semibold uppercase tracking-wider mb-2 flex items-center gap-1">
                <Package className="w-3 h-3" /> Consumable Entries
              </h5>
              <div className="space-y-1">
                {pending.consumable_entries.map(e => (
                  <div key={e.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200">
                    <input type="checkbox" checked={isEntrySelected(e.id)} onChange={() => toggleEntry(e.id, 'cons')} className="rounded border-slate-500" />
                    <span className="text-sm text-slate-900 font-medium flex-shrink-0">{e.employee_name}</span>
                    <span className="font-mono text-blue-700 text-xs">{e.work_order_number}</span>
                    <span className="text-xs text-slate-900">{fmtDate(e.date)}</span>
                    <span className="text-sm text-slate-900">{e.item_name}</span>
                    <span className="text-sm text-slate-900 font-bold">x{e.quantity} {e.unit || ''}</span>
                    {e.is_material && <span className="text-[10px] text-blue-700 bg-blue-50 px-1 rounded">material</span>}
                    {e.is_material && e.weight_info > 0 && (
                      <span className="text-[10px] text-slate-900 bg-slate-100 px-1.5 py-0.5 rounded" title={`${e.weight_info} lb/${e.weight_unit || 'ft'} × ${e.quantity} ${e.weight_unit || 'ft'}`}>
                        {(e.weight_info * e.quantity).toFixed(0)} lbs
                      </span>
                    )}
                    {editingPriceId === e.id ? (
                      <span className="flex items-center gap-1 flex-wrap">
                        {e.is_material && e.weight_info ? (
                          <span className="text-[10px] text-slate-900 mr-1">{e.quantity}{e.unit || 'ft'} × {e.weight_info}lb/{e.weight_unit || 'ft'} = {(e.quantity * e.weight_info).toFixed(0)}lb</span>
                        ) : null}
                        <select value={priceMode} onChange={ev => setPriceMode(ev.target.value)}
                          className="px-1 py-1 bg-white border border-slate-300 rounded text-slate-900 text-[10px] outline-none">
                          {e.is_material && e.weight_info ? <option value="lb">$/lb</option> : null}
                          <option value="unit">$/{e.unit || 'unit'}</option>
                          <option value="flat">Flat $</option>
                        </select>
                        <input type="number" step="0.01" value={priceInput} onFocus={ev => ev.target.select()}
                          onChange={ev => setPriceInput(ev.target.value)} placeholder="0.00"
                          onKeyDown={async ev => {
                            if (ev.key === 'Enter') {
                              const p = parseFloat(priceInput);
                              if (p > 0) {
                                let unitPrice = p;
                                if (priceMode === 'lb' && e.weight_info) unitPrice = p * e.weight_info;
                                if (priceMode === 'flat') unitPrice = p / (e.quantity || 1);
                                await axios.post(`${API}/admin/update-consumable-price`, { entry_id: e.id, new_price: unitPrice });
                                toast.success(`Price set`);
                                setEditingPriceId(null); load();
                              }
                            } else if (ev.key === 'Escape') setEditingPriceId(null);
                          }}
                          className="w-20 px-2 py-1 bg-white border border-blue-500 rounded text-slate-900 text-xs text-center outline-none" autoFocus />
                        {priceInput && parseFloat(priceInput) > 0 && (
                          <span className="text-[10px] text-emerald-700">
                            = ${(priceMode === 'lb' && e.weight_info ? parseFloat(priceInput) * e.weight_info * e.quantity
                              : priceMode === 'flat' ? parseFloat(priceInput)
                              : parseFloat(priceInput) * e.quantity).toFixed(2)}
                          </span>
                        )}
                        <button onClick={async () => {
                          const p = parseFloat(priceInput);
                          if (p > 0) {
                            let unitPrice = p;
                            if (priceMode === 'lb' && e.weight_info) unitPrice = p * e.weight_info;
                            if (priceMode === 'flat') unitPrice = p / (e.quantity || 1);
                            await axios.post(`${API}/admin/update-consumable-price`, { entry_id: e.id, new_price: unitPrice });
                            toast.success(`Price set`);
                            setEditingPriceId(null); load();
                          }
                        }} className="px-2 py-1 bg-emerald-600 text-white rounded text-[10px]">Set</button>
                        <button onClick={() => setEditingPriceId(null)} className="text-xs text-slate-900">X</button>
                      </span>
                    ) : (
                      <button onClick={() => { setEditingPriceId(e.id); setPriceInput(e.base_price_per_lb && e.is_material ? String(e.base_price_per_lb) : ''); setPriceMode(e.is_material && e.weight_info ? 'lb' : 'unit'); }}
                        className={`text-sm font-mono ${(e.total_cost || 0) > 0 ? 'text-emerald-700' : 'text-red-600 bg-red-50 px-2 py-0.5 rounded cursor-pointer hover:bg-red-50'}`}
                        title={e.total_cost > 0 ? `$${(e.unit_cost||0).toFixed(2)}/unit — click to change` : 'Click to set price'}>
                        ${(e.total_cost || 0).toFixed(2)}{(e.total_cost || 0) === 0 && ' — set price'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/* ════════════════════════════════════════════════════════════════
   FAB MANAGER TAB — High-level shop overview
   ════════════════════════════════════════════════════════════════ */
/* ════════════════════════════════════════════════════════════════
   SICK / VACATION MANAGER — mark & review absences for payroll
   ════════════════════════════════════════════════════════════════ */
function AbsenceManager({ dateFrom, dateTo, onChanged, canMark }) {
  const [open, setOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ employee_id: '', type: 'sick', from: '', to: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const loadEvents = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/attendance-events`, { params: { date_from: dateFrom, date_to: dateTo } });
      setEvents(r.data || []);
    } catch { setEvents([]); }
  }, [dateFrom, dateTo]);

  const openModal = async () => {
    setOpen(true);
    setForm({ employee_id: '', type: 'sick', from: dateFrom, to: '', notes: '' });
    loadEvents();
    if (employees.length === 0) {
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users`);
        const list = Array.isArray(r.data) ? r.data : (r.data?.users || []);
        setEmployees(list.filter(u => u.is_active !== false && u.full_name).sort((a, b) => a.full_name.localeCompare(b.full_name)));
      } catch { toast.error('Could not load employee list'); }
    }
  };

  const save = async () => {
    if (!form.employee_id) { toast.error('Pick an employee'); return; }
    if (!form.from) { toast.error('Pick a date'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/attendance-event`, {
        employee_id: form.employee_id, date: form.from, date_to: form.to || form.from, type: form.type, notes: form.notes,
      });
      toast.success(`Marked ${ATT_LABELS[form.type]} — ${r.data.created} day${r.data.created > 1 ? 's' : ''}`);
      setForm(f => ({ ...f, employee_id: '', to: '', notes: '' }));
      loadEvents(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/attendance-event/${id}`);
      toast.success('Removed');
      loadEvents(); onChanged();
    } catch { toast.error('Failed to remove'); }
  };

  if (!canMark) return null;
  return (
    <>
      <button data-testid="mark-absence-btn" onClick={openModal}
        className="px-5 py-2.5 bg-sky-700 hover:bg-sky-600 text-white rounded-lg font-medium flex items-center gap-2">
        <UserX className="w-4 h-4" /> Sick / Vacation
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg my-8" onClick={e => e.stopPropagation()} data-testid="absence-modal">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><UserX className="w-5 h-5 text-sky-600" /> Sick / Vacation Days</h2>
              <button onClick={() => setOpen(false)} data-testid="absence-modal-close"><XCircle className="w-5 h-5 text-slate-500 hover:text-slate-700" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs text-slate-600 mb-1">Employee</label>
                  <select data-testid="absence-employee-select" value={form.employee_id} onChange={e => setForm({ ...form, employee_id: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 outline-none focus:border-blue-500">
                    <option value="">Select employee…</option>
                    {employees.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">Type</label>
                  <select data-testid="absence-type-select" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 outline-none focus:border-blue-500">
                    <option value="sick">Sick</option>
                    <option value="vacation">Vacation</option>
                    <option value="absent">Absent</option>
                    <option value="on_site">On Site</option>
                    <option value="no_work">No Work</option>
                  </select>
                </div>
                <div />
                <div>
                  <label className="block text-xs text-slate-600 mb-1">From</label>
                  <input type="date" data-testid="absence-from" value={form.from} onChange={e => setForm({ ...form, from: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 outline-none focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-xs text-slate-600 mb-1">To <span className="text-slate-500">(optional — for a range)</span></label>
                  <input type="date" data-testid="absence-to" value={form.to} onChange={e => setForm({ ...form, to: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 outline-none focus:border-blue-500" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-slate-600 mb-1">Note <span className="text-slate-500">(optional)</span></label>
                  <input data-testid="absence-note" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                    placeholder="e.g. doctor's appointment" className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 outline-none focus:border-blue-500" />
                </div>
              </div>
              <button data-testid="absence-save-btn" onClick={save} disabled={saving}
                className="w-full px-4 py-2 bg-sky-600 hover:bg-sky-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                {saving ? 'Saving…' : 'Mark It'}
              </button>
              <div>
                <p className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">
                  Marked this week ({fmtDate(dateFrom)} – {fmtDate(dateTo)})
                </p>
                {events.length === 0 ? (
                  <p className="text-xs text-slate-500" data-testid="absence-empty">No sick / vacation days marked in this range.</p>
                ) : (
                  <div className="space-y-1 max-h-48 overflow-y-auto" data-testid="absence-events-list">
                    {events.map(ev => (
                      <div key={ev.id} className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 border border-slate-200 rounded text-xs">
                        <span className={`px-1.5 py-0.5 rounded border font-bold text-[9px] ${ATT_BADGE[ev.type] || ''}`}>{ATT_LABELS[ev.type] || ev.type}</span>
                        <span className="font-semibold text-slate-900">{ev.employee_name}</span>
                        <span className="text-slate-500">{fmtDate(ev.date)}</span>
                        {ev.notes && <span className="text-slate-500 italic truncate">{ev.notes}</span>}
                        <span className="text-slate-500 ml-auto">by {ev.logged_by}</span>
                        <button data-testid={`absence-delete-${ev.id}`} onClick={() => remove(ev.id)} className="text-slate-500 hover:text-red-600 flex-shrink-0">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ════════════════════════════════════════════════════════════════
   REPORTS TAB
   ════════════════════════════════════════════════════════════════ */
function ReportsTab({ isForeman }) {
  const { user } = useAuth();
  const canEditSell = ['super_admin', 'admin', 'office_admin', 'project_manager', 'project_manager_estimator', 'shop_foreman', 'fabrication_manager'].includes(user?.role) || (user?.extra_permissions || []).includes('shop_foreman_review');
  const canEditTimes = ['super_admin', 'admin', 'office_admin', 'project_manager', 'project_manager_estimator', 'fabrication_manager'].includes(user?.role);
  const urlq = new URLSearchParams(window.location.search);
  const [reportType, setReportType] = useState(urlq.get('report') || 'linda');
  const [dateFrom, setDateFrom] = useState(sundayOf(urlq.get('week') || today()));
  const [dateTo, setDateTo] = useState(saturdayOf(urlq.get('week') || today()));
  const canSaveMarkups = ['super_admin', 'admin', 'office_admin', 'fabrication_manager'].includes(user?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editSellId, setEditSellId] = useState(null);
  const [sellInput, setSellInput] = useState('');
  const [savingSell, setSavingSell] = useState(false);
  const [weekApproval, setWeekApproval] = useState(null);
  const [approvalRequested, setApprovalRequested] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [editWORow, setEditWORow] = useState(null); // {employee_name, old_wo}
  const [editWOSearch, setEditWOSearch] = useState('');
  const [editWOResults, setEditWOResults] = useState([]);

  const generate = async () => {
    setLoading(true);
    setApprovalRequested(false);
    try {
      const endpoints = { employee: 'employee-summary', wo: 'wo-cost-summary', timesheet: 'weekly-timesheet', linda: 'linda-report', consumables: 'consumable-costing', materials: 'materials-by-wo', fabtm: 'fab-tm-sheet' };
      const r = await axios.get(`${API}/reports/${endpoints[reportType]}`, { params: { date_from: dateFrom, date_to: dateTo } });
      setData(r.data);
      if (reportType === 'linda' || reportType === 'consumables') {
        const apR = await axios.get(`${API}/reports/week-approval`, { params: { date_from: dateFrom, date_to: dateTo } });
        setWeekApproval(apR.data?.approved_by ? apR.data : null);
      }
    } catch { toast.error('Failed to generate report'); }
    setLoading(false);
  };

  const moveConsumables = async (payload, what, wo) => {
    try {
      const r = await axios.post(`${API}/consumable-entries/move`, { ...payload, new_wo: wo.project_number, new_customer: wo.client_name || '' });
      toast.success(`Moved ${what} → ${wo.project_number} (${r.data.moved} ${r.data.moved === 1 ? 'entry' : 'entries'})`);
      generate();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not move'); }
  };

  const saveSellPrice = async (entryId) => {
    const p = parseFloat(sellInput);
    if (isNaN(p) || p < 0) { toast.error('Enter a valid sell price'); return; }
    setSavingSell(true);
    try {
      const r = await axios.put(`${API}/consumable-entries/${entryId}/sell-price`, { sell_price: p });
      toast.success(r.data?.catalog_updated ? 'Sell price updated (entry + catalog listing)' : 'Sell price updated for this entry');
      setEditSellId(null); setSellInput('');
      await generate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update sell price');
    }
    setSavingSell(false);
  };

  // Foremen: load pending approval requests
  useEffect(() => {
    if (isForeman) {
      axios.get(`${API}/reports/pending-approval-requests`).then(r => setPendingRequests(r.data || [])).catch(() => {});
    }
  }, [isForeman, weekApproval]);

  const rescindWeek = async () => {
    const reason = window.prompt('Reason for rescinding approval (optional):');
    if (reason === null) return; // cancelled
    try {
      await axios.post(`${API}/reports/rescind-week`, { date_from: dateFrom, date_to: dateTo, reason });
      setWeekApproval(null);
      toast.success('Approval rescinded — Chris & Linda notified. Make your changes and re-approve.');
    } catch { toast.error('Failed'); }
  };

  const requestApproval = async () => {
    try {
      await axios.post(`${API}/reports/request-approval`, { date_from: dateFrom, date_to: dateTo });
      setApprovalRequested(true);
      toast.success('Approval request sent to Dickson — he\'ll be notified to review and approve this week\'s timesheets');
    } catch { toast.error('Failed to send request'); }
  };

  const printLindaReport = () => {
    if (!data?.rows) return;
    // Gate: must be approved to print
    if (!weekApproval) {
      toast.error('This week\'s timesheets have not been approved yet. Use "Request Approval" to notify Dickson.');
      return;
    }
    const pw = window.open('', '_blank');
    if (!pw) return;
    const dayLabels = (data.dates || []).map(d => {
      const dt = new Date(d + 'T00:00:00');
      return { date: d, label: dt.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' }) };
    });
    let html = '<html><head><title>QB Entry Report</title><style>';
    html += 'body{font-family:Arial,sans-serif;padding:15px;font-size:11px}';
    html += 'h2{font-size:14px;margin-bottom:4px}p{font-size:10px;color:#666;margin-bottom:8px}';
    html += 'table{border-collapse:collapse;width:100%;font-size:10px}';
    html += 'th{background:#e8e8e8;padding:4px 6px;border:1px solid #bbb;text-align:center;font-size:9px}';
    html += 'th:first-child,th:nth-child(2),th:nth-child(3){text-align:left}';
    html += 'td{padding:3px 6px;border:1px solid #ccc;text-align:center}';
    html += '.emp{text-align:left;font-weight:bold}.wo{text-align:left;font-family:monospace;color:#2255cc}.cust{text-align:left;color:#555}';
    html += '.reg{background:#fffde0;font-weight:bold}.ot{background:#fff0e0;font-weight:bold;color:#b45000}';
    html += 'tr:nth-child(even){background:#f4f8ff}';
    html += '@media print{@page{size:landscape;margin:0.5cm}}';
    html += '</style></head><body>';
    html += '<h2>365 Industrial Solutions — QuickBooks Entry Report</h2>';
    html += '<p>Week: ' + dateFrom + ' to ' + dateTo + '</p>';
    html += '<table><tr><th>Employee</th><th>WO#</th><th>Customer</th>';
    dayLabels.forEach(function(d) { html += '<th>' + d.label + '</th>'; });
    html += '<th class="reg">Reg</th><th class="ot">OT</th></tr>';
    let lastEmp = '';
    let empColorIdx = 0;
    const empColorMap = {};
    const printColors = ['#e8f0fe', '#f5f5f5', '#e6f4ea', '#fef7e0', '#f3e8ff', '#fce8e6'];
    data.rows.forEach(function(row) {
      if (!empColorMap[row.employee_name]) {
        empColorMap[row.employee_name] = printColors[empColorIdx % printColors.length];
        empColorIdx++;
      }
      const bg = empColorMap[row.employee_name];
      const isTotal = row.is_employee_total;
      const showEmp = row.employee_name !== lastEmp;
      lastEmp = row.employee_name;

      if (isTotal) {
        html += '<tr style="border-bottom:3px solid #666;background:#d0d0d0">';
        html += '<td class="emp" style="background:#d0d0d0">' + row.employee_name + ' Total</td>';
        html += '<td></td><td></td>';
        dayLabels.forEach(function(d) {
          var day = row.days[d.date] || {};
          html += '<td style="font-weight:bold">' + (day.reg || '') + '</td>';
        });
        html += '<td class="reg" style="background:#ffe066;font-weight:bold">' + (row.total_reg || '') + '</td>';
        html += '<td class="ot" style="background:#ffcc80;font-weight:bold">' + (row.total_ot || '') + '</td></tr>';
      } else {
        html += '<tr style="background:' + bg + '">';
        html += '<td class="emp" style="background:' + bg + '">' + (showEmp ? row.employee_name : '') + '</td>';
        html += '<td class="wo">' + row.work_order_number + '</td>';
        html += '<td class="cust">' + (row.customer || '') + '</td>';
        dayLabels.forEach(function(d) {
          var day = row.days[d.date] || {};
          var val = '';
          if (day.reg) val += day.reg;
          if (day.ot) val += (val ? '+' : '') + day.ot + ' OT';
          html += '<td>' + val + '</td>';
        });
        html += '<td class="reg">' + (row.total_reg || '') + '</td>';
        html += '<td class="ot">' + (row.total_ot || '') + '</td></tr>';
      }
    });
    html += '</table></body></html>';
    pw.document.write(html); pw.document.close(); pw.print();
  };

  const printConsumableReport = () => {
    if (!data?.rows) return;
    const pw = window.open('', '_blank');
    if (!pw) return;
    let html = `<html><head><title>365 Industrial — Consumables by WO</title>
      <style>
        body{font-family:Arial,sans-serif;padding:15px;font-size:11px;color:#000}
        h2{font-size:14px;margin-bottom:4px}
        p.sub{font-size:10px;color:#666;margin-bottom:10px}
        table{border-collapse:collapse;width:100%;font-size:10px;margin-bottom:12px}
        th{background:#e8e8e8;padding:5px 6px;border:1px solid #bbb;text-align:left;font-size:9px}
        td{padding:4px 6px;border:1px solid #ccc}
        .wo-header{background:#d0e0f0;font-weight:bold;font-size:11px}
        .cat-label{color:#555;font-style:italic}
        .total-row{background:#fff8cc;font-weight:bold;border-top:2px solid #666}
        .grand-total{background:#ffe066;font-weight:bold;font-size:12px}
        .money{text-align:right;font-family:monospace}
        .qty{text-align:center}
        .summary{margin-bottom:12px;padding:8px;background:#f0f4f8;border-radius:6px}
        @media print{@page{size:portrait;margin:0.5cm}}
      </style></head><body>
      <h2>365 Industrial Solutions — Consumables by Work Order</h2>
      <p class="sub">Period: ${dateFrom} to ${dateTo}</p>
      <div class="summary">
        <strong>Welding Supplies:</strong> $${(data.welding_total || 0).toFixed(2)} &nbsp;|&nbsp;
        <strong>Abrasives & Tooling:</strong> $${(data.abrasives_total || 0).toFixed(2)} &nbsp;|&nbsp;
        <strong>Grand Total:</strong> $${(data.grand_total || 0).toFixed(2)}
      </div>
      <table>
        <tr><th>WO#</th><th>Category</th><th>Item</th><th>Employee</th><th>Date</th><th class="qty">Qty</th><th class="money">Unit $</th><th class="money">Total</th></tr>`;

    for (const row of data.rows) {
      if (row.is_wo_header) {
        html += `<tr class="wo-header"><td style="font-family:monospace;color:#1e3a5f">${row.work_order_number}</td><td class="cat-label">${row.qb_category}</td><td colspan="5">${row.customer || ''}</td><td class="money">$${row.category_total.toFixed(2)}</td></tr>`;
        continue;
      }
      if (row.is_wo_total) {
        html += `<tr class="total-row"><td colspan="7" style="text-align:right">${row.work_order_number} Total</td><td class="money">$${row.total.toFixed(2)}</td></tr>`;
        continue;
      }
      const dateStr = row.date ? new Date(row.date + 'T00:00:00').toLocaleDateString('en-US', {weekday:'short', month:'numeric', day:'numeric'}) : '';
      html += `<tr><td></td><td class="cat-label">${row.qb_category || ''}</td><td>${row.item_name || ''}</td><td>${row.employee_name || ''}</td><td>${dateStr}</td><td class="qty">${row.quantity || ''}</td><td class="money">$${(row.sell_price || 0).toFixed(2)}</td><td class="money">$${(row.sell_total || 0).toFixed(2)}</td></tr>`;
    }

    html += `<tr class="grand-total"><td colspan="7" style="text-align:right;font-size:12px">GRAND TOTAL</td><td class="money" style="font-size:12px">$${(data.grand_total || 0).toFixed(2)}</td></tr>`;
    html += `</table>`;
    html += `<p style="font-size:8px;color:#999">Generated ${new Date().toLocaleString(undefined, { timeZone: 'America/Toronto' })} — ${data.entry_count || 0} entries</p>`;
    html += `</body></html>`;
    pw.document.write(html); pw.document.close(); pw.print();
  };

  const printReport = () => {
    if (reportType === 'timesheet') return printTimesheet();
    if (reportType === 'linda') return printLindaReport();
    if (reportType === 'consumables') return printConsumableReport();
    if (reportType === 'materials') return printMaterialsReport(data, dateFrom, dateTo);
    const pw = window.open('', '_blank');
    if (!pw) return;
    let html = `<html><head><title>365 Industrial - ${reportType === 'employee' ? 'Employee Time Summary' : 'WO Cost Summary'}</title>
      <style>body{font-family:Arial,sans-serif;padding:20px;color:#1a1a1a}h1{font-size:18px;margin-bottom:4px}h2{font-size:14px;color:#555;margin-bottom:16px}h3{font-size:13px;margin:12px 0 6px;border-bottom:1px solid #ddd;padding-bottom:3px}table{width:100%;border-collapse:collapse;margin-bottom:16px;font-size:12px}th{background:#f0f0f0;text-align:left;padding:6px 8px;border:1px solid #ccc}td{padding:5px 8px;border:1px solid #ddd}.total-row{font-weight:bold;background:#f9f9f9}.section{page-break-inside:avoid;margin-bottom:20px}@media print{body{padding:0}}</style></head><body>
      <h1>365 Industrial Solutions</h1>
      <h2>${reportType === 'employee' ? 'Employee Time Summary' : 'Work Order Cost Summary'} — ${dateFrom} to ${dateTo}</h2>`;

    if (reportType === 'employee' && data?.employees) {
      for (const emp of data.employees) {
        html += `<div class="section"><h3>${emp.employee_name}</h3><table><tr><th>WO#</th><th>Customer</th><th>Description</th><th>Reg Hrs</th><th>OT Hrs</th></tr>`;
        for (const wo of emp.work_orders) {
          html += `<tr><td>${wo.work_order_number}</td><td>${wo.work_order_customer || ''}</td><td>${wo.work_order_description || ''}</td><td>${wo.total_regular}</td><td>${wo.total_overtime}</td></tr>`;
        }
        html += `<tr class="total-row"><td colspan="3">TOTAL</td><td>${emp.total_regular}</td><td>${emp.total_overtime}</td></tr></table></div>`;
      }
    }
    if (reportType === 'wo' && data?.work_orders) {
      for (const wo of data.work_orders) {
        html += `<div class="section"><h3>WO# ${wo.work_order_number} — ${wo.work_order_customer || ''}</h3><p style="font-size:12px;color:#555">${wo.work_order_description || ''}</p>`;
        if (wo.labour_entries?.length > 0) {
          html += `<table><tr><th>Employee</th><th>Date</th><th>Reg</th><th>OT</th></tr>`;
          for (const e of wo.labour_entries) html += `<tr><td>${e.employee_name}</td><td>${e.date}</td><td>${e.regular_hours}</td><td>${e.overtime_hours}</td></tr>`;
          html += `<tr class="total-row"><td colspan="2">Labour Total</td><td>${wo.total_regular}</td><td>${wo.total_overtime}</td></tr></table>`;
        }
        if (wo.consumable_entries?.length > 0) {
          html += `<table><tr><th>Item</th><th>Employee</th><th>Date</th><th>Qty</th><th>Cost</th></tr>`;
          for (const c of wo.consumable_entries) html += `<tr><td>${c.item_name}</td><td>${c.employee_name}</td><td>${c.date}</td><td>${c.quantity}</td><td>$${(c.total_cost || 0).toFixed(2)}</td></tr>`;
          html += `<tr class="total-row"><td colspan="4">Consumable Total</td><td>$${(wo.total_consumable_cost || 0).toFixed(2)}</td></tr></table>`;
        }
        html += `</div>`;
      }
    }
    html += `<p style="font-size:10px;color:#999;margin-top:30px">Generated ${new Date().toLocaleString(undefined, { timeZone: 'America/Toronto' })}</p></body></html>`;
    pw.document.write(html); pw.document.close(); pw.print();
  };

  const printTimesheet = () => {
    if (!data?.rows) return;
    const pw = window.open('', '_blank');
    if (!pw) return;
    const wos = data.work_orders || [];
    const weDate = new Date(dateTo + 'T00:00:00').toLocaleDateString('en-US', {month:'long', day:'numeric', year:'numeric'});

    let html = `<html><head><title>365 Industrial - Fab Labour</title>
      <style>
        body{font-family:Arial,sans-serif;padding:8px;color:#000;font-size:10px;margin:0}
        table{width:100%;border-collapse:collapse;font-size:10px}
        th,td{padding:2px 5px;border:1px solid #bbb;text-align:center}
        th{background:#e8e8e8;font-size:9px}
        th.wo-head{writing-mode:vertical-lr;transform:rotate(180deg);height:70px;font-family:monospace;background:#d0d0d0}
        .emp-name{text-align:left;font-weight:bold}
        .reg-row td{background:#ddeeff}
        .ot-row td{background:#eef4ff}
        .total-row td{font-weight:bold;background:#fff8cc;border-top:2px solid #000}
        .lbl{text-align:left;padding-left:4px;font-weight:bold}
        .totals-col{background:#fff8cc;font-weight:bold}
        h2{font-size:11px;margin:2px 0}
        p.sub{font-size:9px;color:#666;margin:0 0 4px}
        @media print{body{padding:0}@page{size:landscape;margin:0.3cm}}
      </style></head><body>
      <p class="sub">Totals</p>
      <h2>WE ${weDate} <span style="font-weight:normal;color:#666">Week Ending</span></h2>
      <table>
        <tr><th style="text-align:left">Fab Labour</th><th></th>`;
    for (const wo of wos) html += `<th class="wo-head">${wo}</th>`;
    html += `<th class="wo-head">Totals</th></tr>`;

    data.rows.forEach((row, i) => {
      const cls = i % 2 === 0 ? 'reg-row' : '';
      const attNote = row.attendance_summary ? '<div style="font-size:7px;color:#c00;font-weight:normal">' + row.attendance_summary + '</div>' : '';
      html += `<tr class="${cls}"><td class="emp-name">${row.employee_name}${attNote}</td><td class="lbl">Regular</td>`;
      for (const wo of wos) html += `<td>${row.regular_by_wo?.[wo] || ''}</td>`;
      html += `<td class="totals-col">${row.regular_total || ''}</td></tr>`;
      html += `<tr class="ot-row"><td></td><td class="lbl">O/T</td>`;
      for (const wo of wos) html += `<td>${row.overtime_by_wo?.[wo] || ''}</td>`;
      html += `<td class="totals-col">${row.overtime_total || ''}</td></tr>`;
    });

    html += `<tr class="total-row"><td style="text-align:left"><b>Totals</b></td><td class="lbl">Regular</td>`;
    for (const wo of wos) html += `<td>${data.totals_regular?.[wo] || ''}</td>`;
    html += `<td class="totals-col">${data.grand_total_regular}</td></tr>`;
    html += `<tr class="total-row"><td></td><td class="lbl">O/T</td>`;
    for (const wo of wos) html += `<td>${data.totals_overtime?.[wo] || ''}</td>`;
    html += `<td class="totals-col">${data.grand_total_overtime}</td></tr>`;

    html += `</table><p style="font-size:8px;color:#999;margin-top:8px">Generated ${new Date().toLocaleString(undefined, { timeZone: 'America/Toronto' })}</p></body></html>`;
    pw.document.write(html); pw.document.close(); pw.print();
  };

  const downloadTimesheetCSV = () => {
    const token = localStorage.getItem('token');
    const url = `${API}/reports/weekly-timesheet-csv?date_from=${dateFrom}&date_to=${dateTo}&token=${token}`;
    try { window.top.location.href = url; } catch { window.open(url, '_blank'); }
  };

  return (
    <div className="space-y-4">
      {/* Pending approval requests banner for foreman */}
      {isForeman && pendingRequests.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="pending-approval-banner">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-700">Timesheet Approval Requested</p>
              <div className="mt-2 space-y-1.5">
                {pendingRequests.map(req => (
                  <div key={req.id} className="flex items-center justify-between bg-amber-50 rounded-lg px-3 py-2">
                    <div>
                      <span className="text-sm text-slate-900 font-medium">{req.date_from} to {req.date_to}</span>
                      <span className="text-xs text-slate-600 ml-2">requested by {req.requested_by}</span>
                    </div>
                    <button
                      data-testid={`approve-request-${req.date_from}`}
                      onClick={() => {
                        setDateFrom(req.date_from);
                        setDateTo(req.date_to);
                        setReportType('linda');
                        setData(null);
                        setTimeout(() => generate(), 100);
                      }}
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-medium transition-colors"
                    >
                      Review & Approve
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm p-5">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-slate-900 mb-1">Report Type</label>
            <select data-testid="report-type-select" value={reportType} onChange={e => { setReportType(e.target.value); setData(null); }}
              className="px-3 py-2.5 bg-white border border-slate-200 rounded-lg text-slate-900 outline-none">
              <option value="linda">QB Entry Report (Linda)</option>
              <option value="consumables">Consumables by WO</option>
              <option value="materials">Materials by WO</option>
              <option value="fabtm">Fabrication T&amp;M Sheets</option>
              {/* Hidden reports — uncomment if needed */}
              {/* <option value="employee">Employee Time Summary</option> */}
              {/* <option value="wo">WO Cost Summary</option> */}
              {/* <option value="timesheet">Weekly Timesheet Grid</option> */}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-900 mb-1">Week</label>
            <div className="flex gap-2 items-center">
              <button onClick={() => { const d = new Date(dateFrom); d.setDate(d.getDate() - 7); setDateFrom(d.toISOString().slice(0,10)); const e = new Date(dateTo); e.setDate(e.getDate() - 7); setDateTo(e.toISOString().slice(0,10)); }}
                className="px-2 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-900 rounded-lg text-sm">&lt;</button>
              <div className="px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-slate-900 text-sm font-mono">
                {new Date(dateFrom + 'T00:00:00').toLocaleDateString('en-US', {month:'short', day:'numeric'})} — {new Date(dateTo + 'T00:00:00').toLocaleDateString('en-US', {month:'short', day:'numeric', year:'numeric'})}
              </div>
              <button onClick={() => { const d = new Date(dateFrom); d.setDate(d.getDate() + 7); setDateFrom(d.toISOString().slice(0,10)); const e = new Date(dateTo); e.setDate(e.getDate() + 7); setDateTo(e.toISOString().slice(0,10)); }}
                className="px-2 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-900 rounded-lg text-sm">&gt;</button>
            </div>
          </div>
          <button data-testid="generate-report-btn" onClick={generate} disabled={loading}
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors">
            {loading ? 'Generating...' : 'Generate'}
          </button>
          {data && reportType !== 'fabtm' && (
            reportType === 'linda' && !weekApproval ? (
              <button
                data-testid="print-blocked-btn"
                onClick={printReport}
                className="px-5 py-2.5 bg-slate-100 text-slate-900 rounded-lg font-medium flex items-center gap-2 cursor-not-allowed border border-slate-200"
                title="Week must be approved before printing"
              >
                <Printer className="w-4 h-4" /> Print (Not Approved)
              </button>
            ) : data ? (
              <button data-testid="print-report-btn" onClick={printReport} className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-lg font-medium flex items-center gap-2">
                <Printer className="w-4 h-4" /> Print
              </button>
            ) : null
          )}
          {/* Payroll approval lives here (not inside the scrolling report) so it is reachable on a phone */}
          {data && reportType === 'linda' && (
            weekApproval ? (
              <div className="flex items-center gap-2 flex-wrap" data-testid="week-approved-badge">
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-100 border border-emerald-300 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <div>
                    <p className="text-sm font-bold text-emerald-800 leading-tight">Approved for Payroll</p>
                    <p className="text-[10px] text-emerald-700">{weekApproval.approved_by} — {new Date(weekApproval.approved_at).toLocaleDateString()}</p>
                  </div>
                </div>
                {isForeman && (
                  <button onClick={rescindWeek} data-testid="rescind-week-btn" className="px-3 py-2 bg-red-100 hover:bg-red-200 border border-red-300 text-red-700 rounded-lg text-xs font-medium transition-colors">
                    Rescind
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg" data-testid="week-not-approved-badge">
                <AlertCircle className="w-4 h-4 text-red-500" />
                <div>
                  <p className="text-sm font-bold text-red-700 leading-tight">Not Approved</p>
                  <p className="text-[10px] text-red-500">Each group's manager ticks their people below</p>
                </div>
              </div>
            )
          )}
          {data && reportType === 'linda' && !weekApproval && !isForeman && (
            <button
              data-testid="request-approval-btn"
              onClick={requestApproval}
              disabled={approvalRequested}
              className={`px-5 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                approvalRequested
                  ? 'bg-amber-50 text-amber-700 border border-amber-200 cursor-default'
                  : 'bg-amber-600 hover:bg-amber-700 text-white'
              }`}
            >
              <AlertCircle className="w-4 h-4" />
              {approvalRequested ? 'Request Sent' : 'Request Approval'}
            </button>
          )}
          {reportType === 'timesheet' && (
            <button onClick={downloadTimesheetCSV} className="px-5 py-2.5 bg-blue-700 hover:bg-blue-600 text-white rounded-lg font-medium flex items-center gap-2">
              <FileText className="w-4 h-4" /> Download CSV
            </button>
          )}
          <button
            onClick={() => {
              const token = localStorage.getItem('token');
              const url = `${API}/reports/quickbooks-export?date_from=${dateFrom}&date_to=${dateTo}&token=${token}`;
              try { window.top.location.href = url; } catch { window.open(url, '_blank'); }
            }}
            className="px-5 py-2.5 bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg font-medium flex items-center gap-2"
          >
            <FileText className="w-4 h-4" /> QB Time Export
          </button>
          <AbsenceManager dateFrom={dateFrom} dateTo={dateTo} canMark={canEditTimes || isForeman}
            onChanged={() => { if (data) generate(); }} />
          <button
            onClick={() => {
              const token = localStorage.getItem('token');
              const url = `${API}/reports/quickbooks-consumables-export?date_from=${dateFrom}&date_to=${dateTo}&token=${token}`;
              try { window.top.location.href = url; } catch { window.open(url, '_blank'); }
            }}
            className="px-5 py-2.5 bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg font-medium flex items-center gap-2"
          >
            <Package className="w-4 h-4" /> QB Consumables Export
          </button>
        </div>
      </div>

      {data && reportType === 'employee' && data.employees && (
        <div className="space-y-4">
          {data.employees.length === 0 && <div className="text-center py-12 text-slate-900">No approved time entries for this period</div>}
          {data.employees.map(emp => (
            <div key={emp.employee_id} className="bg-white/70 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 bg-slate-100/80 border-b border-slate-200 flex items-center justify-between">
                <h4 className="text-slate-900 font-semibold">{emp.employee_name}</h4>
                <span className="text-sm text-blue-700">{emp.total_regular}h reg / {emp.total_overtime}h OT</span>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-slate-900 border-b border-slate-200"><th className="p-3 text-left">WO#</th><th className="p-3 text-left">Customer</th><th className="p-3 text-center">Reg</th><th className="p-3 text-center">OT</th></tr></thead>
                <tbody>
                  {emp.work_orders.map((wo, i) => (
                    <tr key={i} className="border-b border-slate-200">
                      <td className="p-3 font-mono text-blue-700">{wo.work_order_number}</td>
                      <td className="p-3 text-slate-900">{wo.work_order_customer}</td>
                      <td className="p-3 text-center text-slate-900 font-bold">{wo.total_regular}</td>
                      <td className="p-3 text-center text-amber-700 font-bold">{wo.total_overtime || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {data && reportType === 'wo' && data.work_orders && (
        <div className="space-y-4">
          {data.work_orders.length === 0 && <div className="text-center py-12 text-slate-900">No data for this period</div>}
          {data.work_orders.map(wo => (
            <div key={wo.work_order_number} className="bg-white/70 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-5 py-3 bg-slate-100/80 border-b border-slate-200">
                <span className="font-mono text-blue-600 font-bold">{wo.work_order_number}</span>
                <span className="mx-2 text-slate-900">|</span>
                <span className="text-slate-900">{wo.work_order_customer}</span>
              </div>
              {wo.labour_entries?.length > 0 && (
                <div className="px-5 py-3">
                  <h5 className="text-xs text-slate-900 font-semibold mb-2 uppercase tracking-wider">Labour</h5>
                  <table className="w-full text-sm">
                    <thead><tr className="text-xs text-slate-900"><th className="pb-1 text-left">Employee</th><th className="pb-1 text-left">Date</th><th className="pb-1 text-center">Reg</th><th className="pb-1 text-center">OT</th></tr></thead>
                    <tbody>{wo.labour_entries.map((e, i) => (<tr key={i}><td className="py-1 text-slate-900">{e.employee_name}</td><td className="py-1 text-slate-900">{fmtDate(e.date)}</td><td className="py-1 text-center text-slate-900">{e.regular_hours}</td><td className="py-1 text-center text-amber-700">{e.overtime_hours || '-'}</td></tr>))}</tbody>
                    <tfoot><tr className="border-t border-slate-200 font-bold"><td colSpan={2} className="py-1 text-slate-900">Total</td><td className="py-1 text-center text-slate-900">{wo.total_regular}</td><td className="py-1 text-center text-amber-700">{wo.total_overtime}</td></tr></tfoot>
                  </table>
                </div>
              )}
              {wo.consumable_entries?.length > 0 && (
                <div className="px-5 py-3 border-t border-slate-200">
                  <h5 className="text-xs text-slate-900 font-semibold mb-2 uppercase tracking-wider">Consumables</h5>
                  <table className="w-full text-sm">
                    <thead><tr className="text-xs text-slate-900"><th className="pb-1 text-left">Item</th><th className="pb-1 text-center">Qty</th><th className="pb-1 text-right">Cost</th></tr></thead>
                    <tbody>{wo.consumable_entries.map((c, i) => (<tr key={i}><td className="py-1 text-slate-900">{c.item_name}</td><td className="py-1 text-center text-slate-900">{c.quantity}</td><td className="py-1 text-right text-emerald-700">${(c.total_cost || 0).toFixed(2)}</td></tr>))}</tbody>
                    <tfoot><tr className="border-t border-slate-200 font-bold"><td className="py-1 text-slate-900">Total</td><td></td><td className="py-1 text-right text-emerald-700">${(wo.total_consumable_cost || 0).toFixed(2)}</td></tr></tfoot>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {data && reportType === 'timesheet' && data.rows && (
        <div className="bg-white rounded-xl border border-slate-300 overflow-x-auto">
          <div className="px-4 py-2 border-b border-slate-300 bg-slate-900/10">
            <p className="text-[10px] text-slate-900">Totals</p>
            <p className="text-sm text-slate-900 font-bold">WE {new Date(dateTo + 'T00:00:00').toLocaleDateString('en-US', {month:'long', day:'numeric', year:'numeric'})} <span className="text-slate-900 font-normal ml-1">Week Ending</span></p>
          </div>
          {data.rows.filter(r => r.regular_total || r.overtime_total || r.attendance_summary).length === 0 ? (
            <div className="text-center py-12 text-slate-900">No time entries for this period</div>
          ) : (
            <table className="text-[11px] whitespace-nowrap border-collapse" style={{tableLayout:'auto'}}>
              <thead>
                <tr className="bg-slate-100">
                  <th className="px-2 py-1 text-left sticky left-0 bg-slate-100 z-10 border border-slate-300 text-slate-900 font-bold">Fab Labour</th>
                  <th className="px-1 py-1 text-left border border-slate-300 text-slate-900 w-[50px]"></th>
                  {(data.work_orders || []).map(wo => (
                    <th key={wo} className="px-1 py-1 text-center font-mono text-slate-900 border border-slate-300" style={{writingMode: 'vertical-lr', transform: 'rotate(180deg)', height: '60px', fontSize: '9px'}}>
                      {wo}
                    </th>
                  ))}
                  <th className="px-1 py-1 text-center font-bold text-slate-900 border border-slate-300" style={{writingMode: 'vertical-lr', transform: 'rotate(180deg)', height: '60px', fontSize: '9px'}}>Totals</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.filter(r => r.regular_total || r.overtime_total || r.attendance_summary).map((row, i) => {
                  const bg = i % 2 === 0 ? 'bg-blue-50' : 'bg-white';
                  return (
                    <React.Fragment key={i}>
                      <tr>
                        <td className={`px-2 py-0.5 font-bold text-slate-900 sticky left-0 z-10 border border-slate-300 ${bg}`}>
                          {row.employee_name}
                          {row.attendance_summary && <div className="text-[9px] font-bold text-rose-600">{row.attendance_summary}</div>}
                        </td>
                        <td className={`px-1 py-0.5 text-slate-900 text-[10px] border border-slate-300 ${bg}`}>Reg</td>
                        {(data.work_orders || []).map(wo => (
                          <td key={wo} className={`px-1 py-0.5 text-center text-slate-900 font-bold border border-slate-300 ${bg}`}>{row.regular_by_wo?.[wo] || ''}</td>
                        ))}
                        <td className={`px-1 py-0.5 text-center text-slate-900 font-bold border border-slate-300 bg-yellow-50`}>{row.regular_total || ''}</td>
                      </tr>
                      <tr>
                        <td className={`sticky left-0 z-10 border border-slate-300 ${bg}`}></td>
                        <td className={`px-1 py-0.5 text-slate-900 text-[10px] border border-slate-300 ${bg}`}>O/T</td>
                        {(data.work_orders || []).map(wo => (
                          <td key={wo} className={`px-1 py-0.5 text-center text-amber-600 font-bold border border-slate-300 ${bg}`}>{row.overtime_by_wo?.[wo] || ''}</td>
                        ))}
                        <td className={`px-1 py-0.5 text-center text-amber-600 font-bold border border-slate-300 bg-yellow-50`}>{row.overtime_total || ''}</td>
                      </tr>
                    </React.Fragment>
                  );
                })}
                <tr className="border-t-2 border-slate-200 bg-slate-200">
                  <td className="px-2 py-1 font-bold text-slate-900 sticky left-0 bg-slate-200 z-10 border border-slate-300">Totals</td>
                  <td className="px-1 py-1 text-slate-900 font-bold border border-slate-300 bg-slate-200 text-[10px]">Reg</td>
                  {(data.work_orders || []).map(wo => (
                    <td key={wo} className="px-1 py-1 text-center text-slate-900 font-bold border border-slate-300 bg-slate-200">{data.totals_regular?.[wo] || ''}</td>
                  ))}
                  <td className="px-1 py-1 text-center text-slate-900 font-bold border border-slate-300 bg-yellow-100">{data.grand_total_regular}</td>
                </tr>
                <tr className="bg-slate-200">
                  <td className="sticky left-0 bg-slate-200 z-10 border border-slate-300"></td>
                  <td className="px-1 py-1 text-amber-700 font-bold border border-slate-300 bg-slate-200 text-[10px]">O/T</td>
                  {(data.work_orders || []).map(wo => (
                    <td key={wo} className="px-1 py-1 text-center text-amber-700 font-bold border border-slate-300 bg-slate-200">{data.totals_overtime?.[wo] || ''}</td>
                  ))}
                  <td className="px-1 py-1 text-center text-amber-700 font-bold border border-slate-300 bg-yellow-100">{data.grand_total_overtime}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Linda's QB Entry Report */}
      {data && reportType === 'fabtm' && <FabTmReport data={data} canSaveMarkups={canSaveMarkups} />}

      {data && reportType === 'linda' && data.rows && (
        <PayrollLanes dateFrom={dateFrom} dateTo={dateTo} onWeekChange={setWeekApproval} />
      )}

      {data && reportType === 'linda' && data.rows && (
        <div className="bg-white rounded-xl border border-slate-300 overflow-x-auto">
          <div className="px-4 py-3 bg-slate-900/10 border-b border-slate-300">
            <div>
              <p className="text-lg text-slate-900 font-bold">QuickBooks Entry Report</p>
              <p className="text-xs text-slate-900">Week: {dateFrom} to {dateTo} — For manual QB entry
                {weekApproval ? <span className="ml-2 text-emerald-700 font-semibold">· Approved by {weekApproval.approved_by}</span> : <span className="ml-2 text-red-600 font-semibold">· Not approved</span>}</p>
            </div>
          </div>
          {data.mismatches?.length > 0 && (
            <div data-testid="qb-mismatch-banner" className="px-4 py-2 bg-amber-50 border-b border-amber-300 text-xs text-amber-900">
              <p className="font-bold mb-1">Report doesn't match the dailies on {data.mismatches.length} day{data.mismatches.length > 1 ? 's' : ''} — the job sessions changed after the hours were booked.</p>
              <div className="flex flex-wrap gap-1.5">
                {data.mismatches.map(m => (
                  <span key={`${m.employee_id}-${m.date}`} data-testid={`qb-mismatch-${m.employee_id}-${m.date}`}
                    className="inline-flex items-center gap-1.5 bg-white border border-amber-300 rounded px-2 py-0.5">
                    <span className="font-semibold">{m.employee_name}</span>
                    <span>{new Date(m.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' })}</span>
                    <span>report <b>{m.booked}h</b> · dailies <b>{m.dailies}h</b></span>
                    {m.edited_in_report && <span className="text-[9px] text-slate-500">(edited here)</span>}
                    {canEditTimes && (
                      <button data-testid={`qb-resync-${m.employee_id}-${m.date}`} onClick={async () => {
                        try {
                          const r = await axios.post(`${API}/time-entries/resync-day`, { employee_id: m.employee_id, date: m.date });
                          toast.success(`${m.employee_name} ${m.date}: now ${r.data.total_hours}h${r.data.approved ? ' (approved)' : ''}`);
                          generate();
                        } catch (e) { toast.error(e.response?.data?.detail || 'Re-sync failed'); }
                      }} className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white hover:bg-amber-600">Re-sync</button>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}
          {data.rows.length === 0 ? (
            <div className="text-center py-12 text-slate-900">No approved time entries for this period</div>
          ) : (() => {
            const dayLabels = (data.dates || []).map(d => {
              const dt = new Date(d + 'T00:00:00');
              return { date: d, label: dt.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric' }) };
            });
            let empColors = {};
            let colorIdx = 0;
            const colors = [
              ['bg-blue-50', 'bg-blue-50'],
              ['bg-slate-900/10', 'bg-slate-900/10'],
              ['bg-emerald-50', 'bg-emerald-50'],
              ['bg-amber-50/50', 'bg-amber-50/50'],
              ['bg-purple-50', 'bg-purple-50'],
              ['bg-rose-50', 'bg-rose-50'],
            ];
            return (
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-100 text-slate-900">
                    <th className="px-3 py-2 text-left border border-slate-300 min-w-[130px]">Employee</th>
                    <th className="px-2 py-2 text-left border border-slate-300">WO#</th>
                    <th className="px-2 py-2 text-left border border-slate-300">Customer</th>
                    {dayLabels.map(d => (
                      <th key={d.date} className="px-2 py-2 text-center border border-slate-300">{d.label}</th>
                    ))}
                    <th className="px-2 py-2 text-center border border-slate-300 bg-yellow-50 font-bold">Reg Total</th>
                    <th className="px-2 py-2 text-center border border-slate-300 bg-amber-50 font-bold">OT Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, i) => {
                    // Assign color per employee
                    if (!empColors[row.employee_name]) {
                      empColors[row.employee_name] = colors[colorIdx % colors.length];
                      colorIdx++;
                    }
                    const [bgClass] = empColors[row.employee_name];
                    const isTotal = row.is_employee_total;
                    const isFirstForEmp = i === 0 || data.rows[i-1]?.employee_name !== row.employee_name;

                    if (isTotal) {
                      return (
                        <tr key={i} className="border-b-4 border-slate-400">
                          <td className={`px-3 py-1 font-bold text-slate-900 border border-slate-300 bg-slate-200`}>{row.employee_name} Total</td>
                          <td className="border border-slate-300 bg-slate-200"></td>
                          <td className="border border-slate-300 bg-slate-200"></td>
                          {dayLabels.map(d => {
                            const att = row.attendance?.[d.date];
                            return (
                              <td key={d.date} className="px-2 py-1 text-center border border-slate-300 bg-slate-200 text-slate-900 font-bold">
                                {(row.days[d.date]?.reg || row.days[d.date]?.ot) ? (
                                  <span data-testid={`qb-total-${d.date}`}>{Math.round(((row.days[d.date].reg || 0) + (row.days[d.date].ot || 0)) * 100) / 100}
                                    {row.days[d.date].ot > 0 && <span className="ml-0.5 text-[8px] text-amber-700 font-bold">{row.days[d.date].ot} OT</span>}
                                  </span>
                                ) : (att ? (
                                  <span data-testid={`att-badge-${d.date}`} className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${ATT_BADGE[att] || ''}`}>{ATT_LABELS[att] || att}</span>
                                ) : '')}
                              </td>
                            );
                          })}
                          <td className="px-2 py-1 text-center font-bold text-slate-900 border border-slate-300 bg-yellow-100">{row.total_reg || ''}</td>
                          <td className="px-2 py-1 text-center font-bold text-amber-700 border border-slate-300 bg-amber-100">{row.total_ot || ''}</td>
                        </tr>
                      );
                    }

                    return (
                      <tr key={i} className={`${bgClass} ${isFirstForEmp ? 'border-t-2 border-slate-400' : ''}`}>
                        <td className={`px-3 py-1 font-semibold text-slate-900 border border-slate-300 min-w-[130px] ${bgClass}`}>
                          {isFirstForEmp ? row.employee_name : ''}
                        </td>
                        <td className="px-2 py-1 font-mono text-blue-600 border border-slate-300 text-sm font-bold relative">
                          {canEditTimes && editWORow && editWORow.employee_name === row.employee_name && editWORow.old_wo === row.work_order_number ? (
                            <div className="flex flex-col gap-1">
                              <input type="text" value={editWOSearch} onChange={async e => {
                                setEditWOSearch(e.target.value);
                                if (e.target.value.length > 2) {
                                  const r = await axios.get(`${API}/work-orders`, { params: { q: e.target.value } }).catch(() => ({ data: [] }));
                                  setEditWOResults(r.data?.slice?.(0, 5) || []);
                                } else setEditWOResults([]);
                              }} placeholder="New WO#..." autoFocus
                                className="w-28 px-1 py-0.5 bg-white border border-blue-500 rounded text-xs outline-none" />
                              {editWOResults.length > 0 && (
                                <div className="absolute z-30 top-8 left-0 w-56 bg-white border border-slate-300 rounded shadow-lg max-h-32 overflow-y-auto">
                                  {editWOResults.map(wo => (
                                    <button key={wo.project_number} onClick={async () => {
                                      try {
                                        await axios.post(`${API}/admin/change-entry-wo`, {
                                          employee_name: row.employee_name, old_wo: row.work_order_number,
                                          new_wo: wo.project_number, new_customer: wo.client_name || '',
                                          date_from: dateFrom, date_to: dateTo,
                                        });
                                        toast.success(`Moved ${row.employee_name}'s hours from ${row.work_order_number} → ${wo.project_number}`);
                                        setEditWORow(null); setEditWOSearch(''); setEditWOResults([]);
                                        generate();
                                      } catch { toast.error('Failed'); }
                                    }} className="w-full text-left px-2 py-1.5 hover:bg-blue-50 text-xs border-b border-slate-100">
                                      <span className="font-mono text-blue-600">{wo.project_number}</span> — {wo.client_name}
                                    </button>
                                  ))}
                                </div>
                              )}
                              <button onClick={() => { setEditWORow(null); setEditWOSearch(''); }} className="text-[10px] text-slate-500">cancel</button>
                            </div>
                          ) : canEditTimes ? (
                            <button onClick={() => setEditWORow({ employee_name: row.employee_name, old_wo: row.work_order_number })}
                              className="hover:underline hover:text-blue-800 cursor-pointer text-left" title="Click to reassign WO">
                              {row.work_order_number}
                            </button>
                          ) : (
                            <span>{row.work_order_number}</span>
                          )}
                        </td>
                        <td className="px-2 py-1 text-slate-900 border border-slate-300">{row.customer}</td>
                        {dayLabels.map(d => {
                          const day = row.days[d.date];
                          const hasHours = day?.reg > 0 || day?.ot > 0;
                          const entryIds = day?.entry_ids || [];
                          const isOT = day?.ot > 0 && day?.reg === 0;
                          const isMixed = day?.ot > 0 && day?.reg > 0;
                          const displayHours = isOT ? day.ot : isMixed ? Math.round((day.reg + day.ot) * 100) / 100 : day?.reg;
                          return (
                            <td key={d.date} data-testid={`qb-cell-${row.work_order_number}-${d.date}`} className={`px-1 py-1 text-center border border-slate-300 ${isOT || isMixed ? 'bg-amber-50' : ''}`}>
                              <div className="flex flex-col items-center gap-0.5">
                                {canEditTimes ? (
                                <input type="number" step="0.5" defaultValue={hasHours ? displayHours : ''}
                                  placeholder="—"
                                  className={`w-12 text-center font-bold bg-transparent border-b border-transparent hover:border-blue-400 focus:border-blue-500 focus:bg-blue-50 outline-none text-xs placeholder-slate-300 ${isOT ? 'text-amber-700' : 'text-slate-900'}`}
                                  onFocus={e => e.target.select()}
                                  onBlur={async (e) => {
                                    const newVal = parseFloat(e.target.value);
                                    if (!hasHours && isNaN(newVal)) return;
                                    if (hasHours && newVal === displayHours) return;
                                    if (hasHours && entryIds.length > 0) {
                                      try {
                                        if (isNaN(newVal) || newVal <= 0) {
                                          await axios.post(`${API}/admin/bulk-delete`, { entry_ids: entryIds, type: 'time' });
                                          toast.success('Deleted');
                                        } else {
                                          await axios.post(`${API}/admin/update-time-entry`, { entry_id: entryIds[0], regular_hours: isMixed ? Math.max(newVal - day.ot, 0) : newVal });
                                          toast.success('Updated');
                                        }
                                        generate();
                                      } catch { toast.error('Failed'); }
                                    } else if (!hasHours && newVal > 0) {
                                      try {
                                        await axios.post(`${API}/admin/add-time-entry`, {
                                          employee_name: row.employee_name,
                                          work_order_number: row.work_order_number,
                                          customer: row.customer,
                                          date: d.date,
                                          regular_hours: newVal,
                                        });
                                        toast.success(`Added ${newVal}h`);
                                        generate();
                                      } catch { toast.error('Failed'); }
                                    }
                                  }}
                                  onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                                />
                                ) : (
                                  <span className={`text-xs font-bold ${isOT ? 'text-amber-700' : 'text-slate-900'}`}>{hasHours ? displayHours : ''}</span>
                                )}
                                {canEditTimes && hasHours && entryIds.length > 0 && (
                                  <button onClick={async () => {
                                    try {
                                      await axios.post(`${API}/admin/toggle-ot`, { entry_id: entryIds[0] });
                                      toast.success(isOT ? 'Switched to Reg' : 'Switched to OT');
                                      generate();
                                    } catch { toast.error('Failed'); }
                                  }} title={isMixed ? `${day.reg} reg + ${day.ot} OT` : undefined}
                                    className={`text-[8px] font-bold px-1.5 py-0.5 rounded cursor-pointer ${isOT ? 'bg-amber-500 text-white' : isMixed ? 'bg-amber-200 text-amber-900' : 'bg-slate-200 text-slate-600 hover:bg-amber-100'}`}>
                                    {isOT ? 'OT' : isMixed ? `${day.ot} OT` : 'REG'}
                                  </button>
                                )}
                              </div>
                            </td>
                          );
                        })}
                        <td className="px-2 py-1 text-center font-bold text-slate-900 border border-slate-300 bg-yellow-50">{row.total_reg || ''}</td>
                        <td className="px-2 py-1 text-center font-bold text-amber-600 border border-slate-300 bg-amber-50">{row.total_ot || ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            );
          })()}
        </div>
      )}

      {/* Consumable Costing Report */}
      {data && reportType === 'materials' && <MaterialsReportTable data={data} dateFrom={dateFrom} dateTo={dateTo} />}

      {data && reportType === 'consumables' && data.rows && (
        <div className="bg-white rounded-xl border border-slate-300 overflow-x-auto">
          <div className="px-4 py-3 bg-slate-900/10 border-b border-slate-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg text-slate-900 font-bold">Consumables by Work Order</p>
                <p className="text-xs text-slate-600">{dateFrom} to {dateTo} — {data.entry_count} entries</p>
              </div>
              <div className="text-right text-xs">
                <p className="text-slate-600">Welding Supplies: <span className="font-bold text-slate-900">${(data.welding_total || 0).toFixed(2)}</span></p>
                <p className="text-slate-600">Abrasives & Tooling: <span className="font-bold text-slate-900">${(data.abrasives_total || 0).toFixed(2)}</span></p>
                <p className="text-slate-900 font-bold mt-1">Total: ${(data.grand_total || 0).toFixed(2)}</p>
              </div>
            </div>
          </div>
          {data.rows.length === 0 ? (
            <div className="text-center py-12 text-slate-600">No consumable entries for this period</div>
          ) : (
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-slate-100 text-slate-900">
                  <th className="px-3 py-2 text-left border border-slate-300">WO#</th>
                  <th className="px-2 py-2 text-left border border-slate-300">Category</th>
                  <th className="px-2 py-2 text-left border border-slate-300">Item</th>
                  <th className="px-2 py-2 text-left border border-slate-300">Employee</th>
                  <th className="px-2 py-2 text-left border border-slate-300">Date</th>
                  <th className="px-2 py-2 text-center border border-slate-300">Qty</th>
                  <th className="px-2 py-2 text-right border border-slate-300">Unit $</th>
                  <th className="px-2 py-2 text-right border border-slate-300 bg-yellow-50 font-bold">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row, i) => {
                  if (row.is_wo_header) {
                    const groupIds = data.rows.filter(r => !r.is_wo_header && !r.is_wo_total && r.work_order_number === row.work_order_number && r.id).map(r => r.id);
                    return (
                      <tr key={i} className="bg-blue-50 border-t-2 border-blue-300">
                        <td className="px-3 py-2 font-mono font-bold text-blue-700 border border-slate-300">{row.work_order_number}</td>
                        <td className="px-2 py-2 font-semibold text-slate-800 border border-slate-300">{row.qb_category}</td>
                        <td colSpan={5} className="px-2 py-2 text-slate-600 border border-slate-300">
                          <span className="inline-flex items-center gap-3">
                            {row.customer}
                            {canEditSell && groupIds.length > 0 && (
                              <MoveToWOButton label={`all ${groupIds.length} item${groupIds.length === 1 ? '' : 's'}`} testid={`cons-move-wo-${row.work_order_number}`}
                                onMove={(wo) => moveConsumables({ entry_ids: groupIds }, `${row.work_order_number}'s ${groupIds.length} item${groupIds.length === 1 ? '' : 's'}`, wo)} />
                            )}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-right font-bold text-slate-900 border border-slate-300">${row.category_total.toFixed(2)}</td>
                      </tr>
                    );
                  }
                  if (row.is_wo_total) {
                    return (
                      <tr key={i} className="bg-slate-200 border-b-2 border-slate-400">
                        <td colSpan={7} className="px-3 py-1.5 font-bold text-slate-900 border border-slate-300 text-right">{row.work_order_number} Total</td>
                        <td className="px-2 py-1.5 text-right font-bold text-slate-900 border border-slate-300 bg-yellow-100 font-mono">${row.total.toFixed(2)}</td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={i} className="bg-white hover:bg-slate-50">
                      <td className="px-3 py-1 border border-slate-200">
                        {canEditSell && row.id && (
                          <MoveToWOButton compact label={row.item_name} testid={`cons-move-${row.id}`}
                            onMove={(wo) => moveConsumables({ entry_ids: [row.id] }, `${row.quantity}× ${row.item_name}`, wo)} />
                        )}
                      </td>
                      <td className="px-2 py-1 border border-slate-200 text-slate-500">{row.qb_category}</td>
                      <td className="px-2 py-1 border border-slate-200 text-slate-900">{row.item_name}</td>
                      <td className="px-2 py-1 border border-slate-200 text-slate-600">{row.employee_name}</td>
                      <td className="px-2 py-1 border border-slate-200 text-slate-600">{row.date ? new Date(row.date + 'T00:00:00').toLocaleDateString('en-US', {weekday:'short', month:'numeric', day:'numeric'}) : ''}</td>
                      <td className="px-2 py-1 border border-slate-200 text-center">{row.quantity}</td>
                      <td className="px-2 py-1 border border-slate-200 text-right font-mono">
                        {editSellId === row.id ? (
                          <span className="flex items-center justify-end gap-1">
                            <span className="text-slate-500">$</span>
                            <input
                              type="number" step="0.01" autoFocus value={sellInput}
                              data-testid="report-sell-input"
                              onFocus={ev => ev.target.select()}
                              onChange={ev => setSellInput(ev.target.value)}
                              onKeyDown={ev => { if (ev.key === 'Enter') saveSellPrice(row.id); else if (ev.key === 'Escape') { setEditSellId(null); setSellInput(''); } }}
                              className="w-16 px-1 py-0.5 border border-blue-500 rounded text-right text-xs outline-none" />
                            <button disabled={savingSell} onClick={() => saveSellPrice(row.id)} data-testid="report-sell-save" className="text-emerald-600 font-bold px-1">✓</button>
                            <button onClick={() => { setEditSellId(null); setSellInput(''); }} className="text-slate-500 px-0.5">✕</button>
                          </span>
                        ) : (
                          <span className="inline-flex items-center justify-end gap-1">
                            ${(row.sell_price || 0).toFixed(2)}
                            {canEditSell && row.id && (
                              <button
                                data-testid={`report-sell-edit-${row.id}`}
                                onClick={() => { setEditSellId(row.id); setSellInput(String(row.sell_price || 0)); }}
                                className="text-blue-500 hover:text-blue-700" title="Edit sell price (updates entry + catalog)">
                                <Edit2 className="w-3 h-3" />
                              </button>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1 border border-slate-200 text-right font-mono font-bold bg-yellow-50">${(row.sell_total || 0).toFixed(2)}</td>
                    </tr>
                  );
                })}
                <tr className="bg-amber-100 font-bold">
                  <td colSpan={7} className="px-3 py-2 text-right border border-slate-300 text-sm">GRAND TOTAL</td>
                  <td className="px-2 py-2 text-right border border-slate-300 font-mono text-sm">${(data.grand_total || 0).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════
   ADMIN TAB — Cleanup & Management
   ════════════════════════════════════════════════════════════════ */
function FieldCrewRecovery() {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const recover = async () => {
    if (!window.confirm(`Recover field crew data for ${date}? This will re-open closed clock events for field employees and contractors.`)) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/field-crew-recover`, { date });
      setResult(r.data);
      toast.success(`Recovered ${r.data.recovered_employees} employees + ${r.data.recovered_contractors} contractors`);
    } catch { toast.error('Recovery failed'); }
    setLoading(false);
  };

  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="field-crew-recovery">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <p className="text-sm text-emerald-700 font-medium">Recover Field Crew Data</p>
          <p className="text-xs text-emerald-700">Re-open field crew clock events that were incorrectly closed</p>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="px-3 py-2 bg-slate-100 border border-slate-300 rounded-lg text-sm text-slate-900" />
        <button onClick={recover} disabled={loading}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold whitespace-nowrap disabled:opacity-50"
          data-testid="field-crew-recover-btn">
          {loading ? 'Recovering...' : 'Recover'}
        </button>
      </div>
      {result && (
        <div className="mt-3 p-3 bg-emerald-50 rounded-lg">
          <p className="text-xs text-emerald-700 font-medium">Recovered for {result.date}:</p>
          <p className="text-xs text-slate-900 mt-1">{result.recovered_employees} employee(s): {(result.recovered_names || []).join(', ') || 'none'}</p>
          <p className="text-xs text-slate-900">{result.recovered_contractors} contractor(s)</p>
        </div>
      )}
    </div>
  );
}

function AdminTab() {
  const [entryType, setEntryType] = useState('time');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [rejected, setRejected] = useState({ time_entries: [], consumable_entries: [] });
  const [rejSelected, setRejSelected] = useState([]);
  const [showRejected, setShowRejected] = useState(false);
  const [zeroCons, setZeroCons] = useState([]);
  const [showZeroCons, setShowZeroCons] = useState(false);
  const [editingPrice, setEditingPrice] = useState(null);
  const [newPrice, setNewPrice] = useState('');
  const [orphaned, setOrphaned] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/all-entries`, { params: { entry_type: entryType } });
      setEntries(r.data || []);
      setSelected([]);
    } catch { toast.error('Failed to load entries'); }
    setLoading(false);
  };

  const loadRejected = async () => {
    try {
      const r = await axios.get(`${API}/admin/rejected-entries`);
      setRejected(r.data);
      setRejSelected([]);
    } catch { toast.error('Failed to load rejected entries'); }
  };

  const reapprove = async (type) => {
    if (rejSelected.length === 0) { toast.error('Select entries first'); return; }
    try {
      const r = await axios.post(`${API}/admin/reapprove`, { entry_ids: rejSelected, type });
      toast.success(`Re-approved ${r.data.modified} entries`);
      setRejSelected([]);
      loadRejected();
    } catch { toast.error('Failed'); }
  };

  useEffect(() => { load(); }, [entryType]);

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const selectAll = () => {
    setSelected(selected.length === entries.length ? [] : entries.map(e => e.id));
  };

  const [confirmPurge, setConfirmPurge] = useState(false);
  const [confirmNuke, setConfirmNuke] = useState(false);

  const deleteSelected = async () => {
    if (selected.length === 0) { toast.error('Select entries to delete first'); return; }
    try {
      const res = await axios.post(`${API}/admin/bulk-delete`, { entry_ids: selected, type: entryType });
      toast.success(`Deleted ${res.data.deleted} entries`);
      load();
    } catch (e) {
      console.error('Delete failed:', e);
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const purgeAll = async () => {
    if (!confirmPurge) {
      setConfirmPurge(true);
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/purge-all`, { type: entryType });
      const total = Object.values(res.data).filter(v => typeof v === 'number').reduce((a, b) => a + b, 0);
      toast.success(`Purged ${total} ${entryType} entries`);
      setConfirmPurge(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purge failed');
      setConfirmPurge(false);
    }
  };

  const nukeEverything = async () => {
    if (!confirmNuke) {
      setConfirmNuke(true);
      return;
    }
    try {
      const res = await axios.post(`${API}/admin/purge-all`, { type: 'everything' });
      const items = [];
      if (res.data.time_entries) items.push(`${res.data.time_entries} time`);
      if (res.data.consumable_entries) items.push(`${res.data.consumable_entries} consumable`);
      if (res.data.clock_events) items.push(`${res.data.clock_events} clock`);
      if (res.data.attendance_records) items.push(`${res.data.attendance_records} attendance`);
      toast.success(`Purged: ${items.join(', ') || 'nothing to purge'}`);
      setConfirmNuke(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purge failed');
      setConfirmNuke(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Orphaned Entries Finder */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-amber-800 font-medium">Find Orphaned Entries</p>
            <p className="text-xs text-amber-600">Time entries with no employee name — trace where they came from</p>
          </div>
          <button onClick={async () => {
            const r = await axios.get(`${API}/admin/orphaned-entries`);
            setOrphaned(r.data);
          }} className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-medium">
            Find Orphaned
          </button>
          {orphaned && orphaned.length > 0 && (
            <button onClick={async () => {
              if (!window.confirm(`Delete ${orphaned.length} orphaned entries? These are duplicates with no employee name.`)) return;
              await axios.post(`${API}/admin/delete-orphaned`);
              toast.success('Orphaned entries deleted');
              setOrphaned([]);
            }} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-medium">
              Delete {orphaned.length} Orphaned
            </button>
          )}
        </div>
        {orphaned && (
          <div className="mt-3 max-h-60 overflow-y-auto">
            {orphaned.length === 0 ? (
              <p className="text-sm text-emerald-700 font-medium">No orphaned entries found</p>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-amber-700 font-bold">{orphaned.length} orphaned entries:</p>
                {orphaned.map((e, i) => (
                  <div key={i} className="text-xs bg-white p-2 rounded border border-amber-200">
                    <p><strong>WO:</strong> {e.work_order_number} | <strong>Date:</strong> {e.date} | <strong>Hours:</strong> {e.regular_hours}h</p>
                    <p><strong>Source:</strong> {e.source} | <strong>Status:</strong> {e.status} | <strong>Created:</strong> {e.created_at}</p>
                    <p><strong>Notes:</strong> {e.notes || 'none'} | <strong>Employee ID:</strong> {e.employee_id || 'NONE'}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm text-red-700 font-medium">Admin Cleanup Tools</p>
          <p className="text-xs text-red-600 mt-0.5">Use with caution. Deletions are permanent.</p>
        </div>
        {confirmNuke ? (
          <div className="flex gap-2">
            <button data-testid="admin-nuke-confirm" onClick={nukeEverything}
              className="px-4 py-2 bg-red-700 hover:bg-red-800 text-white rounded-lg text-xs font-bold animate-pulse">
              YES — PURGE EVERYTHING
            </button>
            <button onClick={() => setConfirmNuke(false)}
              className="px-3 py-2 bg-slate-200 hover:bg-slate-300 text-slate-900 rounded-lg text-xs">
              Cancel
            </button>
          </div>
        ) : (
          <button data-testid="admin-nuke-all" onClick={nukeEverything}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold flex items-center gap-2 whitespace-nowrap">
            <Trash2 className="w-4 h-4" /> Purge All Test Data
          </button>
        )}
      </div>

      {/* Fix floating point hours */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm text-blue-700 font-medium">Fix Rounding Issues</p>
          <p className="text-xs text-blue-700">Round all hours to nearest 0.5h (fixes floating point like 24.509999)</p>
        </div>
        <button onClick={async () => {
          try {
            const r = await axios.post(`${API}/admin/fix-hours`, {});
            toast.success(`Fixed ${r.data.fixed || 0} rounding, ${r.data.lunch_fixed || 0} lunch corrections`);
          } catch { toast.error('Failed'); }
        }} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold whitespace-nowrap">
          Fix Hours
        </button>
      </div>

      {/* Fix consumable prices */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm text-blue-700 font-medium">Fix Consumable Prices</p>
          <p className="text-xs text-blue-700">Backfill $0.00 consumable entries with current catalog prices</p>
        </div>
        <button onClick={async () => {
          try {
            const r = await axios.post(`${API}/admin/fix-consumable-prices`, {});
            toast.success(`Fixed ${r.data.fixed || 0} consumable prices`);
          } catch { toast.error('Failed'); }
        }} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold whitespace-nowrap">
          Fix Prices
        </button>
      </div>

      {/* Field Crew Recovery */}
      <FieldCrewRecovery />

      {/* Re-approve Rejected Entries */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm text-amber-700 font-medium">Rejected Entries</p>
            <p className="text-xs text-amber-700">Re-approve entries that were accidentally rejected</p>
          </div>
          <button onClick={() => { setShowRejected(!showRejected); if (!showRejected) loadRejected(); }}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-semibold">
            {showRejected ? 'Hide' : 'Show Rejected'}
          </button>
        </div>
        {showRejected && (
          <div className="space-y-3">
            {rejected.time_entries.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <h5 className="text-xs text-slate-900 font-bold uppercase">Rejected Time ({rejected.time_entries.length})</h5>
                  <div className="flex gap-2">
                    <button onClick={() => setRejSelected(rejected.time_entries.map(e => e.id))} className="text-xs text-blue-700">Select All</button>
                    <button onClick={() => reapprove('time')} className="text-xs px-2 py-1 bg-emerald-600 text-white rounded">Re-approve ({rejSelected.length})</button>
                  </div>
                </div>
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {rejected.time_entries.map(e => (
                    <label key={e.id} className="flex items-center gap-2 px-2 py-1.5 bg-red-50 border border-red-200 rounded cursor-pointer">
                      <input type="checkbox" checked={rejSelected.includes(e.id)} onChange={() => setRejSelected(prev => prev.includes(e.id) ? prev.filter(x => x !== e.id) : [...prev, e.id])} className="rounded border-slate-500" />
                      <span className="text-xs text-slate-900">{e.employee_name}</span>
                      <span className="text-xs font-mono text-blue-700">{e.work_order_number}</span>
                      <span className="text-xs text-slate-900">{e.date}</span>
                      <span className="text-xs text-slate-900 font-bold">{e.regular_hours}h</span>
                      {e.rejection_reason && <span className="text-xs text-red-600">"{e.rejection_reason}"</span>}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {rejected.consumable_entries.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <h5 className="text-xs text-slate-900 font-bold uppercase">Rejected Consumables ({rejected.consumable_entries.length})</h5>
                  <div className="flex gap-2">
                    <button onClick={() => setRejSelected(rejected.consumable_entries.map(e => e.id))} className="text-xs text-blue-700">Select All</button>
                    <button onClick={() => reapprove('consumable')} className="text-xs px-2 py-1 bg-emerald-600 text-white rounded">Re-approve ({rejSelected.length})</button>
                  </div>
                </div>
                <div className="space-y-1 max-h-[200px] overflow-y-auto">
                  {rejected.consumable_entries.map(e => (
                    <label key={e.id} className="flex items-center gap-2 px-2 py-1.5 bg-red-50 border border-red-200 rounded cursor-pointer">
                      <input type="checkbox" checked={rejSelected.includes(e.id)} onChange={() => setRejSelected(prev => prev.includes(e.id) ? prev.filter(x => x !== e.id) : [...prev, e.id])} className="rounded border-slate-500" />
                      <span className="text-xs text-slate-900">{e.employee_name}</span>
                      <span className="text-xs font-mono text-blue-700">{e.work_order_number}</span>
                      <span className="text-xs text-slate-900">{e.date}</span>
                      <span className="text-xs text-slate-900">{e.item_name} x{e.quantity}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {rejected.time_entries.length === 0 && rejected.consumable_entries.length === 0 && (
              <p className="text-xs text-slate-900 text-center py-4">No rejected entries</p>
            )}
          </div>
        )}
      </div>

      {/* $0 Consumable Entries - Edit Prices */}
      <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm text-emerald-700 font-medium">Consumable Pricing</p>
            <p className="text-xs text-emerald-700">Click any $0 entry to set the price — updates the catalog permanently</p>
          </div>
          <button onClick={async () => {
            setShowZeroCons(!showZeroCons);
            if (!showZeroCons) {
              try {
                const r = await axios.get(`${API}/admin/all-entries`, { params: { entry_type: 'consumable' } });
                setZeroCons((r.data || []).filter(e => !e.unit_cost || e.unit_cost === 0));
              } catch {}
            }
          }} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold">
            {showZeroCons ? 'Hide' : 'Show $0 Items'}
          </button>
        </div>
        {showZeroCons && (
          <div className="space-y-1 max-h-[300px] overflow-y-auto">
            {zeroCons.length === 0 ? (
              <p className="text-xs text-slate-900 text-center py-4">No $0 consumable entries</p>
            ) : zeroCons.map(e => (
              <div key={e.id} className="flex items-center gap-2 px-2 py-1.5 bg-slate-900/10 border border-slate-200 rounded">
                <span className="text-xs text-slate-900 flex-1">{e.employee_name}</span>
                <span className="text-xs font-mono text-blue-700">{e.work_order_number}</span>
                <span className="text-xs text-slate-900">{e.item_name} x{e.quantity}</span>
                {editingPrice === e.id ? (
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-slate-900">$</span>
                    <input type="number" step="0.01" value={newPrice} onChange={ev => setNewPrice(ev.target.value)} autoFocus
                      onKeyDown={ev => { if (ev.key === 'Enter') {
                        axios.post(`${API}/admin/update-consumable-price`, { entry_id: e.id, unit_cost: parseFloat(newPrice) })
                          .then(r => { toast.success(`${r.data.item}: $${r.data.new_price} — catalog updated`); setEditingPrice(null); setZeroCons(prev => prev.filter(x => x.id !== e.id)); })
                          .catch(() => toast.error('Failed'));
                      }}}
                      className="w-20 px-2 py-1 bg-white border border-emerald-500 rounded text-slate-900 text-xs text-center outline-none" />
                    <button onClick={() => {
                      axios.post(`${API}/admin/update-consumable-price`, { entry_id: e.id, unit_cost: parseFloat(newPrice) })
                        .then(r => { toast.success(`${r.data.item}: $${r.data.new_price} — catalog updated`); setEditingPrice(null); setZeroCons(prev => prev.filter(x => x.id !== e.id)); })
                        .catch(() => toast.error('Failed'));
                    }} className="px-2 py-1 bg-emerald-600 text-white rounded text-[10px]">Save</button>
                    <button onClick={() => setEditingPrice(null)} className="text-xs text-slate-900">X</button>
                  </div>
                ) : (
                  <button onClick={() => { setEditingPrice(e.id); setNewPrice(''); }}
                    className="text-xs px-2 py-1 bg-red-50 text-red-600 rounded border border-red-200 hover:bg-red-50">
                    $0.00 — click to set price
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex gap-2">
          <button onClick={() => setEntryType('time')} className={`px-4 py-2 rounded-lg text-sm font-medium ${entryType === 'time' ? 'bg-blue-600 text-white' : 'bg-white text-slate-900'}`}>
            Time Entries
          </button>
          <button onClick={() => setEntryType('consumable')} className={`px-4 py-2 rounded-lg text-sm font-medium ${entryType === 'consumable' ? 'bg-blue-600 text-white' : 'bg-white text-slate-900'}`}>
            Consumable Entries
          </button>
        </div>
        <div className="flex gap-2 ml-auto">
          <button onClick={selectAll} className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-lg text-xs">
            {selected.length === entries.length ? 'Deselect All' : 'Select All'}
          </button>
          <button data-testid="admin-delete-selected" onClick={deleteSelected} disabled={selected.length === 0}
            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 disabled:bg-slate-200 disabled:text-slate-900 text-white rounded-lg text-xs flex items-center gap-1">
            <Trash2 className="w-3.5 h-3.5" /> Delete Selected ({selected.length})
          </button>
          {confirmPurge ? (
            <div className="flex gap-1">
              <button data-testid="admin-purge-confirm" onClick={purgeAll}
                className="px-3 py-1.5 bg-red-700 hover:bg-red-800 text-white rounded-lg text-xs font-bold animate-pulse">
                CONFIRM PURGE
              </button>
              <button onClick={() => setConfirmPurge(false)}
                className="px-3 py-1.5 bg-slate-200 hover:bg-slate-300 text-slate-900 rounded-lg text-xs">
                Cancel
              </button>
            </div>
          ) : (
            <button data-testid="admin-purge-all" onClick={purgeAll}
              className="px-3 py-1.5 bg-red-800 hover:bg-red-900 text-white rounded-lg text-xs flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> Purge All
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-slate-900">Loading...</div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 text-slate-900">No {entryType} entries found</div>
      ) : (
        <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-slate-900 text-xs">
                <th className="p-3 w-8"></th>
                <th className="p-3 text-left">Employee</th>
                <th className="p-3 text-left">WO#</th>
                <th className="p-3 text-left">Date</th>
                {entryType === 'time' ? (
                  <>
                    <th className="p-3 text-center">Reg</th>
                    <th className="p-3 text-center">OT</th>
                  </>
                ) : (
                  <>
                    <th className="p-3 text-left">Item</th>
                    <th className="p-3 text-center">Qty</th>
                    <th className="p-3 text-right">Cost</th>
                  </>
                )}
                <th className="p-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e.id} className="border-b border-slate-200 hover:bg-slate-100">
                  <td className="p-3">
                    <input type="checkbox" checked={selected.includes(e.id)} onChange={() => toggleSelect(e.id)} className="rounded border-slate-500" />
                  </td>
                  <td className="p-3 text-slate-900">{e.employee_name}</td>
                  <td className="p-3 font-mono text-blue-700">{e.work_order_number}</td>
                  <td className="p-3 text-slate-700">{fmtDate(e.date)}</td>
                  {entryType === 'time' ? (
                    <>
                      <td className="p-3 text-center text-slate-900 font-bold">{e.regular_hours}</td>
                      <td className="p-3 text-center text-amber-700 font-bold">{e.overtime_hours || '-'}</td>
                    </>
                  ) : (
                    <>
                      <td className="p-3 text-slate-700">{e.item_name}</td>
                      <td className="p-3 text-center text-slate-900 font-bold">{e.quantity}</td>
                      <td className="p-3 text-right">
                        {editingPrice === e.id ? (
                          <div className="flex items-center justify-end gap-1">
                            <span className="text-xs text-slate-500">$</span>
                            <input type="number" step="0.01" value={newPrice} onChange={ev => setNewPrice(ev.target.value)} autoFocus
                              onKeyDown={ev => {
                                if (ev.key === 'Enter' && newPrice) {
                                  axios.post(`${API}/admin/update-consumable-price`, { entry_id: e.id, unit_cost: parseFloat(newPrice) })
                                    .then(r => { toast.success(`${r.data.item}: $${r.data.new_price} — catalog updated`); setEditingPrice(null); load(); })
                                    .catch(() => toast.error('Failed'));
                                }
                                if (ev.key === 'Escape') setEditingPrice(null);
                              }}
                              className="w-16 px-1 py-0.5 bg-white border border-emerald-500 rounded text-slate-900 text-xs text-right outline-none" />
                          </div>
                        ) : (
                          <button onClick={() => { setEditingPrice(e.id); setNewPrice(e.unit_cost || ''); }}
                            className={`${(!e.total_cost || e.total_cost === 0) ? 'text-red-600 hover:text-red-700' : 'text-emerald-700 hover:text-emerald-700'}`}>
                            ${(e.total_cost || 0).toFixed(2)}
                          </button>
                        )}
                      </td>
                    </>
                  )}
                  <td className="p-3 text-center">
                    <span className={`text-xs capitalize px-2 py-0.5 rounded-full border ${statusColors[e.status]}`}>{e.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 border-t border-slate-200 text-xs text-slate-500">
            {entries.length} entries total
          </div>
        </div>
      )}
    </div>
  );
}